// @ts-nocheck
import * as React from "react";
import { Logo, Ic, Sidebar, Topbar, AvatarEditor, ProfileEditor, PasswordChanger, NotificationPreferences, LineChart, BarChart, Kpi, useCurrentUser, bpSeverity, hrSeverity, tempSeverity, vitalColorVar, MessageComposer, NotifyFeed, useUnreadCount, conflictMessage, apiErrorMessage, OrgCombobox } from "./common";
import { PrivacyBody, TermsBody, PRIVACY_TOC, TERMS_TOC } from "./legal";
import { api, orgTransfer, caseTransfer, visitReports } from "../api";
import { NaverMapCanvas } from "../components/naver/NaverMapCanvas";
import { markerStyle } from "../components/naver/markers";
import { attachMarkerTooltip, bindMapTooltipHide } from "../components/naver/tooltip";

// 모바일 PWA — 케어매니저 현장 앱 (각 화면 390x844)

const StatusBar = () => (
  <div style={{display:'flex', justifyContent:'space-between', padding:'8px 22px 4px', fontSize:11, fontWeight:600, color:'var(--ink-1)', flex:'0 0 auto'}}>
    <span>{new Date().toLocaleTimeString('ko-KR',{hour:'2-digit',minute:'2-digit'})}</span>
    <div style={{display:'flex', gap:5, alignItems:'center'}}>
      <span>●●●</span><span style={{fontSize:10}}>5G</span>
      <span style={{width:18, height:9, border:'1px solid var(--ink-1)', borderRadius:2, position:'relative'}}><span style={{position:'absolute', inset:1, background:'var(--ink-1)'}}/></span>
    </div>
  </div>
);
const HomeBar = () => <div style={{height:4, width:120, background:'var(--ink-1)', borderRadius:99, margin:'2px auto 6px'}}/>;
const TabBar = ({active}) => (
  <div data-tabbar="manager" style={{display:'flex', justifyContent:'space-around', padding:'12px 16px 14px', borderTop:'1px solid var(--border-soft)', background:'var(--surface)', flex:'0 0 auto'}}>
    {[
      {l:'홈',    k:'home',     i:'dashboard', href:'/m/home'},
      {l:'일정',  k:'schedule', i:'schedule',  href:'/m/schedule'},
      {l:'지도',  k:'map',      i:'map',       href:'/m/map'},
      {l:'케이스', k:'cases',   i:'case',      href:'/m/cases'},
      {l:'마이',  k:'me',       i:'user',      href:'/m/me'},
    ].map((n,i)=>(
      <a key={i} href={n.href} style={{textAlign:'center', color: n.k===active ? 'var(--orange-600)' : 'var(--ink-3)', textDecoration:'none'}}>
        <span style={{width:22, height:22, display:'inline-block'}}>{Ic[n.i]}</span>
        <div style={{fontSize:9, fontWeight:700, marginTop:2}}>{n.l}</div>
      </a>
    ))}
  </div>
);

const STATUS_LABEL = { planned: "예정", in_progress: "진행중", completed: "완료", cancelled: "취소" };
const STATUS_COLOR = { planned: "gray", in_progress: "orange", completed: "green", cancelled: "rose" };
const RISK_LABEL  = { critical: "위험", warning: "주의", stable: "안정" };
const RISK_COLOR  = { critical: "rose", warning: "amber", stable: "green" };

// 응급SOS 유형 한글 라벨 — sos.tsx SOS_TYPES와 동일 키. 긴급대응 배너 표시용.
const SOS_TYPE_LABEL = {
  fall: "낙상·외상", wander: "배회·실종", no_answer: "안부 미응답",
  urgent: "긴급돌봄 필요", mental: "정신건강 위기", abuse: "학대 의심",
};

// Shared fetch wrapper for the mobile manager screens. Throws an Error
// carrying the HTTP status so each screen can decide what to render
// (401 redirect, 409 stale-state, generic message). Centralized so a
// future change to retry logic or auth headers updates all four screens.
async function mobileFetch(url, opts = {}) {
  const res = await fetch(url, { credentials: "same-origin", ...opts });
  let body = null;
  try { body = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok) {
    const err = new Error(body?.message || body?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = body;
    err.code = body?.error;
    throw err;
  }
  return body;
}

// Korean-friendly mapping for the mobileFetch errors that field-staff
// might actually see. Anything outside this table falls back to the raw
// server message.
function humanMobileError(e) {
  if (!e) return "처리에 실패했습니다.";
  if (e.status === 401) return "로그인이 필요합니다.";
  if (e.status === 403) return "권한이 없습니다.";
  if (e.status === 404) return "해당 항목을 찾을 수 없습니다.";
  if (e.status === 409) return conflictMessage(e.body);
  return apiErrorMessage(e, e.status);
}

// ────────────────────────────────────────────────────
// 1. 매니저 홈
function MgrHome() {
  const [data, setData] = React.useState(null);
  // "오늘의 방문" 목록 상태 필터칩. null=전체, planned/in_progress/completed.
  const [todayFilter, setTodayFilter] = React.useState(null);
  // 미읽음 배지는 useUnreadCount로 — bfcache 뒤로가기 복원/탭 재포커스/알림 읽음
  // 직후에도 자동 재요청해 "새로고침해야 반영"되던 문제를 막는다.
  const unread = useUnreadCount();

  React.useEffect(() => {
    fetch("/api/v1/mobile/manager/home").then(r => r.json()).then(setData);
  }, []);

  if (!data) return <div className="app muted" style={{padding:40}}>불러오는 중…</div>;

  const m = data.manager;
  const k = data.kpis;
  const next = data.next_visit;

  return (
    <div className="app" style={{background:'var(--bg)', overflow:'hidden'}}>
      <StatusBar/>
      <div style={{padding:'14px 22px 6px', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
        <div>
          <div style={{fontSize:11, color:'var(--ink-3)'}}>{new Date().toLocaleDateString('ko-KR')}</div>
          <div style={{fontSize:18, fontWeight:800, marginTop:2}}>좋은 아침이에요, <span style={{color:'var(--orange-600)'}}>{m.name.slice(1)}</span>님</div>
        </div>
        <div style={{display:'flex', alignItems:'center', gap:10}}>
          <a href="/m/manual" title="케어매니저 사용자 매뉴얼"
            style={{display:'inline-flex', alignItems:'center', gap:4, padding:'4px 8px', borderRadius:7, border:'1px solid var(--border-soft)', background:'var(--surface)', color:'var(--ink-1)', fontSize:10.5, fontWeight:700, textDecoration:'none', whiteSpace:'nowrap'}}>
            <span style={{display:'inline-flex', width:13, height:13, color:'var(--orange-600)'}}>{Ic.help}</span>
            사용자 매뉴얼
          </a>
          <a href="/m/notifications" aria-label={`알림 ${unread}건`}
            style={{position:'relative', color:'var(--ink-1)', textDecoration:'none'}}>
            <span style={{width:22, height:22, display:'inline-block'}}>{Ic.bell}</span>
            {unread > 0 && <span style={{position:'absolute', top:-2, right:-2, minWidth:14, height:14, padding:'0 3px', borderRadius:99, background:'var(--orange-500)', color:'#fff', fontSize:9, fontWeight:800, display:'grid', placeItems:'center'}}>{unread > 99 ? '99+' : unread}</span>}
          </a>
        </div>
      </div>

      {next && (
      <div style={{margin:'14px 22px 0', padding:18, borderRadius:18, background:'linear-gradient(140deg, #E8825B, #D26A44)', color:'#fff', position:'relative', overflow:'hidden'}}>
        <div style={{position:'absolute', top:-30, right:-30, width:140, height:140, borderRadius:'50%', background:'rgba(255,255,255,0.12)'}}/>
        <a href={`/m/visit?id=${next.id}`}
           style={{display:'block', color:'#fff', textDecoration:'none', position:'relative', zIndex:1}}
           aria-label={`다음 방문: ${next.elderly_name}`}>
          <div style={{fontSize:10, fontWeight:700, opacity:.9, letterSpacing:'0.08em'}}>다음 방문</div>
          <div style={{fontSize:22, fontWeight:800, marginTop:6, lineHeight:1.25}}>{next.elderly_name} 어르신<br/><span style={{fontSize:13, fontWeight:600, opacity:.95}}>{next.time} · {next.address}{next.distance_km != null ? ` (${next.distance_km}km)` : ''}</span></div>
          <div style={{display:'flex', alignItems:'center', gap:8, marginTop:14, padding:'10px 12px', background:'rgba(255,255,255,0.16)', borderRadius:10}}>
            <span className="badge" style={{background:'rgba(0,0,0,0.18)', color:'#fff', fontSize:10}}>{RISK_LABEL[next.risk_level]}</span>
            <span style={{fontSize:11, opacity:.95}}>복약 {next.medication_count}종</span>
          </div>
        </a>
        <div style={{display:'flex', gap:8, marginTop:12, position:'relative', zIndex:1}}>
          <a href={`/m/map?visit_id=${next.id}`}
             style={{flex:1, padding:'10px 0', background:'#fff', color:'var(--orange-700)', border:'none', borderRadius:10, fontWeight:700, fontSize:13, textAlign:'center', textDecoration:'none'}}>
            출발 · 내비
          </a>
          {next.guardian_phone ? (
            <a href={`tel:${next.guardian_phone}`} aria-label="보호자 전화"
               style={{padding:'10px 14px', background:'rgba(255,255,255,0.2)', color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:13, display:'inline-flex', alignItems:'center', textDecoration:'none'}}>
              <span style={{width:16,height:16, display:'inline-block'}}>{Ic.phone}</span>
            </a>
          ) : (
            <button type="button" disabled title="보호자 전화번호 미등록"
                    style={{padding:'10px 14px', background:'rgba(255,255,255,0.12)', color:'rgba(255,255,255,0.5)', border:'none', borderRadius:10, fontWeight:700, fontSize:13, cursor:'not-allowed'}}>
              <span style={{width:16,height:16, display:'inline-block', verticalAlign:'middle'}}>{Ic.phone}</span>
            </button>
          )}
        </div>
      </div>
      )}

      <div style={{padding:'18px 22px 0', display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8}}>
        {[
          {l:'오늘', v:String(k.today_total), s:`${k.today_done} 완료`, c:'orange'},
          {l:'담당', v:String(k.assigned), s:`위험 ${k.critical}`, c:'green'},
          {l:'주간', v:`${k.weekly_hours}h`, s:`/${k.max_hours ?? 40}h`, c:'sky'},
        ].map((s,i)=>(
          <div key={i} className="card" style={{padding:'12px 14px'}}>
            <div className="muted" style={{fontSize:10, fontWeight:600}}>{s.l}</div>
            <div style={{fontSize:22, fontWeight:800, color:`var(--${s.c}-600)`, lineHeight:1.1, marginTop:2}}>{s.v}</div>
            <div className="muted" style={{fontSize:10}}>{s.s}</div>
          </div>
        ))}
      </div>

      {data.match_proposals_count > 0 && (
      <a href="/m/match" style={{margin:'14px 22px 0', padding:14, borderRadius:14, background:'var(--orange-50)', border:'1px dashed var(--orange-300)', display:'flex', alignItems:'center', gap:12, textDecoration:'none', color:'inherit'}}>
        <span style={{width:34, height:34, borderRadius:10, background:'var(--orange-500)', color:'#fff', display:'grid', placeItems:'center', fontWeight:800, fontSize:11, flex:'0 0 auto'}}>AI</span>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:12.5, fontWeight:700}}>새 매칭 제안 {data.match_proposals_count}건</div>
          <div className="muted" style={{fontSize:10.5}}>탭하여 확인</div>
        </div>
        <span className="btn btn-primary btn-sm" style={{pointerEvents:'none'}}>보기 →</span>
      </a>
      )}

      <div style={{padding:'18px 22px 14px', flex:1, overflow:'auto'}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10}}>
          <div style={{fontSize:13, fontWeight:700}}>오늘의 방문 ({data.today_visits.length})</div>
          <a href="/m/schedule" style={{fontSize:11, color:'var(--orange-600)', fontWeight:600, textDecoration:'none'}}>전체 →</a>
        </div>
        {data.today_visits.length > 0 && (
          <div style={{display:'flex', gap:6, marginBottom:10, flexWrap:'wrap'}}>
            {[
              { k: null,          l: '전체' },
              { k: 'planned',     l: '예정' },
              { k: 'in_progress', l: '진행중' },
              { k: 'completed',   l: '완료' },
            ].map((t) => {
              const active = todayFilter === t.k;
              return (
                <button key={t.l} type="button" onClick={() => setTodayFilter(t.k)} aria-pressed={active}
                        style={{padding:'5px 12px', borderRadius:99, border:'none', cursor:'pointer', fontSize:12, fontWeight:600,
                          background: active ? 'var(--orange-500)' : 'var(--bg-sub)', color: active ? '#fff' : 'var(--ink-2)'}}>
                  {t.l}
                </button>
              );
            })}
          </div>
        )}
        {data.today_visits.length === 0 && <div className="muted" style={{fontSize:12, textAlign:'center', padding:'40px 0'}}>오늘 예정된 방문이 없습니다.</div>}
        {data.today_visits.length > 0 && data.today_visits.filter(v => !todayFilter || v.status === todayFilter).length === 0 && (
          <div className="muted" style={{fontSize:12, textAlign:'center', padding:'40px 0'}}>조건에 맞는 방문이 없습니다.</div>
        )}
        {data.today_visits.filter(v => !todayFilter || v.status === todayFilter).map((v) => {
          const isNext = v.is_next;
          const c = v.status === 'completed' ? 'green' : v.status === 'in_progress' ? 'orange' : isNext ? 'orange' : 'gray';
          return (
            <a key={v.id} href={`/m/visit?id=${v.id}`}
               className="card"
               style={{padding:'12px 14px', marginBottom:8, display:'flex', alignItems:'center', gap:12, borderLeft: `3px solid var(--${c==='gray'?'ink-4':c+'-500'})`, textDecoration:'none', color:'inherit', cursor:'pointer'}}>
              <div style={{minWidth:42, fontSize:12, fontWeight:800, color: isNext?'var(--orange-700)':'var(--ink-2)'}}>{v.time}</div>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:13.5, fontWeight:700}}>{v.elderly_name} 어르신</div>
                <div className="muted" style={{fontSize:11}}>{v.address}</div>
              </div>
              <span className={"badge badge-" + (v.status==='completed'?'green':isNext?'orange':'gray')}>{isNext && v.status!=='completed' ? '다음' : STATUS_LABEL[v.status]}</span>
            </a>
          );
        })}
      </div>
      <TabBar active="home"/>
      <HomeBar/>
    </div>
  );
}

// ISO(UTC) → datetime-local input 값("YYYY-MM-DDThh:mm"). 컴포넌트를 로컬 시각으로
// 직접 조립한다 — toISOString().slice 류는 KST에서 하루/시간이 밀리므로 쓰지 않는다.
function toLocalDatetimeInput(iso) {
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

// ────────────────────────────────────────────────────
// 2. 방문 상세 + 체크인
//
// Footer state machine drives the primary CTA:
//   planned     → "방문 시작 →" (PATCH status=in_progress + check_in_at=now)
//   in_progress → "방문 종료 → 보고서" (PATCH status=completed + check_out_at=now, navigate)
//   completed   → "보고서 작성 →" (navigate to /m/report?visit_id=…)
//   cancelled   → 행위 없음, 안내만
function MgrVisit() {
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  // 404(대상 방문이 삭제됨 — 오래된 알림의 dangling link)는 일반 오류와 구분해
  // 막다른 텍스트 대신 안내+다른 길 화면을 띄운다.
  const [notFound, setNotFound] = React.useState(false);
  // 메모는 별도 로컬 상태로 보관해 textarea 입력 중 서버 응답으로 덮어쓰지 않게 한다.
  // noteSaved 플래그는 저장 직후 짧게 피드백을 띄울 때 쓴다.
  const [noteDraft, setNoteDraft] = React.useState("");
  const [noteDirty, setNoteDirty] = React.useState(false);
  const [noteSaved, setNoteSaved] = React.useState(false);
  // 일정 변경(reschedule) 인라인 에디터 — 데스크톱 /schedule로 이탈하지 않고
  // 모바일 셸 안에서 PATCH scheduled_at/duration_mins 로 처리한다.
  const [reschedOpen, setReschedOpen] = React.useState(false);
  const [reschedAt, setReschedAt]     = React.useState("");
  const [reschedDur, setReschedDur]   = React.useState("");

  const loadVisit = React.useCallback(async (id) => {
    try {
      const body = await mobileFetch(`/api/v1/mobile/visit/${id}`);
      setData(body);
      // 편집 중이 아닐 때만 서버 값으로 draft 동기화 — 입력 중 덮어쓰기 방지.
      setNoteDirty(prev => {
        if (!prev) setNoteDraft(body?.visit?.note || "");
        return prev;
      });
    } catch (e) {
      // 삭제된 방문(404)은 dead link — 전용 안내 화면으로. 그 외는 일반 오류.
      if (e?.status === 404) setNotFound(true);
      else setErr(humanMobileError(e));
    }
  }, []);

  const retry = React.useCallback(() => {
    setErr(null); setNotFound(false);
    const id = new URLSearchParams(window.location.search).get("id");
    if (id) loadVisit(id);
  }, [loadVisit]);

  // 뒤로가기는 직전 화면(일정·홈·케이스·알림 등 진입한 곳)으로 복귀한다.
  // 과거엔 /m/home 으로 하드코딩돼 일정에서 들어와도 홈으로 튕겼다.
  // 인앱 히스토리가 없는 딥링크 직접 진입만 일정(/m/schedule)으로 폴백한다.
  const goBack = React.useCallback(() => {
    if (window.history.length > 1) window.history.back();
    else window.location.href = "/m/schedule";
  }, []);

  React.useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("id");
    if (id) { loadVisit(id); return; }
    // 진행 중 → 예정 → 첫 항목 순으로 자동 선택.
    fetch("/api/v1/visits", { credentials: "same-origin" }).then(r => r.json()).then(j => {
      const first = j.visits.find(v => v.status === "in_progress")
                 || j.visits.find(v => v.status === "planned")
                 || j.visits[0];
      if (first) loadVisit(first.id);
    });
  }, [loadVisit]);

  const patchVisit = React.useCallback(async (body) => {
    if (!data?.visit?.id || busy) return;
    setBusy(true); setErr(null);
    try {
      await mobileFetch(`/api/v1/visits/${data.visit.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ visit: body }),
      });
      await loadVisit(data.visit.id);
    } catch (e) {
      setErr(humanMobileError(e));
    } finally {
      setBusy(false);
    }
  }, [data?.visit?.id, busy, loadVisit]);

  // 일정 변경 저장 — datetime-local(로컬 시각)을 ISO로 변환해 PATCH.
  // 충돌(409)·권한(403) 등은 서버 메시지를 그대로 노출하고 에디터를 열어둔다.
  const saveReschedule = React.useCallback(async () => {
    if (!data?.visit?.id || busy || !reschedAt) return;
    setBusy(true); setErr(null);
    try {
      await mobileFetch(`/api/v1/visits/${data.visit.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ visit: {
          scheduled_at:  new Date(reschedAt).toISOString(),
          duration_mins: Number(reschedDur) || undefined,
        } }),
      });
      await loadVisit(data.visit.id);
      setReschedOpen(false);
    } catch (e) {
      setErr(humanMobileError(e));
    } finally {
      setBusy(false);
    }
  }, [data?.visit?.id, busy, reschedAt, reschedDur, loadVisit]);

  const saveNote = React.useCallback(async () => {
    if (!data?.visit?.id || busy) return;
    await patchVisit({ note: noteDraft });
    setNoteDirty(false);
    setNoteSaved(true);
    window.setTimeout(() => setNoteSaved(false), 1800);
  }, [data?.visit?.id, busy, patchVisit, noteDraft]);

  // 체크인/체크아웃은 정산·감사 타임스탬프를 남기고 일반 사용자에겐 비가역적이라
  // 오작동 방지용 confirm를 둔다. endVisit은 성공 시 보고서 화면으로 이동하므로
  // 그 사실도 함께 안내한다.
  const startVisit = React.useCallback(() => {
    const name = data?.elderly?.name;
    if (!window.confirm(`${name ? name + " 어르신 " : ""}방문을 시작(체크인)하시겠습니까?\n지금 시각으로 기록됩니다.`)) return;
    patchVisit({ status: "in_progress", check_in_at: new Date().toISOString() });
  }, [data?.elderly?.name, patchVisit]);

  const endVisit = React.useCallback(async () => {
    if (!data?.visit?.id) return;
    const name = data?.elderly?.name;
    if (!window.confirm(`${name ? name + " 어르신 " : ""}방문을 종료(체크아웃)하시겠습니까?\n완료 처리 후 보고서 작성 화면으로 이동합니다.`)) return;
    const vid = data.visit.id;
    await patchVisit({ status: "completed", check_out_at: new Date().toISOString() });
    // 종료 직후 보고서 작성 화면으로. 다른 visit 갱신과 race 없도록 약간 지연.
    window.location.href = `/m/report?visit_id=${vid}`;
  }, [data?.visit?.id, data?.elderly?.name, patchVisit]);

  if (notFound || (err && !data)) {
    const isMissing = notFound;
    return (
      <div className="app" style={{background:'var(--bg)', overflow:'hidden'}}>
        <StatusBar/>
        <div style={{padding:'8px 22px 12px', display:'flex', alignItems:'center', gap:14}}>
          <a onClick={() => history.back()} aria-label="뒤로"
             style={{width:32, height:32, display:'grid', placeItems:'center', borderRadius:10, background:'var(--surface)', color:'var(--ink-1)', cursor:'pointer', textDecoration:'none'}}>‹</a>
          <div style={{fontSize:14, fontWeight:700, flex:1}}>방문 상세</div>
        </div>
        <div style={{flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', padding:'0 32px', gap:14}}>
          <div style={{width:64, height:64, borderRadius:99, background:'var(--bg-sub)', display:'grid', placeItems:'center', fontSize:28}} aria-hidden="true">🗓️</div>
          <div>
            <div style={{fontSize:16, fontWeight:800, marginBottom:6}}>
              {isMissing ? '방문을 찾을 수 없습니다' : '방문을 불러오지 못했습니다'}
            </div>
            <div className="muted" style={{fontSize:12.5, lineHeight:1.6, whiteSpace:'pre-line'}}>
              {isMissing
                ? '이 방문은 삭제되었거나 더 이상 볼 수 없습니다.\n오래된 알림일 수 있어요 — 알림함이나 일정에서 다시 확인해 주세요.'
                : (err || '잠시 후 다시 시도해 주세요.')}
            </div>
          </div>
          <div style={{display:'flex', gap:8, width:'100%', maxWidth:320, marginTop:4}}>
            <a href="/m/notifications" className="btn btn-ghost btn-lg"
               style={{flex:1, textDecoration:'none', display:'inline-flex', alignItems:'center', justifyContent:'center'}}>알림함</a>
            {isMissing ? (
              <a href="/m/schedule" className="btn btn-primary btn-lg"
                 style={{flex:1, textDecoration:'none', display:'inline-flex', alignItems:'center', justifyContent:'center'}}>일정 보기</a>
            ) : (
              <button type="button" onClick={retry} className="btn btn-primary btn-lg" style={{flex:1}}>다시 시도</button>
            )}
          </div>
        </div>
        <HomeBar/>
      </div>
    );
  }
  if (!data) return <div className="app muted" style={{padding:40}}>불러오는 중…</div>;

  const v = data.visit;
  const e = data.elderly;

  return (
    <div className="app" style={{background:'var(--bg)', overflow:'hidden'}}>
      <StatusBar/>
      <div style={{padding:'8px 22px 12px', display:'flex', alignItems:'center', gap:14}}>
        <a onClick={goBack} aria-label="뒤로"
           style={{width:32, height:32, display:'grid', placeItems:'center', borderRadius:10, background:'var(--surface)', color:'var(--ink-1)', textDecoration:'none', cursor:'pointer'}}>‹</a>
        <div style={{flex:1}}>
          <div style={{fontSize:11, color:'var(--ink-3)'}}>방문 #V{String(v.id).padStart(5,'0')} · {STATUS_LABEL[v.status]}</div>
          <div style={{fontSize:14, fontWeight:700}}>{new Date(v.scheduled_at).toLocaleTimeString('ko-KR',{hour:'2-digit',minute:'2-digit'})} {e.name} 어르신</div>
        </div>
        <span className={"badge badge-" + RISK_COLOR[e.risk_level]}>{RISK_LABEL[e.risk_level]}</span>
      </div>

      {err && (
        <div role="alert" style={{margin:'0 22px 8px', padding:'10px 12px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>{err}</div>
      )}

      <div style={{margin:'4px 22px 0', padding:18, borderRadius:16, background:'linear-gradient(135deg, #2A241F, #3E3329)', color:'#F5EBD8', position:'relative', overflow:'hidden'}}>
        <div style={{display:'flex', alignItems:'center', gap:12}}>
          <div className="avatar lg" style={{background:'linear-gradient(135deg, #EFB287, #E8825B)'}}>{e.name[0]}</div>
          <div>
            <div style={{fontSize:11, opacity:.7}}>{e.age}세</div>
            <div style={{fontSize:18, fontWeight:800, color:'#fff'}}>{e.name}</div>
            <div style={{fontSize:11, opacity:.8, marginTop:2}}>{e.address}</div>
          </div>
        </div>
        <div style={{display:'flex', gap:8, marginTop:14, paddingTop:14, borderTop:'1px solid rgba(255,255,255,0.12)'}}>
          {[
            {l:'위험도', v:RISK_LABEL[e.risk_level]},
            {l:'복약', v:`${e.medications.length}종`},
            {l:'보호자', v:e.guardian_name || '없음'},
          ].map((s,i)=>(
            <div key={i} style={{flex:1, overflow:'hidden'}}>
              <div style={{fontSize:9, color:'#A89A7C'}}>{s.l}</div>
              <div style={{fontSize:13, fontWeight:700, color:'#fff', marginTop:2}}>{s.v}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{margin:'14px 22px 0', padding:14, borderRadius:14, background:'var(--orange-50)', border:'1px solid var(--orange-100)'}}>
        <div style={{fontSize:11, fontWeight:700, color:'var(--orange-700)', letterSpacing:'0.06em'}}>체크인</div>
        <div style={{display:'flex', alignItems:'center', gap:12, marginTop:6}}>
          <span style={{width:40, height:40, borderRadius:10, background:'var(--orange-500)', color:'#fff', display:'grid', placeItems:'center'}}>
            <span style={{width:22, height:22, display:'inline-block'}}>{Ic.map}</span>
          </span>
          <div style={{flex:1}}>
            <div style={{fontSize:13.5, fontWeight:700}}>{v.check_in_at ? '체크인 완료' : '체크인 전'}</div>
            <div className="muted" style={{fontSize:11}}>{v.check_in_at ? new Date(v.check_in_at).toLocaleString('ko-KR') : "하단 '방문 시작'을 누르면 지금 시각으로 체크인됩니다"}</div>
          </div>
          {/* 체크인 카드는 체크인 상태 표시 전용(미체크인/완료+시각). 체크인은 GPS 자동이 아니라
              하단 '방문 시작 →' 수동 클릭으로 현재 시각 기록 — 문구도 그에 맞춰 둔다. 시작 동작은
              하단 기본 CTA로 단일화(과거엔 같은 startVisit을 부르는 '시작' 버튼이 여기 하나 더 있었다). */}
        </div>
      </div>

      <div style={{padding:'18px 22px 0', flex:1, overflow:'auto'}}>
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10}}>
          <div style={{fontSize:13, fontWeight:700}}>방문 메모</div>
          {noteSaved && <div style={{fontSize:10.5, color:'var(--green-700)', fontWeight:600}}>✓ 저장됨</div>}
        </div>
        <div className="card" style={{padding:12, marginBottom:14}}>
          <textarea
            value={noteDraft}
            onChange={(ev) => { setNoteDraft(ev.target.value); setNoteDirty(true); }}
            placeholder="현장 상황 · 인수인계 메모를 남겨주세요."
            rows={3}
            style={{width:'100%', resize:'vertical', minHeight:64, padding:10, border:'1px solid var(--border-soft)', borderRadius:8, background:'var(--bg-sub)', fontSize:12.5, lineHeight:1.5, fontFamily:'inherit', color:'var(--ink-1)', boxSizing:'border-box'}}
          />
          <div style={{display:'flex', justifyContent:'flex-end', marginTop:8}}>
            <button type="button" className="btn btn-primary btn-sm" onClick={saveNote} disabled={busy || !noteDirty}>
              {busy ? '저장 중…' : '메모 저장'}
            </button>
          </div>
        </div>

        <div style={{fontSize:13, fontWeight:700, marginBottom:10}}>건강 상태</div>
        <div style={{padding:12, background:'var(--surface)', borderRadius:10, fontSize:12.5, marginBottom:14}}>{e.health_status}</div>

        <div style={{fontSize:13, fontWeight:700, marginBottom:10}}>복약 ({e.medications.length}종)</div>
        {e.medications.map((med, i) => {
          const m = typeof med === "string" ? { name: med } : (med || {});
          const sub = [m.dosage, m.frequency, m.time_of_day].filter(Boolean).join(" · ");
          return (
            <div key={m.id ?? i} className="card" style={{padding:'10px 14px', marginBottom:6, display:'flex', alignItems:'center', gap:10}}>
              <span style={{width:30, height:30, borderRadius:8, background:'var(--orange-50)', color:'var(--orange-600)', display:'grid', placeItems:'center', fontWeight:800, fontSize:12, flexShrink:0}}>℞</span>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:13, fontWeight:600}}>{m.name || '이름 없음'}</div>
                {sub && <div className="muted" style={{fontSize:11, marginTop:1}}>{sub}</div>}
              </div>
            </div>
          );
        })}
      </div>

      {reschedOpen && (
        <div style={{margin:'0 22px 4px', padding:14, borderRadius:14, background:'var(--surface)', border:'1px solid var(--border-soft)'}}>
          <div style={{fontSize:12.5, fontWeight:700, marginBottom:10}}>일정 변경</div>
          <label style={{display:'block', fontSize:11, color:'var(--ink-3)', fontWeight:600, marginBottom:4}}>날짜·시간</label>
          <input type="datetime-local" value={reschedAt} onChange={(ev) => setReschedAt(ev.target.value)}
                 min={toLocalDatetimeInput(new Date())}
                 style={{width:'100%', padding:'9px 10px', border:'1px solid var(--border-soft)', borderRadius:8, background:'var(--bg-sub)', fontSize:12.5, fontFamily:'inherit', color:'var(--ink-1)', boxSizing:'border-box'}}/>
          <label style={{display:'block', fontSize:11, color:'var(--ink-3)', fontWeight:600, margin:'10px 0 4px'}}>방문 시간(분)</label>
          <input type="number" min={15} step={15} value={reschedDur} onChange={(ev) => setReschedDur(ev.target.value)}
                 style={{width:'100%', padding:'9px 10px', border:'1px solid var(--border-soft)', borderRadius:8, background:'var(--bg-sub)', fontSize:12.5, fontFamily:'inherit', color:'var(--ink-1)', boxSizing:'border-box'}}/>
          <div style={{display:'flex', gap:8, marginTop:12}}>
            <button type="button" className="btn btn-ghost btn-md" style={{flex:1}} onClick={() => setReschedOpen(false)} disabled={busy}>취소</button>
            <button type="button" className="btn btn-primary btn-md" style={{flex:1}} onClick={saveReschedule} disabled={busy || !reschedAt}>
              {busy ? '저장 중…' : '변경 저장'}
            </button>
          </div>
        </div>
      )}

      <div style={{marginTop:'auto', padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', gap:8, background:'var(--surface)'}}>
        {/* 일정 변경은 예정(planned) 방문만 — 완료·취소·진행중 방문의 시간을 바꾸는 건
            지난 기록 변조라 백엔드(reschedule_not_allowed)도 막는다. 버튼도 같이 숨긴다. */}
        {v.status === 'planned' && (
          <button type="button"
             onClick={() => {
               setReschedAt(toLocalDatetimeInput(v.scheduled_at));
               setReschedDur(String(v.duration_mins || 60));
               setReschedOpen((o) => !o);
             }}
             className="btn btn-ghost btn-lg" style={{flex:1, justifyContent:'center'}}>
            일정 변경
          </button>
        )}
        {v.status === 'planned' && (
          <button type="button" className="btn btn-primary btn-lg" style={{flex:2}} onClick={startVisit} disabled={busy}>
            {busy ? '처리 중…' : '방문 시작 →'}
          </button>
        )}
        {v.status === 'in_progress' && (
          <button type="button" className="btn btn-primary btn-lg" style={{flex:2}} onClick={endVisit} disabled={busy}>
            {busy ? '처리 중…' : '방문 종료 → 보고서'}
          </button>
        )}
        {v.status === 'completed' && (
          // 이미 보고서가 있으면 작성이 아니라 수정으로 보낸다(중복 작성 방지).
          <a href={data.report ? `/m/report?visit_id=${v.id}&mode=edit` : `/m/report?visit_id=${v.id}`}
             className="btn btn-primary btn-lg"
             style={{flex:2, textDecoration:'none', display:'inline-flex', justifyContent:'center'}}>
            {data.report ? '보고서 수정 →' : '보고서 작성 →'}
          </a>
        )}
        {v.status === 'cancelled' && (
          <div className="muted" style={{flex:2, textAlign:'center', alignSelf:'center', fontSize:12}}>취소된 방문입니다</div>
        )}
      </div>
      <HomeBar/>
    </div>
  );
}

// ────────────────────────────────────────────────────
// 매칭 결정 액션별 확인 시트 카피. 각 결정의 영향이 다르기 때문에
// 동일 다이얼로그 안에서 제목·본문·CTA 색상을 분기한다.
//   accepted — 즉시 일정 추가 + 형제 후보 일괄 hold 이동. 가장 영향이 큼.
//   rejected — 동일 제안 재노출 없음. 일방향.
//   hold     — 다시 결정 가능. 가장 가벼움.
const DECISION_CONFIRM = {
  accepted: {
    title: "수락하시겠어요?",
    body:  "지금 어르신이 담당으로 배정되고 본인 일정에 자동 추가됩니다. 다른 후보 매칭은 일괄 보류로 전환됩니다.",
    cta:   "수락 · 일정 추가",
    ctaClass: "btn btn-primary btn-lg",
  },
  rejected: {
    title: "거절하시겠어요?",
    body:  "거절 후에는 같은 어르신의 동일 제안이 다시 노출되지 않습니다.",
    cta:   "거절",
    ctaClass: "btn btn-lg",
    ctaStyle: { background: "var(--rose-500)", color: "#fff" },
  },
  hold: {
    title: "보류하시겠어요?",
    body:  "보류 처리하면 이번 큐에서 제외되고, 다음 매칭 검토 시 다시 검토 후보로 올라옵니다.",
    cta:   "보류",
    ctaClass: "btn btn-ghost btn-lg",
  },
};

// 좌우 스와이프로 카드 간 이동을 트리거할 임계 픽셀값. 짧은 탭에는 반응하지 않고
// 의도가 분명한 드래그에만 idx 가 한 칸 움직이도록 설정.
const BROWSE_THRESHOLD = 80;

// 3. 매칭 제안 (AI Push) — 좌우 스와이프로 카드 탐색, 결정은 버튼+확인 시트 전용.
// 도메인이 영향이 큰(일정·다른 후보 cascade) 의사결정이라 Tinder식 "스와이프=결정"은
// 위험. 스와이프는 단순 탐색(이전/다음)만 담당하고 결정은 명시적 클릭으로 분리한다.
function MgrMatch() {
  const [inbox, setInbox]   = React.useState(null);   // null=loading, []=empty, [...]=cards
  const [idx, setIdx]       = React.useState(0);      // 현재 카드 인덱스
  const [busy, setBusy]     = React.useState(false);
  const [err, setErr]       = React.useState(null);
  const [feedback, setFeedback] = React.useState(null);
  const [pending, setPending] = React.useState(null); // null | "hold" | "rejected" | "accepted"

  // 좌우 드래그 오프셋. 손가락이 떼지면 임계치 통과 시 idx 갱신, 미달이면 0으로 스프링백.
  const [dx, setDx]       = React.useState(0);
  const [dragging, setDragging] = React.useState(false);
  const startRef = React.useRef(null);

  const loadInbox = React.useCallback(async () => {
    setErr(null);
    try {
      const j = await mobileFetch("/api/v1/mobile/manager/match_inbox");
      setInbox(j.matches || []);
      setIdx(0);
    } catch (e) {
      setErr(humanMobileError(e));
    }
  }, []);

  React.useEffect(() => { loadInbox(); }, [loadInbox]);

  const current = inbox && idx < inbox.length ? inbox[idx] : null;

  // 결정 호출. 성공 시 현재 카드를 큐에서 제거하고 다음 카드 노출.
  const decide = React.useCallback(async (decision) => {
    if (!current?.match?.id || busy) return;
    setBusy(true); setErr(null);
    try {
      await mobileFetch(`/api/v1/matches/${current.match.id}/decide`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ decision }),
      });
      const label = decision === "accepted" ? "수락" : decision === "hold" ? "보류" : "거절";
      setFeedback({ kind: "success", text: `${current.elderly?.name || '제안'} ${label} 처리됨` });
      // 처리된 카드는 inbox에서 제거. 같은 idx가 자동으로 "다음" 카드를 가리키게 됨.
      // (idx를 그대로 두면 끝에 도달했을 때 빈 큐 화면으로 자연스럽게 흘러감.)
      setInbox(arr => (arr ? arr.filter(it => it.match.id !== current.match.id) : arr));
      // 끝에 있던 카드를 처리한 경우 idx가 범위를 벗어날 수 있어 한 칸 당김.
      setIdx(i => Math.max(0, Math.min(i, (inbox?.length || 1) - 2)));
      setDx(0);
    } catch (e) {
      setFeedback({ kind: "error", text: humanMobileError(e) });
    } finally {
      setBusy(false);
    }
  }, [current?.match?.id, current?.elderly?.name, busy, inbox?.length]);

  const confirmPending = React.useCallback(async () => {
    if (!pending || busy) return;
    await decide(pending);
    setPending(null);
  }, [pending, busy, decide]);

  const cancelPending = React.useCallback(() => {
    if (busy) return;
    setPending(null);
  }, [busy]);

  // 드래그 — 좌우 탐색 전용. 임계치 통과 시 idx ± 1, 미달이면 스프링백.
  const onPointerDown = (ev) => {
    if (busy || pending) return;
    ev.currentTarget.setPointerCapture(ev.pointerId);
    startRef.current = ev.clientX;
    setDragging(true);
  };
  const onPointerMove = (ev) => {
    if (startRef.current == null) return;
    setDx(ev.clientX - startRef.current);
  };
  const onPointerUp = () => {
    if (startRef.current == null) return;
    startRef.current = null;
    setDragging(false);
    if (!inbox) { setDx(0); return; }
    // 임계치 통과 + 해당 방향에 카드가 있으면 fly-off → idx 갱신.
    // 양 끝(이전/다음 없음)에서는 스프링백.
    const goNext = dx <= -BROWSE_THRESHOLD && idx < inbox.length - 1;
    const goPrev = dx >=  BROWSE_THRESHOLD && idx > 0;
    if (goNext)      setExit(-1);
    else if (goPrev) setExit(1);
    else             setDx(0);
  };

  // fly-off 트랜지션이 끝나면 실제 idx 변경 + dx 리셋. transitionEnd 이벤트는
  // 카드 노드에 직접 묶기보단 시간 기반으로 트리거해 React 리렌더와 동기화한다.
  const [exit, setExit] = React.useState(0); // 0 | -1(다음으로 가는 중) | 1(이전으로 가는 중)
  React.useEffect(() => {
    if (exit === 0) return;
    const t = window.setTimeout(() => {
      setIdx(i => i + (exit === -1 ? 1 : -1));
      setDx(0);
      setExit(0);
    }, 220);
    return () => window.clearTimeout(t);
  }, [exit]);

  // Esc로 시트 취소 (작업 중엔 무시).
  React.useEffect(() => {
    if (!pending) return;
    const onKey = (e) => { if (e.key === "Escape" && !busy) cancelPending(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [pending, busy, cancelPending]);

  // Feedback auto-clear so the inbox UX stays focused.
  React.useEffect(() => {
    if (!feedback) return;
    const id = window.setTimeout(() => setFeedback(null), 2500);
    return () => window.clearTimeout(id);
  }, [feedback]);

  // 로딩 / 에러 / 빈 큐 분기.
  if (inbox === null && err) return <div className="app muted" style={{padding:40, color:'var(--rose-500)'}}>{err}</div>;
  if (inbox === null)        return <div className="app muted" style={{padding:40}}>불러오는 중…</div>;
  if (!current) {
    return (
      <div className="app" style={{background:'var(--bg)', display:'flex', flexDirection:'column', height:'100%'}}>
        <StatusBar/>
        <div style={{padding:'8px 22px 12px', display:'flex', alignItems:'center', gap:14}}>
          <a href="/m/home" style={{width:32, height:32, display:'grid', placeItems:'center', borderRadius:10, background:'var(--surface)', color:'var(--ink-1)', textDecoration:'none'}}>‹</a>
          <div style={{fontSize:15, fontWeight:700}}>매칭 제안</div>
        </div>
        <div style={{flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'40px 24px', textAlign:'center'}}>
          <div style={{fontSize:54, marginBottom:14}}>✓</div>
          <div style={{fontSize:16, fontWeight:700, marginBottom:6}}>모든 제안을 처리했습니다</div>
          <div className="muted" style={{fontSize:12.5, maxWidth:280}}>새 매칭이 들어오면 알림으로 알려드릴게요.</div>
          <a href="/m/home" className="btn btn-ghost btn-md" style={{marginTop:18, textDecoration:'none'}}>홈으로 →</a>
        </div>
        <HomeBar/>
      </div>
    );
  }

  // 카드 렌더. role='top'은 손가락 따라 움직이는 현재 카드(이벤트 핸들러 보유),
  // role='peek'은 뒤에서 살짝 보이는 다음/이전 카드(y+8 / scale .96 / 그림자 약함).
  const renderCard = (i, role) => {
    const d = inbox[i];
    if (!d) return null;
    const m = d.match;
    const e = d.elderly;
    const dims = [
      {l:'거리', v:`${m.distance_km}km`, s:m.score_distance},
      {l:'자격', v:'매치', s:m.score_qualification},
      {l:'가용', v:'여유', s:m.score_availability},
      {l:'부하', v:'적정', s:m.score_load},
      {l:'이력', v:'양호', s:m.score_history},
      {l:'평가', v:'★', s:m.score_rating},
    ];
    const isTop = role === 'top';
    const interactive = isTop && !pending && !busy && exit === 0;

    // 탑 카드의 transform: 드래그 dx + 회전, fly-off 중엔 화면 밖으로.
    // exit=-1(다음으로) → 좌측으로 fly-off, exit=1(이전으로) → 우측으로 fly-off.
    const topX = exit !== 0 ? exit * 480 : dx;
    const topRot = topX * 0.04;
    const transform = isTop
      ? `translateX(${topX}px) rotate(${topRot}deg)`
      : `translateY(8px) scale(0.96)`;

    return (
      <div key={d.match.id}
        onPointerDown={interactive ? onPointerDown : undefined}
        onPointerMove={interactive ? onPointerMove : undefined}
        onPointerUp={interactive ? onPointerUp : undefined}
        onPointerCancel={interactive ? onPointerUp : undefined}
        style={{
          position:'absolute', left:22, right:22, top:0,
          padding:18, borderRadius:18, background:'var(--surface)',
          border:'1.5px solid var(--orange-500)',
          boxShadow: isTop
            ? 'var(--shadow-lg, 0 12px 32px rgba(0,0,0,0.12))'
            : 'var(--shadow-md)',
          transform,
          transition: dragging ? 'none' : 'transform 0.22s cubic-bezier(0.2, 0.7, 0.3, 1)',
          touchAction: interactive ? 'pan-y' : 'auto',
          cursor: interactive ? 'grab' : 'default',
          opacity: isTop ? 1 : 0.94,
          zIndex: isTop ? 10 : 8,
        }}>
        <div style={{position:'absolute', top:-10, right:18, padding:'4px 12px', background:'var(--orange-500)', color:'#fff', borderRadius:99, fontSize:10, fontWeight:800}}>
          {m.rank}순위 · {i + 1}/{inbox.length}
        </div>
        <div style={{display:'flex', alignItems:'center', gap:12}}>
          <div className="avatar lg">{e?.name[0]}</div>
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontSize:16, fontWeight:800}}>{e?.name} 어르신 · {e?.age}세</div>
            <div className="muted" style={{fontSize:11}}>{e?.address} · {RISK_LABEL[e?.risk_level]}</div>
          </div>
          <div style={{textAlign:'right'}}>
            <div className="mono" style={{fontSize:34, fontWeight:800, color:'var(--orange-600)', lineHeight:1}}>{m.score}</div>
            <div className="muted" style={{fontSize:9, fontWeight:700, letterSpacing:'0.1em'}}>MATCH</div>
          </div>
        </div>
        <div style={{marginTop:14, paddingTop:14, borderTop:'1px solid var(--border-soft)', display:'grid', gridTemplateColumns:'1fr 1fr', gap:10}}>
          {dims.map((dim, j) => (
            <div key={j}>
              <div style={{display:'flex', justifyContent:'space-between', fontSize:10, fontWeight:700, marginBottom:4}}>
                <span style={{color:'var(--ink-2)'}}>{dim.l}</span>
                <span className="mono" style={{color: dim.s>=90?'var(--green-600)':'var(--orange-600)'}}>{dim.s}</span>
              </div>
              <div className="bar"><span style={{width:`${dim.s}%`, background: dim.s>=90?'var(--green-500)':'var(--orange-500)'}}/></div>
              <div className="muted" style={{fontSize:10, marginTop:3}}>{dim.v}</div>
            </div>
          ))}
        </div>
        {m.reasoning && (
          <div style={{marginTop:14, padding:12, background:'var(--bg-sub)', borderRadius:10}}>
            <div className="muted" style={{fontSize:10.5, lineHeight:1.5}}>{m.reasoning}</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="app" style={{background:'var(--bg)', overflow:'hidden'}}>
      <StatusBar/>
      <div style={{padding:'8px 22px 12px', display:'flex', alignItems:'center', gap:14}}>
        <a href="/m/home" aria-label="홈으로"
           style={{width:32, height:32, display:'grid', placeItems:'center', borderRadius:10, background:'var(--surface)', color:'var(--ink-1)', textDecoration:'none'}}>‹</a>
        <div style={{flex:1, fontSize:15, fontWeight:700}}>매칭 제안</div>
        <span className="muted" style={{fontSize:11, fontWeight:600}}>{idx + 1}/{inbox.length}건</span>
      </div>

      {feedback && (
        <div role="status" aria-live="polite"
          style={{margin:'0 22px 8px', padding:'8px 12px',
                  background: feedback.kind === 'success' ? 'var(--green-50)' : 'var(--rose-50)',
                  color:      feedback.kind === 'success' ? 'var(--green-700)' : 'var(--rose-500)',
                  borderRadius:10, fontSize:12.5, fontWeight:600}}>
          {feedback.kind === 'success' ? '✓ ' : '⚠ '}{feedback.text}
        </div>
      )}

      <div className="muted" style={{padding:'2px 22px 8px', fontSize:11, textAlign:'center'}}>
        ← 좌우 스와이프로 다른 제안 보기 · 결정은 아래 버튼
      </div>

      {/* 카드 덱 — Tinder식 stack. 탑 카드는 손가락 따라 회전·이동, peek 카드는 뒤에서 살짝. */}
      {/* 의도 방향(드래그/exit 방향)에 따라 peek 카드가 idx+1(다음) 또는 idx-1(이전)로 전환된다. */}
      <div style={{position:'relative', flex:1, minHeight:380, marginTop:6}}>
        {(() => {
          // 의도 방향 결정:
          //  - fly-off 중엔 가는 방향의 카드가 곧 peek (exit=-1 다음행→peek=+1, exit=1 이전행→peek=-1)
          //  - 드래그 중엔 손가락이 향하는 쪽의 카드
          //  - 정지엔 다음 카드 우선 (없으면 이전)
          let dir = 1;
          if (exit !== 0)        dir = -exit;
          else if (dx < 0)       dir = 1;
          else if (dx > 0)       dir = -1;
          let peekIdx = idx + dir;
          // 경계 보정: 다음이 없으면 이전, 둘 다 없으면 null로.
          if (peekIdx < 0 || peekIdx >= inbox.length) peekIdx = idx - dir;
          return renderCard(peekIdx, 'peek');
        })()}
        {renderCard(idx, 'top')}
      </div>

      {/* 좌/우 페이지 인디케이터 도트 — 4건 이하면 단순 도트, 그 이상이면 N/total만 헤더에 의존. */}
      {inbox.length > 1 && inbox.length <= 8 && (
        <div style={{display:'flex', justifyContent:'center', gap:6, padding:'6px 0 2px'}}>
          {inbox.map((_, i) => (
            <span key={i} style={{
              width: i === idx ? 8 : 6,
              height: i === idx ? 8 : 6,
              borderRadius:99,
              background: i === idx ? 'var(--orange-500)' : 'var(--ink-4)',
              transition:'all 0.2s',
            }}/>
          ))}
        </div>
      )}

      <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', gap:8, background:'var(--surface)'}}>
        <button type="button" className="btn btn-ghost btn-lg" style={{flex:1}}
          onClick={()=>setPending("hold")} disabled={busy || !!pending}>
          보류
        </button>
        <button type="button" className="btn btn-ghost btn-lg" aria-label="거절"
          onClick={()=>setPending("rejected")} disabled={busy || !!pending}
          style={{color:'var(--rose-500)'}}>
          {Ic.close}
        </button>
        <button type="button" className="btn btn-primary btn-lg" style={{flex:2}}
          onClick={()=>setPending("accepted")} disabled={busy || !!pending}>
          수락 · 일정 추가 →
        </button>
      </div>

      {pending && (() => {
        const c = DECISION_CONFIRM[pending];
        const e = current?.elderly;
        return (
          <div role="dialog" aria-modal="true" aria-label={c.title}
            onClick={cancelPending}
            style={{position:'absolute', inset:0, zIndex:50, background:'rgba(20,16,12,0.45)',
                    display:'flex', flexDirection:'column', justifyContent:'flex-end'}}>
            <div onClick={(ev)=>ev.stopPropagation()}
              style={{background:'#fff', borderRadius:'20px 20px 0 0', padding:'20px 22px 14px',
                      boxShadow:'0 -8px 28px rgba(0,0,0,0.18)'}}>
              <div style={{width:36, height:4, background:'var(--ink-4)', borderRadius:99, margin:'0 auto 14px'}}/>
              <div style={{fontSize:16, fontWeight:800, marginBottom:6}}>{c.title}</div>
              {e?.name && (
                <div className="muted" style={{fontSize:12, marginBottom:10}}>
                  <b style={{color:'var(--ink-1)'}}>{e.name} 어르신</b>
                  {e.age != null && ` · ${e.age}세`}
                  {e.risk_level && (
                    <span className={"badge badge-" + RISK_COLOR[e.risk_level]}
                      style={{fontSize:9, marginLeft:6}}>{RISK_LABEL[e.risk_level]}</span>
                  )}
                </div>
              )}
              <div style={{fontSize:12.5, lineHeight:1.55, color:'var(--ink-2)', marginBottom:16}}>{c.body}</div>
              <div style={{display:'flex', gap:8}}>
                <button type="button" className="btn btn-ghost btn-lg" style={{flex:1}}
                  onClick={cancelPending} disabled={busy}>
                  취소
                </button>
                <button type="button" className={c.ctaClass} style={{flex:2, ...(c.ctaStyle || {})}}
                  onClick={confirmPending} disabled={busy}>
                  {busy ? '처리 중…' : c.cta}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <HomeBar/>
    </div>
  );
}

// 외부 내비 딥링크 제공자. needsCoord=true 인 제공자는 좌표가 없으면 비활성.
// 각 builder 는 (lat, lng, name, address) → URL string. 좌표 미등록 어르신은
// 텍스트 검색을 지원하는 Naver Maps / Google Maps 만 활성화한다.
//
// 스킴 레퍼런스:
//   네이버:  nmap://route/car?dlat=&dlng=&dname=&appname=
//   T맵:    tmap://route?goalx=&goaly=&goalname=&reqCoordType=WGS84GEO
//   카카오: kakaonavi://navigate?name=&x=&y=&coord_type=wgs84
//   구글:   maps URL (https; 모바일에선 자동 앱 진입)
// build  → 네이티브 앱 딥링크(스킴). 앱 설치 시 OS가 해당 내비 앱을 띄운다.
// web    → 앱 미설치/데스크톱 폴백용 https 웹 지도 URL. openWithWebFallback 이
//          스킴을 먼저 던지고, 앱이 안 열리면 이 URL 로 넘어간다. null 이면 build
//          자체가 이미 웹 URL(구글)이라 폴백이 필요 없다는 뜻.
const NAV_PROVIDERS = [
  {
    key: "naver",
    label: "네이버 지도",
    needsCoord: false,
    build: (lat, lng, name, address) => lat != null && lng != null
      ? `nmap://route/car?dlat=${lat}&dlng=${lng}&dname=${encodeURIComponent(name || address || "도착지")}&appname=integrated-care`
      : `nmap://search?query=${encodeURIComponent(address || name)}&appname=integrated-care`,
    web: (lat, lng, name, address) =>
      `https://map.naver.com/p/search/${encodeURIComponent(address || name || "도착지")}`,
  },
  {
    key: "tmap",
    label: "T맵",
    needsCoord: true,
    build: (lat, lng, name) =>
      `tmap://route?goalx=${lng}&goaly=${lat}&goalname=${encodeURIComponent(name || "도착지")}&reqCoordType=WGS84GEO`,
    // T맵은 소비자용 웹 길찾기가 없어, 미설치 시 범용 웹 길찾기(구글)로 폴백한다.
    web: (lat, lng) => `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
  },
  {
    key: "kakao",
    label: "카카오내비",
    needsCoord: true,
    build: (lat, lng, name) =>
      `kakaonavi://navigate?name=${encodeURIComponent(name || "도착지")}&x=${lng}&y=${lat}&coord_type=wgs84`,
    // 카카오맵 웹 링크 API — 목적지 길찾기를 웹에서 연다.
    web: (lat, lng, name) =>
      `https://map.kakao.com/link/to/${encodeURIComponent(name || "도착지")},${lat},${lng}`,
  },
  {
    key: "google",
    label: "구글 지도",
    needsCoord: false,
    build: (lat, lng, name, address) => lat != null && lng != null
      ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
      : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address || name)}`,
    web: null, // build 가 이미 웹 URL(유니버설 링크) — 폴백 불필요.
  },
];

// 마지막 선택한 제공자를 localStorage에 기억해 다음엔 1-탭 진입.
// 같은 키는 mobile-manager.tsx의 REPORT_DRAFT_PREFIX와 네임스페이스 패턴을 맞춤.
const NAV_PROVIDER_STORAGE_KEY = "care-os.nav-provider";
function loadPreferredNavProvider() {
  if (typeof window === "undefined") return null;
  try { return window.localStorage.getItem(NAV_PROVIDER_STORAGE_KEY) || null; }
  catch { return null; }
}
function savePreferredNavProvider(key) {
  if (typeof window === "undefined" || !key) return;
  try { window.localStorage.setItem(NAV_PROVIDER_STORAGE_KEY, key); }
  catch { /* quota / blocked storage */ }
}

// 네이티브 앱이 안 열려도(앱 미설치·데스크톱) 페이지가 안 가려지면 일정 시간 뒤 폴백.
const NAV_FALLBACK_MS = 1500;

// 네이티브 앱 스킴을 먼저 던지고, 앱이 안 뜨면 웹 지도로 폴백한다.
// 핵심: nmap:// 같은 커스텀 스킴은 핸들러가 없으면(데스크톱/미설치) 조용히 무시돼
// "버튼 눌러도 무반응"이 된다.
//
// 스킴은 top-level location.href 대신 hidden iframe.src 로 던진다 — top-level 로
// 던지면 일부 브라우저가 현재 문서를 에러 페이지로 unload 해 아래 폴백 타이머까지
// 죽어버린다. iframe 은 메인 문서를 유지하므로, 앱이 안 열려도 타이머가 살아 폴백한다.
//
// 앱이 실제로 떠서 탭이 백그라운드로 가면(visibilitychange→hidden) 폴백을 취소해
// 웹까지 같이 열리는 이중진입을 막는다. webUrl 이 없으면(구글) 스킴 자체가 웹이라
// 그냥 top-level 로 이동한다.
function openWithWebFallback(scheme, webUrl) {
  if (typeof window === "undefined") return;
  if (!webUrl) { window.location.href = scheme; return; }

  let cancelled = false;
  const onVisibility = () => { if (document.visibilityState === "hidden") cancelled = true; };
  document.addEventListener("visibilitychange", onVisibility);

  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = scheme;
  document.body.appendChild(iframe);

  window.setTimeout(() => {
    document.removeEventListener("visibilitychange", onVisibility);
    try { iframe.remove(); } catch { /* already gone */ }
    if (!cancelled) window.location.href = webUrl; // 앱 안 열림 → 웹 지도
  }, NAV_FALLBACK_MS);
}

// ────────────────────────────────────────────────────
// 4. 지도 + 내비
// 마커 색/모양/라벨은 markers.ts(단일 출처)로 이관 — 여기서 따로 두지 않는다.

// 케어매니저 모바일 지도 — 가짜 SVG 투영을 실제 네이버 지도로 교체.
// 풀스크린 배경(absolute inset 0, zIndex 0)으로 깔고, 상단 상태바·하단 시트·내비
// 시트는 더 높은 zIndex로 그 위에 뜬다. 데이터는 기존과 동일하게 /api/v1/map/points
// + manager/home 을 재사용한다. 마커: 주변 어르신(위험색 '어'), 다음 방문 목적지
// (강조 오렌지 핀), 유관기관(분류색), 매니저 현위치(파란 점). 프레이밍은 목적지+현위치를
// 함께 담는 bbox(둘 다 있을 때) → 목적지 중심 → 권역 전체 순으로 폴백한다.
function MgrNaverMap({ data, next, me }) {
  const [map, setMap] = React.useState(null);
  const focusNext = next && next.elderly_lat != null && next.elderly_lng != null;

  const frame = React.useMemo(() => {
    const pts = [];
    if (focusNext) pts.push([next.elderly_lat, next.elderly_lng]);
    if (me?.lat != null && me?.lng != null) pts.push([me.lat, me.lng]);
    if (pts.length >= 2) {
      const lats = pts.map(p => p[0]), lngs = pts.map(p => p[1]);
      const padLat = Math.max((Math.max(...lats) - Math.min(...lats)) * 0.4, 0.01);
      const padLng = Math.max((Math.max(...lngs) - Math.min(...lngs)) * 0.4, 0.01);
      return {
        center: { lat: (Math.max(...lats) + Math.min(...lats)) / 2, lng: (Math.max(...lngs) + Math.min(...lngs)) / 2 },
        zoom: 14,
        bbox: { north: Math.max(...lats) + padLat, south: Math.min(...lats) - padLat, east: Math.max(...lngs) + padLng, west: Math.min(...lngs) - padLng },
      };
    }
    if (pts.length === 1) return { center: { lat: pts[0][0], lng: pts[0][1] }, zoom: 14, bbox: null };
    return { center: data.center, zoom: data.zoom || 12, bbox: data.bbox || null };
  }, [focusNext, next, me, data.center, data.zoom, data.bbox]);

  // 좌표/분류/위험도 시그니처가 바뀔 때만 마커 재생성.
  const sig = React.useMemo(() => JSON.stringify({
    e: (data.elderlies || []).map(x => [x.id, x.lat, x.lng, x.risk_level]),
    o: (data.organizations || []).map(x => [x.id, x.lat, x.lng, x.category]),
    n: focusNext ? [next.elderly_id, next.elderly_lat, next.elderly_lng] : null,
    m: me ? [me.lat, me.lng] : null,
  }), [data.elderlies, data.organizations, focusNext, next, me]);

  React.useEffect(() => {
    if (!map || !window.naver?.maps) return;
    const naver = window.naver;
    const markers = [];
    const add = (lat, lng, content, anchor, z, tooltip) => {
      if (lat == null || lng == null) return;
      const marker = new naver.maps.Marker({ map, position: new naver.maps.LatLng(lat, lng), zIndex: z, icon: { content, anchor } });
      attachMarkerTooltip(naver, marker, tooltip);
      markers.push(marker);
    };
    // 데스크톱 지도(/map)와 같은 마커 모양·툴팁을 쓴다(markers.ts 단일 출처).
    const addStyled = (lat, lng, style) => add(lat, lng, style.content,
      new naver.maps.Point(style.anchorX, style.anchorY), style.zIndex, style.tooltip);

    // 유관기관 (분류색 + 분류 픽토그램, 작게)
    (data.organizations || []).forEach(o => {
      addStyled(o.lat, o.lng, markerStyle("org", o, { compact: true }));
    });

    // 주변 어르신 (위험색 핀) — 다음 방문 대상은 아래 강조 핀으로 빼고 제외.
    (data.elderlies || []).forEach(e => {
      if (focusNext && e.id === next.elderly_id) return;
      addStyled(e.lat, e.lng, markerStyle("elderly", e, { compact: true }));
    });

    // 다음 방문 목적지 — 강조 오렌지 핀(어르신 픽토그램 + 선택 글로우).
    if (focusNext) {
      const style = markerStyle("elderly", {
        name: next.elderly_name, risk_level: next.risk_level, address: next.address,
      }, { selected: true, note: "다음 방문" });
      add(next.elderly_lat, next.elderly_lng,
        style.content, new naver.maps.Point(style.anchorX, style.anchorY), 100, style.tooltip);
    }

    // 매니저 현위치 — 파란 점(+ 후광).
    if (me?.lat != null && me?.lng != null) {
      add(me.lat, me.lng,
        `<div style="width:18px;height:18px;border-radius:50%;background:var(--sky-500);border:3px solid #fff;box-shadow:0 0 0 6px rgba(56,160,232,0.18),0 4px 12px rgba(0,0,0,0.25);"></div>`,
        new naver.maps.Point(9, 9), 90, '');
    }

    const unbindTooltip = bindMapTooltipHide(naver, map);
    return () => {
      unbindTooltip();
      markers.forEach(m => { try { m.setMap(null); } catch {} });
    };
  }, [map, sig]);

  return <NaverMapCanvas center={frame.center} zoom={frame.zoom} bbox={frame.bbox} fitPadding={70} onMapReady={setMap}/>;
}

function MgrMap() {
  const [data, setData] = React.useState(null);
  const [home, setHome] = React.useState(null);
  const [navOpen, setNavOpen] = React.useState(false);
  // 마지막 선택한 제공자 키. localStorage 에서 1회 복원, 선택 시 갱신.
  const [preferredKey, setPreferredKey] = React.useState(() => loadPreferredNavProvider());

  React.useEffect(() => {
    fetch("/api/v1/map/points").then(r => r.json()).then(setData);
    fetch("/api/v1/mobile/manager/home").then(r => r.json()).then(setHome);
  }, []);

  const next = home?.next_visit;
  const hasCoord = next?.elderly_lat != null && next?.elderly_lng != null;

  // 매니저 본인 좌표 — 지도 현위치 마커 + 프레이밍용. map/points의 care_managers에서
  // 로그인 매니저(home.manager.id)를 찾아 쓴다(브라우저 geolocation 대신 등록 base 좌표).
  const me = React.useMemo(() => {
    if (!data?.care_managers || !home?.manager?.id) return null;
    return data.care_managers.find(cm => cm.id === home.manager.id) || null;
  }, [data, home]);

  // 저장된 제공자가 현 케이스에 쓸 수 있나? (좌표 필요한데 미등록이면 사용 불가)
  const preferredProvider = React.useMemo(() => {
    if (!preferredKey) return null;
    const p = NAV_PROVIDERS.find(x => x.key === preferredKey);
    if (!p) return null;
    if (p.needsCoord && !hasCoord) return null;
    return p;
  }, [preferredKey, hasCoord]);

  // 외부 내비 발사. 네이티브 앱 스킴을 먼저 던지고, 앱이 안 열리면(미설치·데스크톱)
  // openWithWebFallback 이 웹 지도로 폴백한다 — "버튼 눌러도 무반응"을 없앤다.
  // remember=true 이면 이 선택을 다음 1-탭 진입에 쓰도록 localStorage 에 저장.
  const launchNav = React.useCallback((provider, remember = false) => {
    if (!next || !provider) return;
    if (provider.needsCoord && !hasCoord) return;
    if (remember) {
      savePreferredNavProvider(provider.key);
      setPreferredKey(provider.key);
    }
    const name = `${next.elderly_name} 어르신`;
    const scheme = provider.build(next.elderly_lat, next.elderly_lng, name, next.address);
    const webUrl = provider.web ? provider.web(next.elderly_lat, next.elderly_lng, name, next.address) : null;
    openWithWebFallback(scheme, webUrl);
    setNavOpen(false);
  }, [next, hasCoord]);

  // "내비 시작 →" 메인 액션: 저장된 기본 제공자 있으면 즉시 진입, 아니면 시트 오픈.
  const onPrimaryNav = React.useCallback(() => {
    if (preferredProvider) {
      launchNav(preferredProvider, /* remember */ false);
    } else {
      setNavOpen(true);
    }
  }, [preferredProvider, launchNav]);

  // Esc 로 시트 닫기.
  React.useEffect(() => {
    if (!navOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setNavOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navOpen]);

  return (
    <div className="app" style={{background:'#E7E1CF', overflow:'hidden', position:'relative'}}>
      {/* 실제 네이버 지도 배경. 데이터 도착 전엔 컨테이너 배경색(#E7E1CF)이 보인다. */}
      {data?.center && (
        <div style={{position:'absolute', inset:0, zIndex:0}}>
          <MgrNaverMap data={data} next={next} me={me}/>
        </div>
      )}

      <div style={{position:'relative', zIndex:1}}><StatusBar/></div>
      <div style={{padding:'8px 16px 0', position:'relative', zIndex:1, display:'flex', gap:10, alignItems:'center'}}>
        <a onClick={() => history.back()} style={{width:38, height:38, display:'grid', placeItems:'center', borderRadius:10, background:'#fff', color:'var(--ink-1)', cursor:'pointer', textDecoration:'none', boxShadow:'var(--shadow-md)', flex:'0 0 auto'}}>‹</a>
        {/* 검색 입력이 아니라 현재 안내 중인 경로지를 보여주는 상태 라벨이다.
            이 지도 화면은 decorative(입력·검색 기능 없음)라, 돋보기 아이콘 +
            "검색" 문구는 동작할 것 같은 오해를 줬다 → 지도 아이콘 + 상태 문구로
            바꿔 "표시용"임을 분명히 한다. */}
        <div style={{padding:'10px 14px', background:'#fff', borderRadius:14, boxShadow:'var(--shadow-md)', display:'flex', alignItems:'center', gap:10, flex:1}}>
          <span style={{width:18, height:18, color:'var(--ink-3)'}}>{Ic.map}</span>
          <span style={{fontSize:13, color:'var(--ink-2)', flex:1}}>{next ? `${next.elderly_name} 어르신 · ${next.address}` : '안내 중인 경로 없음'}</span>
          {next && <span style={{padding:'3px 10px', background:'var(--orange-50)', color:'var(--orange-700)', borderRadius:99, fontSize:10, fontWeight:700}}>경로 중</span>}
        </div>
      </div>

      <div style={{position:'absolute', left:0, right:0, bottom:0, background:'#fff', borderRadius:'24px 24px 0 0', padding:'12px 22px 8px', boxShadow:'0 -8px 24px rgba(0,0,0,0.1)', zIndex:3}}>
        <div style={{width:42, height:4, background:'var(--ink-4)', borderRadius:99, margin:'0 auto 14px'}}/>
        {next ? (
          <>
            <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:12}}>
              <div className="avatar lg">{next.elderly_name[0]}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:11, color:'var(--ink-3)'}}>다음 방문 · {next.time}</div>
                <div style={{fontSize:15, fontWeight:800}}>{next.elderly_name} 어르신</div>
              </div>
              <div style={{textAlign:'right'}}>
                <div style={{fontSize:18, fontWeight:800, color: next.distance_km != null ? 'var(--orange-600)' : 'var(--ink-3)'}}>
                  {next.distance_km != null ? `${next.distance_km} km` : '거리 미상'}
                </div>
                {next.distance_km != null && <div className="muted" style={{fontSize:11}}>차량 약 {Math.max(1, Math.round(next.distance_km * 2))}분</div>}
              </div>
            </div>
            <div style={{display:'flex', gap:8}}>
              {/* 보호자 전화 — 번호 등록된 경우만 tel: 링크 활성. 미등록은 비활성 버튼.
                  MgrVisit 화면(line 119-129)과 동일 패턴을 재사용. */}
              {next.guardian_phone ? (
                <a href={`tel:${next.guardian_phone}`}
                   aria-label={`보호자 ${next.guardian_name || ''} 전화 ${next.guardian_phone}`}
                   title={next.guardian_name ? `${next.guardian_name} 보호자` : '보호자 전화'}
                   className="btn btn-ghost btn-lg"
                   style={{flex:1, textDecoration:'none', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:6}}>
                  <span style={{width:16, height:16, display:'inline-block'}}>{Ic.phone}</span>
                  보호자
                </a>
              ) : (
                <button type="button" disabled
                  title="보호자 전화번호 미등록"
                  className="btn btn-ghost btn-lg"
                  style={{flex:1, cursor:'not-allowed', opacity:0.5, display:'inline-flex', alignItems:'center', justifyContent:'center', gap:6}}>
                  <span style={{width:16, height:16, display:'inline-block'}}>{Ic.phone}</span>
                  보호자
                </button>
              )}
              {/* 저장된 기본 제공자가 있으면 1-탭 진입, 라벨에 제공자명 표시.
                  옆 ▾ 버튼(또는 길게 눌러서)로 시트 열어 다른 앱 선택·기본 변경 가능. */}
              <button type="button" className="btn btn-primary btn-lg"
                onClick={onPrimaryNav}
                style={{flex:2, display:'inline-flex', alignItems:'center', justifyContent:'center', gap:6}}>
                {preferredProvider ? `${preferredProvider.label}로 시작` : "내비 시작"} →
              </button>
              {preferredProvider && (
                <button type="button" className="btn btn-ghost btn-lg"
                  onClick={() => setNavOpen(true)}
                  aria-label="다른 내비 앱 선택"
                  title="다른 앱 선택 / 기본 변경"
                  style={{flex:'0 0 44px', padding:0, fontSize:16, fontWeight:700, color:'var(--ink-2)'}}>
                  ▾
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="muted" style={{padding:'20px 0', textAlign:'center'}}>예정된 방문 없음</div>
        )}
        <HomeBar/>
      </div>

      {navOpen && next && (
        <div role="dialog" aria-modal="true" aria-label="내비게이션 앱 선택"
          onClick={() => setNavOpen(false)}
          style={{position:'absolute', inset:0, zIndex:50, background:'rgba(20,16,12,0.45)',
                  display:'flex', flexDirection:'column', justifyContent:'flex-end'}}>
          <div onClick={(ev)=>ev.stopPropagation()}
            style={{background:'#fff', borderRadius:'20px 20px 0 0', padding:'20px 22px 18px',
                    boxShadow:'0 -8px 28px rgba(0,0,0,0.18)'}}>
            <div style={{width:36, height:4, background:'var(--ink-4)', borderRadius:99, margin:'0 auto 14px'}}/>
            <div style={{fontSize:15, fontWeight:800, marginBottom:4}}>어디로 안내할까요?</div>
            <div className="muted" style={{fontSize:11.5, marginBottom:14}}>
              {next.elderly_name} 어르신 · {next.address}
              {!hasCoord && (
                <span style={{display:'block', marginTop:4, color:'var(--rose-500)', fontWeight:600}}>
                  좌표 미등록 — 일부 앱은 비활성
                </span>
              )}
            </div>
            <div style={{display:'flex', flexDirection:'column', gap:8}}>
              {NAV_PROVIDERS.map((p) => {
                const disabled = p.needsCoord && !hasCoord;
                const isDefault = preferredKey === p.key;
                return (
                  <button key={p.key} type="button"
                    onClick={() => launchNav(p, /* remember */ true)} disabled={disabled}
                    style={{
                      display:'flex', alignItems:'center', gap:10, padding:'12px 14px',
                      background: disabled ? 'var(--bg-sub)' : (isDefault ? 'var(--orange-50)' : '#fff'),
                      border: isDefault ? '1.5px solid var(--orange-500)' : '1px solid var(--border)',
                      borderRadius:12,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      color: disabled ? 'var(--ink-3)' : 'var(--ink-1)',
                      fontSize:14, fontWeight:700, textAlign:'left',
                    }}>
                    <span style={{flex:1, display:'flex', alignItems:'center', gap:8}}>
                      {p.label}
                      {isDefault && (
                        <span style={{padding:'1px 7px', fontSize:9.5, fontWeight:800,
                                      background:'var(--orange-500)', color:'#fff', borderRadius:99}}>기본</span>
                      )}
                    </span>
                    {disabled
                      ? <span className="muted" style={{fontSize:10}}>좌표 필요</span>
                      : <span style={{color:'var(--orange-600)', fontSize:14}}>→</span>}
                  </button>
                );
              })}
            </div>
            <div className="muted" style={{marginTop:10, fontSize:10.5, textAlign:'center', lineHeight:1.5}}>
              선택한 앱이 기본으로 저장되어 다음엔 1-탭으로 진입합니다.
            </div>
            <button type="button" onClick={() => setNavOpen(false)}
              style={{marginTop:10, width:'100%', padding:'10px 0', background:'transparent',
                      border:'none', fontSize:13, fontWeight:600, color:'var(--ink-3)', cursor:'pointer'}}>
              취소
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────
// 4-b. 내 케이스 목록 — 케이스 탭 진입점.
// 매니저는 자기 담당 어르신을 여기서 골라 상세(/m/case?id=)로 이동한다.
// 백엔드 cases#index가 manager 역할이면 자동으로 본인 케이스만 반환하므로
// 별도 manager_id 필터를 보낼 필요가 없다.
const CASE_STATUS_LABEL = {
  applying: "보류", matching: "매칭중",
  assigned: "배정", caring: "돌봄중", closed: "종료",
};
const CASE_STATUS_COLOR = {
  applying: "gray", matching: "sky",
  assigned: "orange", caring: "green", closed: "gray",
};
const PRIORITY_LABEL = { critical: "높음", warning: "보통", normal: "낮음" };
const PRIORITY_COLOR = { critical: "rose", warning: "amber", normal: "gray" };

// "진행"은 ACTIVE_STATUSES(미종결 전 단계) 합집합, 그 외 키는 정확히 해당 status.
// 종결 케이스는 load 단계에서 아예 제외하므로 "전체"·"종결" 칩은 두지 않는다
// ("전체"는 진행과 동일·"종결"은 항상 비어 무의미). 매니저는 케이스가 배정(assigned)된
// 이후에만 보게 되지만 ACTIVE_STATUSES 자체는 "진행" 의미가 데스크톱과 어긋나지 않게
// 전 단계를 그대로 유지한다.
const CASE_ACTIVE_STATUSES = ["applying", "matching", "assigned", "caring"];
const STATUS_CHIPS = [
  { key: "active",   label: "진행" },
  { key: "assigned", label: "배정" },
  { key: "caring",   label: "돌봄중" },
];
const PRIORITY_CHIPS = [
  { key: "all",      label: "전체" },
  { key: "critical", label: "높음" },
  { key: "warning",  label: "보통" },
  { key: "normal",   label: "낮음" },
];

// 작은 가로 스크롤 칩 행. 현재 선택은 오렌지 강조, 비선택은 회색. 모바일에서
// 한 손 터치로 빠르게 바꾸는 데 select 보다 빠르다. 칩 자체는 stateless 라
// 부모가 value/onChange 를 관리한다.
function ChipRow({ value, onChange, chips, ariaLabel, style }) {
  return (
    <div role="radiogroup" aria-label={ariaLabel}
         style={{display:'flex', gap:6, overflowX:'auto', paddingBottom:2, scrollbarWidth:'none', ...style}}>
      {chips.map(c => {
        const active = value === c.key;
        return (
          <button key={c.key} type="button" role="radio" aria-checked={active}
                  onClick={() => onChange(c.key)}
                  style={{flex:'0 0 auto', padding:'6px 12px', fontSize:11.5, fontWeight: active ? 700 : 600,
                          borderRadius:99, border: active ? '1px solid var(--orange-500)' : '1px solid var(--border-soft)',
                          background: active ? 'var(--orange-50)'  : 'var(--surface)',
                          color:      active ? 'var(--orange-700)' : 'var(--ink-2)',
                          cursor:'pointer', whiteSpace:'nowrap'}}>
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

function MgrCaseList() {
  const [cases, setCases] = React.useState(null);
  const [err, setErr]     = React.useState(null);
  // 데스크톱과 동일한 3축 필터: 이름 부분일치 + 상태 + 우선순위.
  // 기본값은 운영 관심사("진행 중 케이스 전 우선순위") — 진입 직후 closed는
  // 자동 숨김이라 이전 showClosed=false 동작과 호환된다.
  const [q, setQ] = React.useState("");
  const [statusFilter, setStatusFilter]     = React.useState("active");
  const [priorityFilter, setPriorityFilter] = React.useState("all");
  const hasFilter = q.trim() !== "" || statusFilter !== "active" || priorityFilter !== "all";

  const load = React.useCallback(async () => {
    try {
      const body = await mobileFetch("/api/v1/cases");
      // 종결(closed) 케이스는 '내 케이스'에서 완전히 제외 — 매니저는 진행 중 케이스만
      // 본다. 종결 이력은 어르신 상세/방문보고서에서 확인 가능하므로 여기선 숨긴다.
      // (KPI 카운트·상태 칩도 이 걸러진 목록을 기준으로 계산된다.)
      const arr = Array.isArray(body) ? body : [];
      setCases(arr.filter(c => c.status !== "closed"));
      setErr(null);
    } catch (e) {
      setErr(humanMobileError(e));
    }
  }, []);

  React.useEffect(() => {
    load();
    // 케이스 상세에서 상태/메모를 바꾸고 뒤로가기로 돌아오면 bfcache 복원이라
    // 컴포넌트가 재마운트되지 않아 카드 배지가 옛 값으로 멈춘다(useUnreadCount와 동일
    // 이슈). pageshow(bfcache 복원 포함)·탭 재포커스(visibilitychange)에 재요청해
    // 목록을 항상 최신 상태로 맞춘다.
    const onShow = () => load();
    const onVis  = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("pageshow", onShow);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("pageshow", onShow);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [load]);

  // 필터 적용 후 정렬: 우선순위 critical 먼저 → 진행 단계상 활성 우선 → 최근 생성 순.
  // 필터/정렬을 한 memo로 묶어 cases 변화 시 두 번 도는 일이 없게 한다.
  const sorted = React.useMemo(() => {
    if (!cases) return null;
    const needle = q.trim();
    const filtered = cases.filter(c => {
      if (statusFilter === "active" && !CASE_ACTIVE_STATUSES.includes(c.status)) return false;
      if (statusFilter !== "active" && statusFilter !== "all" && c.status !== statusFilter) return false;
      if (priorityFilter !== "all" && c.priority !== priorityFilter) return false;
      if (needle && !(c.elderly_name || "").includes(needle)) return false;
      return true;
    });
    const rank = (c) => {
      const p = c.priority === "critical" ? 0 : c.priority === "warning" ? 1 : 2;
      const sActive = CASE_ACTIVE_STATUSES.indexOf(c.status);
      const s = sActive === -1 ? 9 : sActive;
      return [p, s, -new Date(c.created_at).getTime()];
    };
    return filtered.slice().sort((a, b) => {
      const ra = rank(a), rb = rank(b);
      for (let i = 0; i < ra.length; i++) if (ra[i] !== rb[i]) return ra[i] - rb[i];
      return 0;
    });
  }, [cases, q, statusFilter, priorityFilter]);

  const counts = React.useMemo(() => {
    if (!cases) return { total: 0, critical: 0, active: 0 };
    // cases 에는 이미 종결이 빠져 있으므로 total 자체가 진행 중 담당 수다.
    return {
      total:    cases.length,
      critical: cases.filter(c => c.priority === "critical").length,
      active:   cases.filter(c => c.status === "caring" || c.status === "assigned").length,
    };
  }, [cases]);

  const resetFilters = () => { setQ(""); setStatusFilter("active"); setPriorityFilter("all"); };

  // KPI 카드 → 빠른 필터 토글. 이미 같은 필터가 켜져 있으면 해제. 진입 즉시
  // 위험/돌봄 건만 볼 때 가장 빈번한 동작이라 별도 진입을 한 단계 줄인다.
  const togglePriority = (key) => setPriorityFilter(p => p === key ? "all" : key);
  const toggleStatus   = (key) => setStatusFilter(s => s === key ? "active" : key);

  // KPI 카드 → 빠른 필터 (담당=전체 리셋, 높음=priority critical, 돌봄중=status caring)
  // 같은 키를 다시 누르면 토글 해제로 기본값 복귀. active 표시는 색상 강조.
  const kpiCards = [
    { l:"담당",   v: counts.total,    c:"orange", active: !hasFilter,                       onClick: resetFilters },
    { l:"높음",   v: counts.critical, c:"rose",   active: priorityFilter === "critical",    onClick: () => togglePriority("critical") },
    { l:"돌봄중", v: counts.active,   c:"green",  active: statusFilter === "caring",        onClick: () => toggleStatus("caring") },
  ];

  return (
    <div className="app" style={{background:'var(--bg)', overflow:'hidden'}}>
      <StatusBar/>
      <div style={{padding:'8px 22px 6px', display:'flex', alignItems:'center', gap:8}}>
        <a href="/m/home" aria-label="홈으로"
           style={{width:32, height:32, display:'grid', placeItems:'center', borderRadius:10, background:'var(--surface)', color:'var(--ink-1)', textDecoration:'none'}}>‹</a>
        <div style={{flex:1, fontSize:15, fontWeight:800}}>내 케이스</div>
        {hasFilter && (
          <button type="button" onClick={resetFilters}
                  style={{padding:'0 10px', height:30, border:'none', background:'var(--orange-50)', borderRadius:8, color:'var(--orange-700)', fontSize:11, fontWeight:700, cursor:'pointer'}}>
            초기화
          </button>
        )}
      </div>

      <div style={{padding:'4px 22px 0', display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:8}}>
        {kpiCards.map((s, i) => (
          <button key={i} type="button" onClick={s.onClick}
                  aria-pressed={s.active}
                  className="card" style={{padding:'10px 12px', textAlign:'left', border:'none', cursor:'pointer',
                    background: s.active ? `var(--${s.c}-50)` : 'var(--surface)',
                    boxShadow: s.active ? `inset 0 0 0 1px var(--${s.c}-500)` : undefined}}>
            <div className="muted" style={{fontSize:10, fontWeight:600}}>{s.l}</div>
            <div style={{fontSize:18, fontWeight:800, color: `var(--${s.c}-600)`, marginTop:2, lineHeight:1.1}}>{s.v}</div>
          </button>
        ))}
      </div>

      <div style={{padding:'12px 22px 0'}}>
        {/* 이름 검색 — 부분일치, 데스크톱과 동일 동작. 입력 자체가 필터라 별도 적용 버튼은 두지 않는다. */}
        <div style={{display:'flex', alignItems:'center', gap:8, padding:'8px 12px', background:'var(--surface)', borderRadius:10, border:'1px solid var(--border-soft)'}}>
          <span style={{display:'inline-flex', color:'var(--ink-3)'}}>{Ic.search}</span>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="어르신 이름 검색"
                 style={{flex:1, border:'none', outline:'none', background:'transparent', fontSize:13, color:'var(--ink-1)', minWidth:0, fontFamily:'inherit'}}/>
          {q && (
            <button type="button" onClick={() => setQ("")} aria-label="검색어 지우기"
                    style={{border:'none', background:'transparent', fontSize:14, color:'var(--ink-3)', cursor:'pointer', padding:0}}>×</button>
          )}
        </div>

        {/* 상태/우선순위 칩 — 가로 스크롤. 모바일 화면 폭이 좁아 select 보다 칩이 빠르다.
            두 줄 다 "전체"로 시작해 어느 축 필터인지 모호했으므로, 줄 앞에 고정 축 라벨
            (상태/우선순위)을 둬 무엇을 거르는 필터인지 한눈에 알게 한다(라벨은 스크롤 고정). */}
        <div style={{display:'flex', alignItems:'center', gap:8, marginTop:8}}>
          <span style={{flex:'0 0 auto', width:50, fontSize:10.5, fontWeight:700, color:'var(--ink-3)', whiteSpace:'nowrap'}}>상태</span>
          <ChipRow value={statusFilter}   onChange={setStatusFilter}   chips={STATUS_CHIPS}   ariaLabel="상태 필터"   style={{flex:1, minWidth:0}}/>
        </div>
        <div style={{display:'flex', alignItems:'center', gap:8, marginTop:6}}>
          <span style={{flex:'0 0 auto', width:50, fontSize:10.5, fontWeight:700, color:'var(--ink-3)', whiteSpace:'nowrap'}}>우선순위</span>
          <ChipRow value={priorityFilter} onChange={setPriorityFilter} chips={PRIORITY_CHIPS} ariaLabel="우선순위 필터" style={{flex:1, minWidth:0}}/>
        </div>
      </div>

      {err && (
        <div role="alert" style={{margin:'10px 22px 0', padding:'10px 12px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>{err}</div>
      )}

      <div style={{padding:'12px 22px 0', flex:1, overflow:'auto'}}>
        {sorted && (
          <div className="muted" style={{fontSize:10.5, marginBottom:8, textAlign:'right'}}>{sorted.length}건 표시</div>
        )}
        {sorted === null && !err && <div className="muted" style={{padding:'30px 0', textAlign:'center', fontSize:12}}>불러오는 중…</div>}
        {sorted && sorted.length === 0 && (
          <div className="muted" style={{padding:'30px 0', textAlign:'center', fontSize:12}}>
            {(cases || []).length === 0
              ? "담당 케이스가 없습니다."
              : hasFilter ? "조건에 맞는 케이스가 없습니다." : "진행 중인 케이스가 없습니다."}
          </div>
        )}
        {sorted && sorted.map((c) => {
          // 가장 심각한 알림(critical 우선)을 카드 아래 한 줄로 노출 — 매니저가
          // 어르신 단위로 한눈에 위험 신호를 보게 한다. 카드 자체의 좌측 컬러바는
          // priority 기준 유지하되, 알림이 있으면 이름 옆 점으로 추가 표시.
          const alerts = Array.isArray(c.alerts) ? c.alerts : [];
          const topAlert = alerts.find(a => a.severity === "critical") || alerts[0];
          const dotColor = topAlert?.severity === "critical" ? "rose-500" : "amber-500";
          // 카드 내 전화/메시지는 카드 클릭(상세 이동)과 충돌하지 않게 stopPropagation.
          // 전화 대상: 어르신 본인 번호 우선, 없으면 보호자 번호. 둘 다 없으면 비활성.
          const callPhone = c.elderly_phone || c.guardian_phone;
          const callIsElder = !!c.elderly_phone;
          const goDetail = () => { window.location.href = `/m/case?id=${c.elderly_id}`; };
          return (
          <div key={c.id} role="link" tabIndex={0} onClick={goDetail}
             onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); goDetail(); } }}
             className="card"
             style={{display:'flex', flexDirection:'column', gap:6, padding:'12px 14px', marginBottom:8, textDecoration:'none', color:'inherit', cursor:'pointer', borderLeft:`3px solid var(--${PRIORITY_COLOR[c.priority] === 'gray' ? 'ink-4' : PRIORITY_COLOR[c.priority] + '-500'})`}}>
            <div style={{display:'flex', alignItems:'center', gap:12}}>
              <div className="avatar" style={{width:38, height:38, fontSize:14, position:'relative'}}>
                {(c.elderly_name || '?')[0]}
                {topAlert && (
                  <span aria-label={`알림 ${alerts.length}건`}
                        style={{position:'absolute', top:-2, right:-2, width:10, height:10, borderRadius:99, background:`var(--${dotColor})`, border:'2px solid var(--surface)'}}/>
                )}
              </div>
              <div style={{flex:1, minWidth:0}}>
                <div style={{display:'flex', alignItems:'center', gap:6}}>
                  <div style={{fontSize:13.5, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                    {c.elderly_name || '미상'} <span style={{fontWeight:500, color:'var(--ink-3)', fontSize:11}}>· {c.elderly_age}세</span>
                  </div>
                </div>
                <div className="muted" style={{fontSize:11, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginTop:1}}>{c.elderly_address}</div>
              </div>
              <div style={{display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4}}>
                <span className={"badge badge-" + CASE_STATUS_COLOR[c.status]} style={{fontSize:9}}>{CASE_STATUS_LABEL[c.status] || c.status}</span>
                {/* 어르신 위험도 — 주의/위험만 노출(안정은 대다수라 숨겨 예외를 부각). 우선순위는
                    좌측 색상바(borderLeft=PRIORITY_COLOR)가 이미 인코딩하므로 별도 뱃지를 두지 않는다.
                    elderly_risk_level은 cases#index 응답, 상세 변경 후 pageshow 재요청으로 즉시 반영. */}
                {(c.elderly_risk_level === "warning" || c.elderly_risk_level === "critical") && (
                  <span className={"badge badge-" + (RISK_COLOR[c.elderly_risk_level] || 'gray')} style={{fontSize:9}}>
                    {RISK_LABEL[c.elderly_risk_level]}
                  </span>
                )}
              </div>
            </div>
            {topAlert && (
              <div style={{display:'flex', alignItems:'center', gap:6, padding:'6px 8px', borderRadius:8, background: topAlert.severity === "critical" ? 'var(--rose-50)' : 'var(--amber-50)', color: topAlert.severity === "critical" ? 'var(--rose-700)' : 'var(--amber-700)', fontSize:11, fontWeight:600}}>
                <span aria-hidden="true">●</span>
                <span style={{flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{topAlert.message}</span>
                {alerts.length > 1 && <span style={{fontWeight:700, fontSize:10}}>+{alerts.length - 1}</span>}
              </div>
            )}
            {/* 빠른 연락 — 전화(본인 우선·없으면 보호자)/메시지. 카드 탭(상세)과 분리. */}
            <div style={{display:'flex', justifyContent:'flex-end', gap:8, marginTop:2}}>
              {callPhone ? (
                <a href={`tel:${callPhone}`} onClick={(ev) => ev.stopPropagation()}
                   aria-label={`${callIsElder ? c.elderly_name + ' 본인' : '보호자'} 전화 ${callPhone}`}
                   title={callIsElder ? '어르신 본인 전화' : '보호자 전화'}
                   style={{display:'inline-flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:9, background: callIsElder ? 'var(--green-50)' : 'var(--orange-50)', color: callIsElder ? 'var(--green-700)' : 'var(--orange-700)', fontSize:11.5, fontWeight:700, textDecoration:'none'}}>
                  <span style={{width:14, height:14, display:'inline-block'}}>{Ic.phone}</span>
                  {callIsElder ? '본인' : '보호자'}
                </a>
              ) : (
                <span aria-disabled="true" title="등록된 전화번호 없음"
                      style={{display:'inline-flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:9, background:'var(--bg-sub)', color:'var(--ink-3)', fontSize:11.5, fontWeight:600}}>
                  <span style={{width:14, height:14, display:'inline-block'}}>{Ic.phone}</span>
                  번호 없음
                </span>
              )}
              <a href={`/m/chat?elderly_id=${c.elderly_id}`} onClick={(ev) => ev.stopPropagation()}
                 aria-label={`${c.elderly_name || '어르신'} 메시지`} title="메시지"
                 style={{display:'inline-flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:9, background:'var(--surface)', color:'var(--orange-600)', border:'1px solid var(--orange-300)', fontSize:11.5, fontWeight:700, textDecoration:'none'}}>
                <span style={{width:14, height:14, display:'inline-block'}}>{Ic.chat}</span>
                메시지
              </a>
            </div>
          </div>
          );
        })}
      </div>

      <TabBar active="cases"/>
      <HomeBar/>
    </div>
  );
}

// ────────────────────────────────────────────────────
// 5. 케이스(어르신) 상세
// 케이스 상세 탭 정의 — 한 화면에서 정보를 의미별로 나눈다. key는 상태 식별자,
// label은 탭바에 표시. 순서 = 사용 빈도(요약 → 복약 → 일정 → 기록 → 가족).
const CASE_TABS = [
  { key: "summary", label: "요약" },
  { key: "meds",    label: "복약" },
  { key: "visits",  label: "일정" },
  { key: "reports", label: "기록" },
  { key: "family",  label: "가족" },
];

// 활력징후 임계치 — 성인/노인 일반 참고치. 매니저는 의료진이 아니므로 "확정 진단"
// 색이 아닌 "주의가 필요한 값"의 시각 단서로만 쓴다. 임계치 변경은 운영팀과
// 합의 후 한 곳만 손대면 되도록 여기 모았다.
// bpSeverity/hrSeverity/tempSeverity/vitalColorVar 는 common.tsx 에서 import.
// 같은 임계치를 Ruby (VitalSeverity concern) 와도 공유한다.

// 활력 미니 추세 스파크라인 — 의존성 없이 SVG로 직접 그린다. points는 시간순
// (과거→현재) {y, sev} 배열. y=null 인 측정은 선이 끊긴 채로 표시되어 결측을
// 평균으로 가리지 않는다. 각 점은 임계치 색상을 그대로 따르므로 "최근에 위험
// 값이 몰렸는지"가 한눈에 보인다.
function Sparkline({ points, width = 78, height = 26 }) {
  const valid = (points || []).filter(p => p && p.y != null && Number.isFinite(p.y));
  if (valid.length === 0) {
    return <div className="muted" style={{fontSize:10, textAlign:'center', height, lineHeight:`${height}px`}}>—</div>;
  }
  if (valid.length === 1) {
    return (
      <svg width={width} height={height} aria-hidden="true">
        <circle cx={width / 2} cy={height / 2} r={2.4} fill={vitalColorVar(valid[0].sev)}/>
      </svg>
    );
  }
  const ys  = valid.map(p => p.y);
  const min = Math.min(...ys), max = Math.max(...ys);
  const range  = max - min || 1;
  const stepX  = width / (points.length - 1);
  const padY   = 3;
  const toY    = (y) => height - padY - ((y - min) / range) * (height - padY * 2);

  // 결측을 가로지르는 직선을 피하려면 연속 구간끼리만 잇는다. polyline 한 줄은
  // 결측을 평균치로 잇기 때문에 의도와 다른 신호를 줄 수 있다.
  const segments = [];
  let prev = null;
  points.forEach((p, i) => {
    if (!p || p.y == null) { prev = null; return; }
    const x = i * stepX, y = toY(p.y);
    if (prev) segments.push({ x1: prev.x, y1: prev.y, x2: x, y2: y });
    prev = { x, y };
  });

  return (
    <svg width={width} height={height} aria-hidden="true">
      {segments.map((s, i) => (
        <line key={i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke="var(--ink-3)" strokeWidth={1} opacity={0.6}/>
      ))}
      {points.map((p, i) => (p && p.y != null) ? (
        <circle key={i} cx={i * stepX} cy={toY(p.y)} r={1.8} fill={vitalColorVar(p.sev)}/>
      ) : null)}
    </svg>
  );
}

// recent_vitals (created_at DESC) → 지표별 시간순 시퀀스. CaseSummaryPane/
// CaseReportsPane 양쪽에서 같은 변환이 필요해 함수로 분리.
function buildVitalTrend(recentVitals) {
  const chrono = (recentVitals || []).slice().reverse();
  const num = (x) => {
    const n = Number(x);
    return Number.isFinite(n) ? n : null;
  };
  const sys = (bp) => {
    if (!bp) return null;
    const v = parseInt(String(bp).split('/')[0], 10);
    return Number.isFinite(v) ? v : null;
  };
  return {
    bp:   chrono.map(r => ({ y: sys(r.bp),  sev: bpSeverity(r.bp) })),
    hr:   chrono.map(r => ({ y: num(r.hr),  sev: hrSeverity(r.hr) })),
    temp: chrono.map(r => ({ y: num(r.temp), sev: tempSeverity(r.temp) })),
  };
}

// 측정 시각의 상대 표시. "오늘 14:32" / "어제 09:15" / "3일 전" / 그 이상은 날짜.
// "마지막 활력이 언제였는지"는 위험 판단에 직접적이라 항상 표시한다.
function formatMeasuredAt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const hhmm = d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false });
  const sameDay = d.toDateString() === now.toDateString();
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  if (sameDay) return `오늘 ${hhmm}`;
  if (d.toDateString() === yest.toDateString()) return `어제 ${hhmm}`;
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays < 7) return `${diffDays}일 전`;
  return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
}

// 이상징후 배너 — 백엔드 Cases::AlertDetector 가 만든 alerts 배열을 그대로 표시.
// 매니저가 케이스 상세를 펼치자마자 "왜 이 분이 빨간 점이었는지" 한눈에 보이게.
// alerts 가 비어 있으면 컴포넌트가 알아서 사라지므로 호출자는 조건 분기 없이 둔다.
function CaseAlertBanner({ alerts }) {
  const list = Array.isArray(alerts) ? alerts : [];
  if (list.length === 0) return null;
  const sorted = [...list].sort((a, b) => (a.severity === "critical" ? -1 : 1) - (b.severity === "critical" ? -1 : 1));
  return (
    <div role="region" aria-label="이상징후"
         style={{padding:'10px 12px', marginBottom:14, borderRadius:10, background:'var(--rose-50)', border:'1px solid var(--rose-100)'}}>
      <div style={{fontSize:11, fontWeight:800, color:'var(--rose-700)', letterSpacing:'0.06em', marginBottom:6}}>이상징후 {list.length}건</div>
      {sorted.map((a, i) => (
        <div key={i} style={{display:'flex', alignItems:'center', gap:8, padding:'3px 0'}}>
          <span aria-hidden="true" style={{color: a.severity === "critical" ? 'var(--rose-600)' : 'var(--amber-600)'}}>●</span>
          <div style={{fontSize:12.5, color:'var(--ink-1)', flex:1}}>{a.message}</div>
        </div>
      ))}
    </div>
  );
}

// 매니저가 직접 바꿀 수 있는 케이스 상태(중도 정책). 백엔드 CaseRecord::
// MANAGER_WRITABLE_STATUSES 와 정확히 같아야 PATCH가 통과한다.
const MGR_CASE_STATUSES = ["assigned", "caring", "closed"];
// 종결 사유 — 백엔드 CaseRecord::CLOSURE_REASONS 와 키가 동일. 라벨은 모바일
// 표시용. 둘이 어긋나면 모달 선택은 통과해도 모델 검증에서 422 가 떨어진다.
const CLOSURE_REASON_LABEL = {
  recovery:        "회복",
  hospitalization: "입원",
  deceased:        "사망",
  moved:           "이사",
  declined:        "거절",
  transferred:     "타기관 이관",
  other:           "기타",
};

// 응급SOS 긴급대응 배너 — 케이스 상태와 분리된 별도 트랙. 케어매니저가 즉시 대응해야 하는
// 미해결 SOS를 헤더 아래 고정 노출한다. fall(낙상)이면 자동연계 소방기관명을, 그 외 유형은
// "담당 매니저 직접 대응"을 안내한다(sos_controller의 연계 정책과 동일 카피).
function SosAlertBanner({ alert }) {
  if (!alert) return null;
  const typeLabel = SOS_TYPE_LABEL[alert.sos_type] || alert.sos_type || "응급";
  const when = alert.created_at
    ? new Date(alert.created_at).toLocaleString("ko-KR", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
    : null;
  const responder = alert.quick_matched && alert.organization_name
    ? `소방 자동연계: ${alert.organization_name}`
    : "담당 케어매니저 직접 대응";
  return (
    <div role="alert" style={{margin:'12px 22px 0', padding:'12px 14px', background:'var(--rose-50)', border:'1px solid var(--rose-300)', borderRadius:12}}>
      <div style={{display:'flex', alignItems:'center', gap:8}}>
        <span style={{fontSize:14}}>🚨</span>
        <span style={{fontSize:13, fontWeight:800, color:'var(--rose-700)'}}>긴급 SOS · {typeLabel}</span>
        <span className="badge badge-rose" style={{fontSize:9, marginLeft:'auto'}}>대응 필요</span>
      </div>
      <div style={{fontSize:11.5, color:'var(--rose-700)', marginTop:6, lineHeight:1.5}}>
        {responder}{when ? ` · 접수 ${when}` : ''}
      </div>
    </div>
  );
}

// 케이스 상태 카드. 현재 상태 뱃지 + (가능한 경우) "변경" 버튼. 종결된 경우
// 사유와 종결 시각도 함께 표시 — 나중에 매니저나 운영자가 보고 맥락 파악.
// caseId 가 없으면 카드 자체를 숨겨 빈 영역을 만들지 않는다.
function CaseStatusCard({ caseRecord, onSaveStatus }) {
  const [open, setOpen] = React.useState(false);
  if (!caseRecord) return null;
  const status = caseRecord.status;
  // assigned/caring/closed 외(applying/matching)는 운영자 흐름 — 매니저는 만지지 않게 변경 버튼을 숨긴다.
  const canChange = MGR_CASE_STATUSES.includes(status);
  return (
    <>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8}}>
        <div style={{fontSize:12, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em'}}>케이스 상태</div>
        {canChange && (
          <button type="button" onClick={() => setOpen(true)}
                  style={{fontSize:11, color:'var(--orange-600)', fontWeight:700, background:'transparent', border:'none', cursor:'pointer', padding:0}}>
            변경
          </button>
        )}
      </div>
      <div className="card" style={{padding:'12px 14px', marginBottom:14, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
        <span className={"badge badge-" + (CASE_STATUS_COLOR[status] || 'gray')} style={{fontSize:11}}>{CASE_STATUS_LABEL[status] || status}</span>
        {status === "closed" && (
          <div className="muted" style={{fontSize:11.5}}>
            사유: {CLOSURE_REASON_LABEL[caseRecord.closure_reason] || caseRecord.closure_reason || '미기재'}
            {caseRecord.closed_at && ` · 종결 ${formatMeasuredAt(caseRecord.closed_at) || ''}`}
          </div>
        )}
      </div>
      {open && (
        <CaseStatusSheet current={status} onClose={() => setOpen(false)} onSave={onSaveStatus}/>
      )}
    </>
  );
}

// 상태 변경 바텀 시트. 대상 상태 선택 → closed 인 경우 사유 picker 가 나타남.
// 같은 상태로 저장 시도는 disabled. 저장 성공 시 부모가 시트를 닫는 책임.
function CaseStatusSheet({ current, onClose, onSave }) {
  const [target, setTarget] = React.useState(current);
  const [reason, setReason] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [err, setErr]       = React.useState(null);

  const needsReason = target === "closed";
  const isChange    = target !== current;
  const canSave     = isChange && (!needsReason || reason);

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true); setErr(null);
    try {
      await onSave({ status: target, closure_reason: needsReason ? reason : null });
      onClose();
    } catch (e) {
      setErr(humanMobileError(e));
      setSaving(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="케이스 상태 변경"
         onClick={onClose}
         style={{position:'fixed', inset:0, background:'rgba(15,15,18,0.5)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:50}}>
      <div onClick={e => e.stopPropagation()}
           style={{width:'100%', maxWidth:480, background:'var(--surface)', borderTopLeftRadius:18, borderTopRightRadius:18, padding:'18px 22px 22px'}}>
        <div style={{width:36, height:4, background:'var(--ink-4)', borderRadius:99, margin:'0 auto 14px'}}/>
        <div style={{fontSize:15, fontWeight:800, marginBottom:14}}>케이스 상태 변경</div>

        <div style={{display:'flex', gap:6, marginBottom:14}}>
          {MGR_CASE_STATUSES.map(s => {
            const active = target === s;
            return (
              <button key={s} type="button" onClick={() => setTarget(s)} disabled={saving}
                      style={{flex:1, padding:'10px 6px', fontSize:12.5, fontWeight: active ? 800 : 600,
                              borderRadius:10, border: active ? '1px solid var(--orange-500)' : '1px solid var(--border-soft)',
                              background: active ? 'var(--orange-50)' : 'var(--surface)',
                              color:      active ? 'var(--orange-700)' : 'var(--ink-2)',
                              cursor: saving ? 'not-allowed' : 'pointer'}}>
                {CASE_STATUS_LABEL[s]}
              </button>
            );
          })}
        </div>

        {needsReason && (
          <div style={{marginBottom:14}}>
            <div className="muted" style={{fontSize:11.5, fontWeight:700, marginBottom:6}}>종결 사유</div>
            <select value={reason} onChange={e => setReason(e.target.value)} disabled={saving}
                    style={{width:'100%', padding:'10px 12px', fontSize:13, borderRadius:10, border:'1px solid var(--border-soft)', background:'var(--bg)', color:'var(--ink-1)', fontFamily:'inherit'}}>
              <option value="">사유를 선택하세요</option>
              {Object.entries(CLOSURE_REASON_LABEL).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>
          </div>
        )}

        {err && (
          <div role="alert" style={{marginBottom:10, padding:'8px 10px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:8, fontSize:11.5}}>{err}</div>
        )}

        <div style={{display:'flex', gap:8}}>
          <button type="button" onClick={onClose} disabled={saving}
                  style={{flex:1, padding:'12px 0', fontSize:13, fontWeight:700, background:'var(--surface)', border:'1px solid var(--border-soft)', borderRadius:10, color:'var(--ink-2)', cursor: saving ? 'not-allowed' : 'pointer'}}>
            취소
          </button>
          <button type="button" onClick={handleSave} disabled={!canSave || saving}
                  style={{flex:2, padding:'12px 0', fontSize:13, fontWeight:800, color:'#fff', border:'none', borderRadius:10,
                          background: (!canSave || saving) ? 'var(--ink-3)' : 'var(--orange-500)',
                          cursor: !canSave ? 'not-allowed' : (saving ? 'wait' : 'pointer')}}>
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

// 어르신 위험등급(위험/주의/안정) 카드. CaseStatusCard와 동일 골격 — 현재 등급
// 뱃지 + (담당 매니저인 경우) "변경" 버튼. canEdit(=백엔드 risk_editable)가 곧
// 본인 배정 케이스 보유 판정이라, 버튼은 PATCH가 통과할 때만 노출된다.
function RiskLevelCard({ elderly, canEdit, onSaveRisk }) {
  const [open, setOpen] = React.useState(false);
  if (!elderly) return null;
  const level = elderly.risk_level;
  return (
    <>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8}}>
        <div style={{fontSize:12, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em'}}>위험등급</div>
        {canEdit && (
          <button type="button" onClick={() => setOpen(true)}
                  style={{fontSize:11, color:'var(--orange-600)', fontWeight:700, background:'transparent', border:'none', cursor:'pointer', padding:0}}>
            변경
          </button>
        )}
      </div>
      <div className="card" style={{padding:'12px 14px', marginBottom:14, display:'flex', alignItems:'center', gap:10}}>
        <span className={"badge badge-" + (RISK_COLOR[level] || 'gray')} style={{fontSize:11}}>{RISK_LABEL[level] || level || '-'}</span>
      </div>
      {open && (
        <RiskLevelSheet current={level} onClose={() => setOpen(false)} onSave={onSaveRisk}/>
      )}
    </>
  );
}

// 위험등급 변경 바텀 시트. CaseStatusSheet와 동일 패턴 — 등급 선택 + 사유 필수.
// 같은 등급으로의 저장은 disabled. 저장 성공 시 부모가 시트를 닫는다.
function RiskLevelSheet({ current, onClose, onSave }) {
  const [target, setTarget] = React.useState(current);
  const [reason, setReason] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [err, setErr]       = React.useState(null);

  const isChange = target !== current;
  const canSave  = isChange && reason.trim().length > 0;

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true); setErr(null);
    try {
      await onSave({ risk_level: target, reason: reason.trim() });
      onClose();
    } catch (e) {
      setErr(humanMobileError(e));
      setSaving(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="위험등급 변경"
         onClick={onClose}
         style={{position:'fixed', inset:0, background:'rgba(15,15,18,0.5)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:50}}>
      <div onClick={e => e.stopPropagation()}
           style={{width:'100%', maxWidth:480, background:'var(--surface)', borderTopLeftRadius:18, borderTopRightRadius:18, padding:'18px 22px 22px'}}>
        <div style={{width:36, height:4, background:'var(--ink-4)', borderRadius:99, margin:'0 auto 14px'}}/>
        <div style={{fontSize:15, fontWeight:800, marginBottom:14}}>위험등급 변경</div>

        <div style={{display:'flex', gap:6, marginBottom:14}}>
          {["critical","warning","stable"].map(s => {
            const active = target === s;
            return (
              <button key={s} type="button" onClick={() => setTarget(s)} disabled={saving}
                      style={{flex:1, padding:'10px 6px', fontSize:12.5, fontWeight: active ? 800 : 600,
                              borderRadius:10, border: active ? '1px solid var(--orange-500)' : '1px solid var(--border-soft)',
                              background: active ? 'var(--orange-50)' : 'var(--surface)',
                              color:      active ? 'var(--orange-700)' : 'var(--ink-2)',
                              cursor: saving ? 'not-allowed' : 'pointer'}}>
                {RISK_LABEL[s]}
              </button>
            );
          })}
        </div>

        <div style={{marginBottom:14}}>
          <div className="muted" style={{fontSize:11.5, fontWeight:700, marginBottom:6}}>변경 사유 (필수)</div>
          <textarea value={reason} onChange={e => setReason(e.target.value)} disabled={saving} rows={3}
                    placeholder="예: 최근 방문 시 낙상 위험 증가 확인"
                    style={{width:'100%', padding:'10px 12px', fontSize:13, borderRadius:10, border:'1px solid var(--border-soft)', background:'var(--bg)', color:'var(--ink-1)', fontFamily:'inherit', resize:'vertical'}}/>
        </div>

        {err && (
          <div role="alert" style={{marginBottom:10, padding:'8px 10px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:8, fontSize:11.5}}>{err}</div>
        )}

        <div style={{display:'flex', gap:8}}>
          <button type="button" onClick={onClose} disabled={saving}
                  style={{flex:1, padding:'12px 0', fontSize:13, fontWeight:700, background:'var(--surface)', border:'1px solid var(--border-soft)', borderRadius:10, color:'var(--ink-2)', cursor: saving ? 'not-allowed' : 'pointer'}}>
            취소
          </button>
          <button type="button" onClick={handleSave} disabled={!canSave || saving}
                  style={{flex:2, padding:'12px 0', fontSize:13, fontWeight:800, color:'#fff', border:'none', borderRadius:10,
                          background: (!canSave || saving) ? 'var(--ink-3)' : 'var(--orange-500)',
                          cursor: !canSave ? 'not-allowed' : (saving ? 'wait' : 'pointer')}}>
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

// 어르신 건강상태 카드. RiskLevelCard와 동일 골격 — 현재 내용 + (담당 매니저인 경우)
// "변경" 버튼. canEdit(=백엔드 risk_editable, 위험도와 같은 권한)이 곧 본인 배정
// 케이스 보유 판정이라, 버튼은 PATCH가 통과할 때만 노출된다. 편집 불가 + 내용 없음
// 이면 카드 자체를 숨겨 기존 동작(비어 있으면 미표시)을 유지한다.
function HealthStatusCard({ elderly, canEdit, onSaveHealth }) {
  const [open, setOpen] = React.useState(false);
  if (!elderly) return null;
  const health = elderly.health_status;
  if (!canEdit && !health) return null;
  return (
    <>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8}}>
        <div style={{fontSize:12, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em'}}>건강 상태</div>
        {canEdit && (
          <button type="button" onClick={() => setOpen(true)}
                  style={{fontSize:11, color:'var(--orange-600)', fontWeight:700, background:'transparent', border:'none', cursor:'pointer', padding:0}}>
            변경
          </button>
        )}
      </div>
      <div className="card" style={{padding:'12px 14px', marginBottom:14, fontSize:12.5, lineHeight:1.55, whiteSpace:'pre-wrap'}}>
        {health || <span className="muted">건강상태 정보 없음</span>}
      </div>
      {open && (
        <HealthStatusSheet current={health || ""} onClose={() => setOpen(false)} onSave={onSaveHealth}/>
      )}
    </>
  );
}

// 건강상태 변경 바텀 시트. RiskLevelSheet와 동일 패턴 — 자유서술 입력 + 사유 필수.
// 내용 무변경이면 disabled. 저장 성공 시 부모가 시트를 닫는다.
function HealthStatusSheet({ current, onClose, onSave }) {
  const [draft, setDraft]   = React.useState(current);
  const [reason, setReason] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [err, setErr]       = React.useState(null);

  const isChange = draft.trim() !== (current || "").trim();
  const canSave  = isChange && reason.trim().length > 0;

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true); setErr(null);
    try {
      await onSave({ health_status: draft.trim(), reason: reason.trim() });
      onClose();
    } catch (e) {
      setErr(humanMobileError(e));
      setSaving(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="건강상태 변경"
         onClick={onClose}
         style={{position:'fixed', inset:0, background:'rgba(15,15,18,0.5)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:50}}>
      <div onClick={e => e.stopPropagation()}
           style={{width:'100%', maxWidth:480, background:'var(--surface)', borderTopLeftRadius:18, borderTopRightRadius:18, padding:'18px 22px 22px'}}>
        <div style={{width:36, height:4, background:'var(--ink-4)', borderRadius:99, margin:'0 auto 14px'}}/>
        <div style={{fontSize:15, fontWeight:800, marginBottom:14}}>건강상태 변경</div>

        <div style={{marginBottom:14}}>
          <div className="muted" style={{fontSize:11.5, fontWeight:700, marginBottom:6}}>건강 상태</div>
          <textarea value={draft} onChange={e => setDraft(e.target.value)} disabled={saving} rows={3}
                    placeholder="예: 고혈압·당뇨, 거동 불편"
                    style={{width:'100%', padding:'10px 12px', fontSize:13, borderRadius:10, border:'1px solid var(--border-soft)', background:'var(--bg)', color:'var(--ink-1)', fontFamily:'inherit', resize:'vertical'}}/>
        </div>

        <div style={{marginBottom:14}}>
          <div className="muted" style={{fontSize:11.5, fontWeight:700, marginBottom:6}}>변경 사유 (필수)</div>
          <textarea value={reason} onChange={e => setReason(e.target.value)} disabled={saving} rows={3}
                    placeholder="예: 최근 방문 시 보행 보조 필요 확인"
                    style={{width:'100%', padding:'10px 12px', fontSize:13, borderRadius:10, border:'1px solid var(--border-soft)', background:'var(--bg)', color:'var(--ink-1)', fontFamily:'inherit', resize:'vertical'}}/>
        </div>

        {err && (
          <div role="alert" style={{marginBottom:10, padding:'8px 10px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:8, fontSize:11.5}}>{err}</div>
        )}

        <div style={{display:'flex', gap:8}}>
          <button type="button" onClick={onClose} disabled={saving}
                  style={{flex:1, padding:'12px 0', fontSize:13, fontWeight:700, background:'var(--surface)', border:'1px solid var(--border-soft)', borderRadius:10, color:'var(--ink-2)', cursor: saving ? 'not-allowed' : 'pointer'}}>
            취소
          </button>
          <button type="button" onClick={handleSave} disabled={!canSave || saving}
                  style={{flex:2, padding:'12px 0', fontSize:13, fontWeight:800, color:'#fff', border:'none', borderRadius:10,
                          background: (!canSave || saving) ? 'var(--ink-3)' : 'var(--orange-500)',
                          cursor: !canSave ? 'not-allowed' : (saving ? 'wait' : 'pointer')}}>
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

// 매니저의 케이스 메모 카드. 백엔드는 case.note 한 필드만 받는다 — 다른 필드를
// 같이 PATCH로 보내면 cases#can_write_this_case? 가 forbidden 반환.
// caseId 가 없으면(=활성 케이스가 없는 어르신) 편집 UI 자체를 띄우지 않는다.
function CaseNoteCard({ caseId, note, onSave }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft]     = React.useState(note || "");
  const [saving, setSaving]   = React.useState(false);
  const [err, setErr]         = React.useState(null);

  // 외부에서 note가 갱신되면(다시 fetch 등) draft도 동기화. 편집 중이면 사용자
  // 입력을 우선해야 하므로 editing=false 일 때만 동기화한다.
  React.useEffect(() => { if (!editing) setDraft(note || ""); }, [note, editing]);

  if (!caseId) return null;

  const dirty = draft !== (note || "");
  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e) {
      setErr(humanMobileError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8}}>
        <div style={{fontSize:12, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em'}}>매니저 메모</div>
        {!editing && (
          <button type="button" onClick={() => setEditing(true)}
                  style={{fontSize:11, color:'var(--orange-600)', fontWeight:700, background:'transparent', border:'none', cursor:'pointer', padding:0}}>
            {note ? '편집' : '추가'}
          </button>
        )}
      </div>
      <div className="card" style={{padding:'12px 14px', marginBottom:14}}>
        {!editing ? (
          <div style={{fontSize:12.5, lineHeight:1.55, whiteSpace:'pre-wrap', color: note ? 'var(--ink-1)' : 'var(--ink-3)'}}>
            {note || '아직 메모가 없습니다. 추가를 눌러 작성하세요.'}
          </div>
        ) : (
          <>
            <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={4} disabled={saving}
                      placeholder="관찰 사항, 가족 요청, 다음 방문 시 확인할 점 등"
                      style={{width:'100%', boxSizing:'border-box', resize:'vertical', fontSize:12.5, lineHeight:1.55, padding:8, border:'1px solid var(--border-soft)', borderRadius:8, background:'var(--bg)', color:'var(--ink-1)', fontFamily:'inherit'}}/>
            <div style={{display:'flex', gap:6, justifyContent:'flex-end', marginTop:8}}>
              <button type="button" onClick={() => { setDraft(note || ""); setEditing(false); setErr(null); }} disabled={saving}
                      style={{padding:'6px 12px', fontSize:11, fontWeight:600, background:'var(--surface)', border:'1px solid var(--border-soft)', borderRadius:8, color:'var(--ink-2)', cursor: saving ? 'not-allowed' : 'pointer'}}>
                취소
              </button>
              <button type="button" onClick={save} disabled={saving || !dirty}
                      style={{padding:'6px 12px', fontSize:11, fontWeight:700, background: (!dirty || saving) ? 'var(--ink-3)' : 'var(--orange-500)', color:'#fff', border:'none', borderRadius:8, cursor: saving ? 'wait' : (!dirty ? 'not-allowed' : 'pointer')}}>
                {saving ? '저장 중…' : '저장'}
              </button>
            </div>
            {err && <div role="alert" style={{marginTop:8, padding:'8px 10px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:8, fontSize:11.5}}>{err}</div>}
          </>
        )}
      </div>
    </>
  );
}

// 케이스 이관 카드. 본인 담당·미종결 케이스(transfer_eligible)면 "이관 신청" 버튼을,
// 진행 중(pending_transfer) 신청이 있으면 "승인 대기중" 상태를 보여준다. 둘 다 아니면
// 카드 자체를 숨겨 빈 영역을 만들지 않는다(운영자/조회 전용 진입에는 안 뜸).
function CaseTransferCard({ caseRecord, onReload }) {
  const [open, setOpen] = React.useState(false);
  if (!caseRecord) return null;
  const pending  = caseRecord.pending_transfer;
  const eligible = caseRecord.transfer_eligible;
  if (!pending && !eligible) return null;
  return (
    <>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8}}>
        <div style={{fontSize:12, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em'}}>케이스 이관</div>
        {eligible && !pending && (
          <button type="button" onClick={() => setOpen(true)}
                  style={{fontSize:11, color:'var(--orange-600)', fontWeight:700, background:'transparent', border:'none', cursor:'pointer', padding:0}}>
            이관 신청
          </button>
        )}
      </div>
      <div className="card" style={{padding:'12px 14px', marginBottom:14, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
        {pending ? (
          <>
            <span className="badge badge-amber" style={{fontSize:11}}>승인 대기중</span>
            <div className="muted" style={{fontSize:11.5}}>
              {pending.to_care_manager_name ? `→ ${pending.to_care_manager_name}님` : '대상 매니저'}에게 이관 신청됨
            </div>
          </>
        ) : (
          <div className="muted" style={{fontSize:11.5}}>다른 매니저에게 이 케이스를 이관할 수 있습니다. 운영자 승인 후 적용됩니다.</div>
        )}
      </div>
      {open && (
        <CaseTransferSheet caseId={caseRecord.id} onClose={() => setOpen(false)}
                           onSubmitted={async () => { setOpen(false); if (onReload) await onReload(); }}/>
      )}
    </>
  );
}

// 케이스 이관 바텀 시트. 진입 시 같은 권역 대상 매니저 후보를 불러와(select) 사유와
// 함께 신청한다. RiskLevelSheet와 동일 골격 — 대상+사유가 모두 채워져야 저장 가능.
// 신청 성공 시 onSubmitted(케이스 상세 재조회)로 pending 상태를 반영한다.
function CaseTransferSheet({ caseId, onClose, onSubmitted }) {
  const [managers, setManagers] = React.useState(null); // null=로딩중
  const [target, setTarget]     = React.useState("");
  const [reason, setReason]     = React.useState("");
  const [saving, setSaving]     = React.useState(false);
  const [err, setErr]           = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    caseTransfer.candidates(caseId)
      .then(res => { if (!cancelled) setManagers(res.managers || []); })
      .catch(e => { if (!cancelled) { setManagers([]); setErr(humanMobileError(e)); } });
    return () => { cancelled = true; };
  }, [caseId]);

  const canSave = !!target && reason.trim().length > 0;

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true); setErr(null);
    try {
      await caseTransfer.request(caseId, Number(target), reason.trim());
      await onSubmitted();
    } catch (e) {
      setErr(humanMobileError(e));
      setSaving(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="케이스 이관 신청"
         onClick={onClose}
         style={{position:'fixed', inset:0, background:'rgba(15,15,18,0.5)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:50}}>
      <div onClick={e => e.stopPropagation()}
           style={{width:'100%', maxWidth:480, background:'var(--surface)', borderTopLeftRadius:18, borderTopRightRadius:18, padding:'18px 22px 22px'}}>
        <div style={{width:36, height:4, background:'var(--ink-4)', borderRadius:99, margin:'0 auto 14px'}}/>
        <div style={{fontSize:15, fontWeight:800, marginBottom:4}}>케이스 이관 신청</div>
        <div className="muted" style={{fontSize:11.5, marginBottom:14}}>같은 권역 매니저에게만 이관할 수 있으며, 운영자 승인 후 담당이 변경됩니다.</div>

        <div style={{marginBottom:14}}>
          <div className="muted" style={{fontSize:11.5, fontWeight:700, marginBottom:6}}>대상 매니저 (필수)</div>
          <select value={target} onChange={e => setTarget(e.target.value)} disabled={saving || managers === null}
                  style={{width:'100%', padding:'10px 12px', fontSize:13, borderRadius:10, border:'1px solid var(--border-soft)', background:'var(--bg)', color:'var(--ink-1)', fontFamily:'inherit'}}>
            <option value="">{managers === null ? '불러오는 중…' : (managers.length ? '매니저를 선택하세요' : '이관 가능한 매니저가 없습니다')}</option>
            {(managers || []).map(m => (
              <option key={m.id} value={m.id}>{m.name}{m.organization_name ? ` · ${m.organization_name}` : ''}</option>
            ))}
          </select>
        </div>

        <div style={{marginBottom:14}}>
          <div className="muted" style={{fontSize:11.5, fontWeight:700, marginBottom:6}}>이관 사유 (필수)</div>
          <textarea value={reason} onChange={e => setReason(e.target.value)} disabled={saving} rows={3}
                    placeholder="예: 담당 권역 재배치로 인계 / 휴직 예정으로 이관 요청"
                    style={{width:'100%', padding:'10px 12px', fontSize:13, borderRadius:10, border:'1px solid var(--border-soft)', background:'var(--bg)', color:'var(--ink-1)', fontFamily:'inherit', resize:'vertical'}}/>
        </div>

        {err && (
          <div role="alert" style={{marginBottom:10, padding:'8px 10px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:8, fontSize:11.5}}>{err}</div>
        )}

        <div style={{display:'flex', gap:8}}>
          <button type="button" onClick={onClose} disabled={saving}
                  style={{flex:1, padding:'12px 0', fontSize:13, fontWeight:700, background:'var(--surface)', border:'1px solid var(--border-soft)', borderRadius:10, color:'var(--ink-2)', cursor: saving ? 'not-allowed' : 'pointer'}}>
            취소
          </button>
          <button type="button" onClick={handleSave} disabled={!canSave || saving}
                  style={{flex:2, padding:'12px 0', fontSize:13, fontWeight:800, color:'#fff', border:'none', borderRadius:10,
                          background: (!canSave || saving) ? 'var(--ink-3)' : 'var(--orange-500)',
                          cursor: !canSave ? 'not-allowed' : (saving ? 'wait' : 'pointer')}}>
            {saving ? '신청 중…' : '이관 신청'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MgrCase() {
  const [data, setData] = React.useState(null);
  const [err, setErr]   = React.useState(null);
  const [activeTab, setActiveTab] = React.useState("summary");

  React.useEffect(() => {
    // ?id= 또는 ?elderly_id= 모두 허용 — 외부에서 진입할 수 있는 표기를 둘 다 받는다.
    const qs = new URLSearchParams(window.location.search);
    const id = qs.get("id") || qs.get("elderly_id");
    const url = id ? `/api/v1/mobile/case/${encodeURIComponent(id)}` : "/api/v1/mobile/case";
    let cancelled = false;
    (async () => {
      try {
        const body = await mobileFetch(url);
        if (!cancelled) setData(body);
      } catch (e) {
        if (!cancelled) setErr(humanMobileError(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 복약 변경(추가·수정·중단·재개·삭제) 후 케이스 상세를 재조회해 medications 를 최신화.
  // case_detail 한 번이면 카운트·중단 구분까지 일관되게 갱신되어 가장 단순·정확.
  const reload = React.useCallback(async () => {
    const qs = new URLSearchParams(window.location.search);
    const id = qs.get("id") || qs.get("elderly_id");
    const url = id ? `/api/v1/mobile/case/${encodeURIComponent(id)}` : "/api/v1/mobile/case";
    const body = await mobileFetch(url);
    setData(body);
  }, []);

  // 케이스 메모 저장. 성공하면 서버가 돌려준 case 직렬화로 로컬 상태를 덮어
  // "저장 직후 다른 탭으로 갔다 돌아와도 최신 값"이 보장된다. 실패는 throw해서
  // CaseNoteCard 가 에러 배지를 직접 띄우게 한다.
  const saveNote = React.useCallback(async (next) => {
    const caseId = data?.case?.id;
    if (!caseId) throw new Error("케이스가 없습니다.");
    const updated = await mobileFetch(`/api/v1/cases/${caseId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ case: { note: next } }),
    });
    setData(d => d && { ...d, case: { ...d.case, note: updated.note } });
  }, [data?.case?.id]);

  // 케이스 상태 저장. 종결 시 closure_reason 동봉, 다른 상태로 갈 때는 보내지
  // 않아 서버가 closed_at·closure_reason 을 자동으로 비우게 한다.
  // 응답 case 직렬화에는 status·closure_reason·closed_at 가 포함되므로 한 번에 덮어쓴다.
  const saveStatus = React.useCallback(async ({ status, closure_reason }) => {
    const caseId = data?.case?.id;
    if (!caseId) throw new Error("케이스가 없습니다.");
    const body = { case: { status } };
    if (closure_reason) body.case.closure_reason = closure_reason;
    const updated = await mobileFetch(`/api/v1/cases/${caseId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    setData(d => d && {
      ...d,
      case: { ...d.case, status: updated.status, closure_reason: updated.closure_reason, closed_at: updated.closed_at },
    });
  }, [data?.case?.id]);

  // 위험등급 저장. elderlies#update(위험도 전용)로 PATCH — 사유 필수. 응답은
  // payload(@elderly) 형태라 elderly.risk_level 로 헤더 뱃지까지 한 번에 갱신.
  const saveRisk = React.useCallback(async ({ risk_level, reason }) => {
    const elderlyId = data?.elderly?.id;
    if (!elderlyId) throw new Error("어르신 정보가 없습니다.");
    const updated = await mobileFetch(`/api/v1/elderlies/${elderlyId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ elderly: { risk_level }, reason }),
    });
    setData(d => d && { ...d, elderly: { ...d.elderly, risk_level: updated.elderly?.risk_level ?? risk_level } });
  }, [data?.elderly?.id]);

  // 건강상태 저장. 위험도와 같은 elderlies#update(health_status 분기)로 PATCH — 사유 필수.
  const saveHealth = React.useCallback(async ({ health_status, reason }) => {
    const elderlyId = data?.elderly?.id;
    if (!elderlyId) throw new Error("어르신 정보가 없습니다.");
    const updated = await mobileFetch(`/api/v1/elderlies/${elderlyId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ elderly: { health_status }, reason }),
    });
    setData(d => d && { ...d, elderly: { ...d.elderly, health_status: updated.elderly?.health_status ?? health_status } });
  }, [data?.elderly?.id]);

  if (err) {
    return (
      <div className="app" style={{background:'var(--bg)', overflow:'hidden'}}>
        <StatusBar/>
        <div style={{padding:'8px 22px 12px', display:'flex', alignItems:'center', gap:14}}>
          <a onClick={() => history.back()} style={{width:32, height:32, display:'grid', placeItems:'center', borderRadius:10, background:'var(--surface)', color:'var(--ink-1)', cursor:'pointer', textDecoration:'none'}}>‹</a>
          <div style={{fontSize:14, fontWeight:700, flex:1}}>케이스 상세</div>
        </div>
        <div role="alert" style={{margin:'12px 22px', padding:'12px 14px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>{err}</div>
        <div style={{flex:1}}/>
        <TabBar active="cases"/>
        <HomeBar/>
      </div>
    );
  }
  if (!data) return <div className="app muted" style={{padding:40}}>불러오는 중…</div>;

  const e = data.elderly;
  const v = data.recent_vitals[0]; // latest vitals
  const bp = v?.bp ? v.bp.split('/') : null;

  return (
    <div className="app" style={{background:'var(--bg)', overflow:'hidden'}}>
      <StatusBar/>
      <div style={{padding:'8px 22px 12px', display:'flex', alignItems:'center', gap:14}}>
        <a onClick={() => history.back()} style={{width:32, height:32, display:'grid', placeItems:'center', borderRadius:10, background:'var(--surface)', color:'var(--ink-1)', cursor:'pointer', textDecoration:'none'}}>‹</a>
        <div style={{fontSize:14, fontWeight:700, flex:1}}>케이스 상세</div>
      </div>

      <div style={{padding:'8px 22px 0', display:'flex', alignItems:'center', gap:14}}>
        <div className="avatar lg" style={{width:56, height:56, fontSize:22}}>{e.name[0]}</div>
        <div style={{flex:1}}>
          <div style={{fontSize:20, fontWeight:800}}>{e.name} <span style={{fontWeight:500, color:'var(--ink-3)', fontSize:13}}>· {e.age}세</span></div>
          <div className="muted" style={{fontSize:11.5, marginTop:2}}>{e.address}</div>
          <div style={{display:'flex', gap:5, marginTop:6}}>
            <span className={"badge badge-" + RISK_COLOR[e.risk_level]}>{RISK_LABEL[e.risk_level]}</span>
            <span className="badge badge-gray">{e.household_type}</span>
          </div>
        </div>
      </div>

      {/* 응급SOS 긴급대응 배너 — 케이스 상태(돌봄중)와 분리된 별도 트랙. 탭 무관하게 항상 노출해
          담당 매니저가 즉시 인지하도록 헤더 바로 아래에 둔다. */}
      {data.sos_alert && <SosAlertBanner alert={data.sos_alert}/>}

      <div style={{padding:'14px 22px 0', display:'flex', gap:0, borderBottom:'1px solid var(--border-soft)', overflowX:'auto'}}>
        {CASE_TABS.map((t) => {
          const active = activeTab === t.key;
          return (
            <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
              style={{padding:'10px 14px', fontSize:13, fontWeight: active ? 700 : 500,
                      color: active ? 'var(--orange-700)' : 'var(--ink-3)',
                      borderBottom: active ? '2px solid var(--orange-500)' : '2px solid transparent',
                      background:'transparent', border:'none', borderRadius:0, cursor:'pointer', whiteSpace:'nowrap'}}>
              {t.label}
            </button>
          );
        })}
      </div>

      <div style={{padding:'14px 22px 0', flex:1, overflow:'auto'}}>
        {activeTab === "summary" && (
          <CaseSummaryPane elderly={e} latestVital={v} weeklyVisitCount={data.this_week_visit_count ?? 0}
                           caseRecord={data.case} onSaveNote={saveNote} onSaveStatus={saveStatus}
                           riskEditable={!!data.risk_editable} onSaveRisk={saveRisk} onSaveHealth={saveHealth}
                           recentVitals={data.recent_vitals} onReload={reload}/>
        )}
        {activeTab === "meds" && (
          <CaseMedsPane medications={e.medications} elderlyId={e.id}
                        canEdit={!!data.meds_editable} onChange={reload}/>
        )}
        {activeTab === "visits" && (
          <CaseVisitsPane upcoming={data.upcoming_visits} past={data.past_visits}/>
        )}
        {activeTab === "reports" && (
          <CaseReportsPane reports={data.recent_vitals}/>
        )}
        {activeTab === "family" && (
          <CaseFamilyPane elderly={e}/>
        )}
      </div>

      <div style={{padding:'12px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', gap:8, background:'var(--surface)'}}>
        {e.guardian_phone ? (
          <a href={`tel:${e.guardian_phone}`} aria-label={`보호자 ${e.guardian_name || ''} 전화`}
             className="btn btn-ghost btn-lg" style={{textDecoration:'none', display:'inline-flex', alignItems:'center', justifyContent:'center'}}>
            {Ic.phone}
          </a>
        ) : (
          <button type="button" disabled title="보호자 전화번호 미등록" aria-label="보호자 전화번호 미등록"
                  className="btn btn-ghost btn-lg" style={{opacity:0.5, cursor:'not-allowed'}}>
            {Ic.phone}
          </button>
        )}
        {/* 방문 시작은 일정 탭에 이미 체크인이 있으므로 중복 핸들러를 두지 않고 진입점만 제공한다. */}
        <a href="/m/schedule" className="btn btn-primary btn-lg"
           style={{flex:1, textDecoration:'none', display:'inline-flex', alignItems:'center', justifyContent:'center'}}>
          일정에서 시작 →
        </a>
      </div>
      <TabBar active="cases"/>
      <HomeBar/>
    </div>
  );
}

// MgrCase 탭별 콘텐츠. 각 Pane은 데이터를 표시만 하므로 상태가 없는 함수형으로
// 분리해 두면 MgrCase 본문이 가볍게 유지된다. 액션이 생기면 부모로 콜백을 올린다.
function CaseSummaryPane({ elderly, latestVital, weeklyVisitCount, caseRecord, onSaveNote, onSaveStatus, riskEditable, onSaveRisk, onSaveHealth, recentVitals, onReload }) {
  const e = elderly;
  const v = latestVital;
  const measuredAt = formatMeasuredAt(v?.at);
  const trend = React.useMemo(() => buildVitalTrend(recentVitals), [recentVitals]);
  // 점이 1개 이하면 추세를 그릴 게 없어 행 자체를 숨긴다 — 빈 영역으로 카드를
  // 키우는 것보다 깨끗.
  const trendCount = (recentVitals || []).filter(r => r && (r.bp || r.hr != null || r.temp != null)).length;
  const showTrend = trendCount >= 2;
  const items = [
    { l:"혈압", val: v?.bp || '미측정', u:"mmHg", sev: bpSeverity(v?.bp),   points: trend.bp },
    { l:"맥박", val: v?.hr ?? '-',     u:"bpm",  sev: hrSeverity(v?.hr),    points: trend.hr },
    { l:"체온", val: v?.temp ?? '-',   u:"°C",   sev: tempSeverity(v?.temp), points: trend.temp },
  ];
  return (
    <>
      <CaseAlertBanner alerts={caseRecord?.alerts}/>
      <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom: measuredAt ? 6 : 14}}>
        {items.map((s, i) => (
          <div key={i} className="card" style={{padding:'10px 6px 8px', textAlign:'center'}}>
            <div className="muted" style={{fontSize:10, fontWeight:600}}>{s.l}</div>
            <div style={{fontSize:18, fontWeight:800, color: vitalColorVar(s.sev), marginTop:2}}>{s.val}</div>
            <div className="muted" style={{fontSize:10}}>{s.u}</div>
            {showTrend && (
              <div style={{marginTop:4, display:'flex', justifyContent:'center'}}>
                <Sparkline points={s.points}/>
              </div>
            )}
          </div>
        ))}
      </div>
      {measuredAt && (
        <div className="muted" style={{fontSize:10.5, marginBottom: showTrend ? 4 : 14, textAlign:'right'}}>마지막 측정 · {measuredAt}</div>
      )}
      {showTrend && (
        <div className="muted" style={{fontSize:10, marginBottom:14, textAlign:'right'}}>최근 {trendCount}회 추세</div>
      )}

      <CaseStatusCard caseRecord={caseRecord} onSaveStatus={onSaveStatus}/>
      <RiskLevelCard elderly={e} canEdit={riskEditable} onSaveRisk={onSaveRisk}/>
      <CaseNoteCard caseId={caseRecord?.id} note={caseRecord?.note} onSave={onSaveNote}/>
      <CaseTransferCard caseRecord={caseRecord} onReload={onReload}/>

      <HealthStatusCard elderly={e} canEdit={riskEditable} onSaveHealth={onSaveHealth}/>

      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:14}}>
        <div className="card" style={{padding:'10px 12px'}}>
          <div className="muted" style={{fontSize:10, fontWeight:600}}>복약</div>
          <div style={{fontSize:16, fontWeight:800, color:'var(--orange-600)', marginTop:2}}>{e.medications.filter(medIsActive).length}<span style={{fontSize:11, fontWeight:600, marginLeft:2}}>종</span></div>
        </div>
        <div className="card" style={{padding:'10px 12px'}}>
          <div className="muted" style={{fontSize:10, fontWeight:600}}>이번 주 방문</div>
          <div style={{fontSize:16, fontWeight:800, color:'var(--sky-600)', marginTop:2}}>{weeklyVisitCount}<span style={{fontSize:11, fontWeight:600, marginLeft:2}}>건</span></div>
        </div>
      </div>
    </>
  );
}

// 복약 활성/중단 판정 — stopped_at 미설정 또는 "내일 이후"면 활성(백엔드 active 스코프
// `stopped_at IS NULL OR stopped_at > Date.current` 와 동일 규칙: 오늘 중단=비활성).
// date-only(YYYY-MM-DD) 사전식 문자열 비교 — new Date 파싱은 UTC 자정으로 해석돼
// KST off-by-one(오늘 중단인데 활성으로 보임)이 나므로 쓰지 않는다.
// 문자열 폴백(일부 진입점이 약명 문자열 배열을 보냄)은 항상 활성으로 본다.
function medIsActive(med) {
  if (typeof med === "string" || !med) return true;
  if (!med.stopped_at) return true;
  return String(med.stopped_at).slice(0, 10) > todayYmd();
}

// 복약 한 장 카드. canEdit 이면 [수정]/[중단·재개]/[삭제] 액션을 노출한다.
function MedCard({ med, canEdit, stopped, onEdit, onToggleStop, onDelete }) {
  const m = typeof med === "string" ? { name: med } : (med || {});
  const sub = [m.dosage, m.frequency, m.time_of_day].filter(Boolean).join(" · ");
  return (
    <div className="card" style={{padding:'12px 14px', marginBottom:8, display:'flex', alignItems:'flex-start', gap:12, opacity: stopped ? 0.6 : 1}}>
      <span style={{width:34, height:34, borderRadius:10, background:'var(--orange-50)', color:'var(--orange-600)', display:'grid', placeItems:'center', fontWeight:800, fontSize:13, flexShrink:0}}>℞</span>
      <div style={{flex:1, minWidth:0}}>
        <div style={{fontSize:13, fontWeight:600, color:'var(--ink-1)'}}>
          {m.name || '이름 없음'}
          {stopped && <span className="badge badge-gray" style={{fontSize:9, marginLeft:6}}>중단됨</span>}
        </div>
        {sub && <div className="muted" style={{fontSize:11, marginTop:2}}>{sub}</div>}
        {m.note && <div style={{fontSize:11, color:'var(--ink-2)', marginTop:4, whiteSpace:'pre-wrap'}}>{m.note}</div>}
        {canEdit && typeof med !== "string" && (
          <div style={{display:'flex', gap:14, marginTop:8}}>
            <button type="button" onClick={() => onEdit(m)}
                    style={{fontSize:11, fontWeight:700, color:'var(--orange-600)', background:'transparent', border:'none', padding:0, cursor:'pointer'}}>수정</button>
            <button type="button" onClick={() => onToggleStop(m)}
                    style={{fontSize:11, fontWeight:700, color:'var(--ink-3)', background:'transparent', border:'none', padding:0, cursor:'pointer'}}>{stopped ? '재개' : '중단'}</button>
            <button type="button" onClick={() => onDelete(m)}
                    style={{fontSize:11, fontWeight:700, color:'var(--rose-500)', background:'transparent', border:'none', padding:0, cursor:'pointer'}}>삭제</button>
          </div>
        )}
      </div>
    </div>
  );
}

function CaseMedsPane({ medications, elderlyId, canEdit, onChange }) {
  const [editing, setEditing] = React.useState(null); // null | "new" | medObject
  const [busy, setBusy]       = React.useState(false);
  const [err, setErr]         = React.useState(null);

  const meds    = medications || [];
  const active  = meds.filter(medIsActive);
  const stopped = meds.filter(m => !medIsActive(m));

  // 중단 토글(soft). 중단=오늘 날짜로 stopped_at 설정, 재개=stopped_at 비움.
  const toggleStop = async (m) => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const reopen = !medIsActive(m);
      await mobileFetch(`/api/v1/medications/${m.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ medication: { stopped_at: reopen ? null : todayYmd() } }),
      });
      await onChange?.();
    } catch (e) { setErr(humanMobileError(e)); }
    finally { setBusy(false); }
  };

  // 완전 삭제(hard). 복구 불가라 confirm 1단계.
  const remove = async (m) => {
    if (busy) return;
    if (!window.confirm(`'${m.name}' 복약 정보를 완전히 삭제할까요? 되돌릴 수 없습니다.`)) return;
    setBusy(true); setErr(null);
    try {
      await mobileFetch(`/api/v1/medications/${m.id}`, { method: "DELETE" });
      await onChange?.();
    } catch (e) { setErr(humanMobileError(e)); }
    finally { setBusy(false); }
  };

  const cardProps = {
    canEdit,
    onEdit: (m) => setEditing(m),
    onToggleStop: toggleStop,
    onDelete: remove,
  };

  return (
    <>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8}}>
        <div style={{fontSize:12, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em'}}>복약 ({active.length}종)</div>
        {canEdit && (
          <button type="button" onClick={() => setEditing("new")}
                  style={{fontSize:11.5, fontWeight:800, color:'var(--orange-600)', background:'transparent', border:'none', padding:0, cursor:'pointer'}}>+ 추가</button>
        )}
      </div>

      {err && (
        <div role="alert" style={{marginBottom:10, padding:'8px 10px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:8, fontSize:11.5}}>{err}</div>
      )}

      {meds.length === 0 && (
        <div className="muted" style={{padding:'30px 0', textAlign:'center', fontSize:12}}>등록된 복약 정보가 없습니다.</div>
      )}

      {active.map((med, i) => (
        <MedCard key={med.id ?? `a${i}`} med={med} stopped={false} {...cardProps}/>
      ))}

      {stopped.length > 0 && (
        <>
          <div className="muted" style={{fontSize:11, fontWeight:700, margin:'12px 0 6px'}}>중단된 복약 ({stopped.length})</div>
          {stopped.map((med, i) => (
            <MedCard key={med.id ?? `s${i}`} med={med} stopped={true} {...cardProps}/>
          ))}
        </>
      )}

      {editing && (
        <MedEditSheet
          med={editing === "new" ? null : editing}
          elderlyId={elderlyId}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await onChange?.(); }}
        />
      )}
    </>
  );
}

// 오늘 날짜 YYYY-MM-DD(로컬). date-only 값이라 toISOString(UTC) 금지 — KST off-by-one 방지.
function todayYmd() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// 복약 추가/수정 바텀 시트. med=null 이면 추가, 객체면 수정. 이름만 필수.
// <input type="date"> 는 로컬 YYYY-MM-DD 문자열을 그대로 주므로 TZ 변환이 필요 없다.
function MedEditSheet({ med, elderlyId, onClose, onSaved }) {
  const isNew = !med;
  const [name, setName]   = React.useState(med?.name || "");
  const [dosage, setDosage] = React.useState(med?.dosage || "");
  const [freq, setFreq]   = React.useState(med?.frequency || "");
  const [tod, setTod]     = React.useState(med?.time_of_day || "");
  const [note, setNote]   = React.useState(med?.note || "");
  const [started, setStarted] = React.useState((med?.started_at || "").slice(0, 10));
  const [saving, setSaving]   = React.useState(false);
  const [err, setErr]         = React.useState(null);

  const canSave = name.trim().length > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true); setErr(null);
    const payload = {
      name: name.trim(), dosage: dosage.trim(), frequency: freq.trim(),
      time_of_day: tod.trim(), note: note.trim(), started_at: started || null,
    };
    try {
      if (isNew) {
        await mobileFetch(`/api/v1/medications`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ medication: { ...payload, elderly_id: elderlyId } }),
        });
      } else {
        await mobileFetch(`/api/v1/medications/${med.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ medication: payload }),
        });
      }
      await onSaved();
    } catch (e) {
      setErr(humanMobileError(e));
      setSaving(false);
    }
  };

  const field = { width:'100%', padding:'10px 12px', fontSize:13, borderRadius:10, border:'1px solid var(--border-soft)', background:'var(--bg)', color:'var(--ink-1)', fontFamily:'inherit', boxSizing:'border-box' };
  const lbl   = { fontSize:11.5, fontWeight:700, color:'var(--ink-3)', marginBottom:5 };

  return (
    <div role="dialog" aria-modal="true" aria-label={isNew ? "복약 추가" : "복약 수정"}
         onClick={onClose}
         style={{position:'fixed', inset:0, background:'rgba(15,15,18,0.5)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:50}}>
      <div onClick={e => e.stopPropagation()}
           style={{width:'100%', maxWidth:480, maxHeight:'88vh', overflowY:'auto', background:'var(--surface)', borderTopLeftRadius:18, borderTopRightRadius:18, padding:'18px 22px 22px'}}>
        <div style={{width:36, height:4, background:'var(--ink-4)', borderRadius:99, margin:'0 auto 14px'}}/>
        <div style={{fontSize:15, fontWeight:800, marginBottom:14}}>{isNew ? '복약 추가' : '복약 수정'}</div>

        <div style={{marginBottom:12}}>
          <div style={lbl}>약 이름 <span style={{color:'var(--rose-500)'}}>*</span></div>
          <input value={name} onChange={e => setName(e.target.value)} disabled={saving}
                 placeholder="예: 혈압약" maxLength={100} style={field}/>
        </div>
        <div style={{display:'flex', gap:8, marginBottom:12}}>
          <div style={{flex:1}}>
            <div style={lbl}>용량</div>
            <input value={dosage} onChange={e => setDosage(e.target.value)} disabled={saving}
                   placeholder="예: 10mg 1정" maxLength={100} style={field}/>
          </div>
          <div style={{flex:1}}>
            <div style={lbl}>횟수</div>
            <input value={freq} onChange={e => setFreq(e.target.value)} disabled={saving}
                   placeholder="예: 1일 2회" maxLength={100} style={field}/>
          </div>
        </div>
        <div style={{display:'flex', gap:8, marginBottom:12}}>
          <div style={{flex:1}}>
            <div style={lbl}>복용 시간</div>
            <input value={tod} onChange={e => setTod(e.target.value)} disabled={saving}
                   placeholder="예: 아침/저녁" maxLength={100} style={field}/>
          </div>
          <div style={{flex:1}}>
            <div style={lbl}>시작일</div>
            <input type="date" value={started} onChange={e => setStarted(e.target.value)} disabled={saving} style={field}/>
          </div>
        </div>
        <div style={{marginBottom:14}}>
          <div style={lbl}>메모</div>
          <textarea value={note} onChange={e => setNote(e.target.value)} disabled={saving}
                    rows={2} placeholder="복약 관련 특이사항" style={{...field, resize:'vertical'}}/>
        </div>

        {err && (
          <div role="alert" style={{marginBottom:10, padding:'8px 10px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:8, fontSize:11.5}}>{err}</div>
        )}

        <div style={{display:'flex', gap:8}}>
          <button type="button" onClick={onClose} disabled={saving}
                  style={{flex:1, padding:'12px 0', fontSize:13, fontWeight:700, background:'var(--surface)', border:'1px solid var(--border-soft)', borderRadius:10, color:'var(--ink-2)', cursor: saving ? 'not-allowed' : 'pointer'}}>
            취소
          </button>
          <button type="button" onClick={handleSave} disabled={!canSave}
                  style={{flex:2, padding:'12px 0', fontSize:13, fontWeight:800, color:'#fff', border:'none', borderRadius:10,
                          background: !canSave ? 'var(--ink-3)' : 'var(--orange-500)',
                          cursor: !canSave ? 'not-allowed' : (saving ? 'wait' : 'pointer')}}>
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

// 예정 방문의 상대 시간 — "오늘 14:00" / "내일 14:00" / "수요일 14:00" /
// "12/15 (목) 14:00". 다음 방문 카드 안 카운트다운에서 같이 쓴다. 결측은 null.
// formatMeasuredAt 과 분리한 이유: 그쪽은 "최근 측정" 과거 시점 전용이고
// 이쪽은 미래/현재 표시라 분기 규칙이 다르다.
const DOW_KO = ['일','월','화','수','목','금','토'];
function formatScheduledAt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now  = new Date();
  const hhmm = d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false });
  if (d.toDateString() === now.toDateString()) return `오늘 ${hhmm}`;
  const tmr = new Date(now); tmr.setDate(now.getDate() + 1);
  if (d.toDateString() === tmr.toDateString()) return `내일 ${hhmm}`;
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return `어제 ${hhmm}`;
  const diffDays = Math.round((d.getTime() - now.getTime()) / 86400000);
  // 이번 주(앞으로 6일 이내) 미래는 요일로 — "수요일 14:00"이 "12/15"보다 직관적.
  if (diffDays >= 2 && diffDays <= 6) return `${DOW_KO[d.getDay()]}요일 ${hhmm}`;
  return `${d.getMonth() + 1}/${d.getDate()} (${DOW_KO[d.getDay()]}) ${hhmm}`;
}

const VISIT_STATUS_BADGE = {
  planned:     { label: '예정',   cls: 'badge-gray'   },
  in_progress: { label: '진행 중', cls: 'badge-orange' },
  completed:   { label: '완료',   cls: 'badge-green'  },
  cancelled:   { label: '취소',   cls: 'badge-rose'   },
};

// 한 줄짜리 방문 카드 — 예정/지난 양쪽이 같은 표시. 차이는 부모가 정렬 + 라벨로 처리.
function VisitRow({ v }) {
  const dt = new Date(v.scheduled_at);
  const monthDay = `${dt.getMonth() + 1}/${dt.getDate()}`;
  const hhmm = dt.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false });
  const badge = VISIT_STATUS_BADGE[v.status] || { label: v.status, cls: 'badge-gray' };
  return (
    <a href={`/m/visit?id=${v.id}`}
       style={{padding:'10px 14px', background:'transparent', border:'1px solid var(--border-soft)', borderRadius:10, marginBottom:6, display:'flex', alignItems:'center', gap:10, textDecoration:'none', color:'inherit'}}>
      <div style={{fontSize:11, fontWeight:700, color:'var(--ink-2)', minWidth:64}}>{monthDay} {DOW_KO[dt.getDay()]}</div>
      <div style={{flex:1, fontSize:12.5, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{hhmm} · {v.manager_name}</div>
      <span className={"badge " + badge.cls} style={{fontSize:9}}>{badge.label}</span>
    </a>
  );
}

function CaseVisitsPane({ upcoming, past }) {
  const up = upcoming || [], pa = past || [];
  if (up.length === 0 && pa.length === 0) {
    return <div className="muted" style={{padding:'30px 0', textAlign:'center', fontSize:12}}>등록된 방문이 없습니다.</div>;
  }
  // 다음 방문은 upcoming 의 첫 번째(가장 가까운 미래). 강조 카드로 한 번,
  // 아래 목록에서는 제외해 중복을 피한다.
  const next = up[0];
  const upRest = up.slice(1);
  const nextLabel = next ? formatScheduledAt(next.scheduled_at) : null;
  return (
    <>
      {next && (
        <div className="card" style={{padding:'14px 16px', marginBottom:14, background:'var(--orange-50)', border:'1px solid var(--orange-100)'}}>
          <div style={{fontSize:10.5, fontWeight:700, color:'var(--orange-700)', letterSpacing:'0.06em'}}>다음 방문</div>
          <a href={`/m/visit?id=${next.id}`} style={{display:'block', textDecoration:'none', color:'inherit', marginTop:4}}>
            <div style={{fontSize:16, fontWeight:800, color:'var(--ink-1)'}}>{nextLabel || '시각 미상'}</div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>{next.manager_name} 매니저 · {VISIT_STATUS_BADGE[next.status]?.label || next.status}</div>
          </a>
        </div>
      )}

      {upRest.length > 0 && (
        <>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
            <div style={{fontSize:12, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em'}}>예정 방문 ({upRest.length})</div>
            <a href="/m/schedule" style={{fontSize:11, color:'var(--orange-600)', fontWeight:600, textDecoration:'none'}}>전체 일정 →</a>
          </div>
          {upRest.map(v => <VisitRow key={v.id} v={v}/>)}
        </>
      )}

      {pa.length > 0 && (
        <div style={{marginTop: (next || upRest.length > 0) ? 16 : 0}}>
          <div style={{fontSize:12, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em', marginBottom:8}}>지난 방문 ({pa.length})</div>
          {pa.map(v => <VisitRow key={v.id} v={v}/>)}
        </div>
      )}
    </>
  );
}

function CaseReportsPane({ reports }) {
  if (!reports || reports.length === 0) {
    return <div className="muted" style={{padding:'30px 0', textAlign:'center', fontSize:12}}>방문 기록이 없습니다.</div>;
  }
  return (
    <>
      <div style={{fontSize:12, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em', marginBottom:8}}>최근 방문 기록 ({reports.length})</div>
      {reports.map((r) => {
        const cells = [
          { l:"BP",   v: r.bp || '-',          sev: bpSeverity(r.bp) },
          { l:"HR",   v: r.hr  != null ? `${r.hr}`   : '-', sev: hrSeverity(r.hr) },
          { l:"Temp", v: r.temp != null ? `${r.temp}` : '-', sev: tempSeverity(r.temp) },
        ];
        return (
          <div key={r.id} className="card" style={{padding:'12px 14px', marginBottom:8}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
              <div style={{fontSize:12, fontWeight:700}}>{new Date(r.at).toLocaleDateString('ko-KR', { month:'short', day:'numeric', weekday:'short' })}</div>
              {r.condition && <span className="badge badge-gray" style={{fontSize:9}}>{r.condition}</span>}
            </div>
            <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6, marginBottom: r.note ? 10 : 0}}>
              {cells.map((s, i) => (
                <div key={i} style={{padding:'6px 8px', background:'var(--bg-sub)', borderRadius:6, textAlign:'center'}}>
                  <div className="muted" style={{fontSize:9, fontWeight:600}}>{s.l}</div>
                  <div style={{fontSize:13, fontWeight:700, color: vitalColorVar(s.sev) === 'var(--ink-3)' ? 'var(--ink-1)' : vitalColorVar(s.sev), marginTop:1}}>{s.v}</div>
                </div>
              ))}
            </div>
            {r.note && (
              <div style={{padding:'8px 10px', background:'var(--bg-sub)', borderRadius:6, fontSize:11.5, lineHeight:1.5, whiteSpace:'pre-wrap', color:'var(--ink-2)'}}>{r.note}</div>
            )}
          </div>
        );
      })}
    </>
  );
}

function CaseFamilyPane({ elderly }) {
  const e = elderly;
  if (!e.guardian_name && !e.guardian_phone && !e.phone) {
    return <div className="muted" style={{padding:'30px 0', textAlign:'center', fontSize:12}}>등록된 연락처가 없습니다.</div>;
  }
  return (
    <>
      {/* 어르신 본인 연락처 — elderlies.phone(보호자 번호와 별개)이 있을 때만 노출.
          본인 계정이 없는 어르신은 번호가 없어 이 블록은 생략된다. */}
      {e.phone && (
        <>
          <div style={{fontSize:12, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em', marginBottom:8}}>어르신 본인</div>
          <div className="card" style={{padding:'14px 16px', marginBottom:10}}>
            <div style={{display:'flex', alignItems:'center', gap:12}}>
              <div className="avatar" style={{width:42, height:42, fontSize:15, background:'var(--green-50)', color:'var(--green-700)'}}>{(e.name || '?')[0]}</div>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:14, fontWeight:700}}>{e.name || '어르신'}</div>
                <div className="muted" style={{fontSize:11, marginTop:1}}>본인 · {e.phone}</div>
              </div>
            </div>
            {/* 연락 — 전화/메시지 나란히. 메시지는 앱 내 채널이라 항상 가능하고,
                대화 스레드가 어르신(elderly_id) 기준이라 어르신 본인 계정으로 도착한다. */}
            <div style={{marginTop:12, display:'flex', gap:8}}>
              <a href={`tel:${e.phone}`} aria-label={`${e.name || '어르신'} 본인 전화 ${e.phone}`}
                 style={{flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'10px 0', background:'var(--green-600)', color:'#fff', borderRadius:10, fontSize:13, fontWeight:700, textDecoration:'none'}}>
                <span style={{width:16, height:16, display:'inline-block'}}>{Ic.phone}</span>
                전화 걸기
              </a>
              <a href={`/m/chat?elderly_id=${e.id}`} aria-label={`${e.name || '어르신'}께 메시지`}
                 style={{flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'10px 0', background:'var(--surface)', color:'var(--green-700)', border:'1px solid var(--green-300)', borderRadius:10, fontSize:13, fontWeight:700, textDecoration:'none'}}>
                <span style={{width:16, height:16, display:'inline-block'}}>{Ic.chat}</span>
                메시지
              </a>
            </div>
          </div>
        </>
      )}
      {(e.guardian_name || e.guardian_phone) && (<>
      <div style={{fontSize:12, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em', marginBottom:8}}>보호자</div>
      <div className="card" style={{padding:'14px 16px', marginBottom:10}}>
        <div style={{display:'flex', alignItems:'center', gap:12}}>
          <div className="avatar" style={{width:42, height:42, fontSize:15, background:'var(--orange-50)', color:'var(--orange-700)'}}>{(e.guardian_name || '?')[0]}</div>
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontSize:14, fontWeight:700}}>{e.guardian_name || '미등록'}</div>
            <div className="muted" style={{fontSize:11, marginTop:1}}>
              {e.guardian_relation ? `${e.guardian_relation}` : '관계 미상'}
              {e.guardian_phone ? ` · ${e.guardian_phone}` : ''}
            </div>
          </div>
        </div>
        {/* 연락 — 전화/메시지 나란히. 전화는 번호 없으면 비활성이지만,
            메시지는 앱 내 채널이라 번호와 무관하게 항상 가능. */}
        <div style={{marginTop:12, display:'flex', gap:8}}>
          {e.guardian_phone ? (
            <a href={`tel:${e.guardian_phone}`}
               style={{flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'10px 0', background:'var(--orange-500)', color:'#fff', borderRadius:10, fontSize:13, fontWeight:700, textDecoration:'none'}}>
              <span style={{width:16, height:16, display:'inline-block'}}>{Ic.phone}</span>
              전화 걸기
            </a>
          ) : (
            <div className="muted" style={{flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'10px 0', background:'var(--bg-sub)', borderRadius:10, fontSize:12, fontWeight:600}}>
              <span style={{width:16, height:16, display:'inline-block'}}>{Ic.phone}</span>
              번호 미등록
            </div>
          )}
          {/* 대화 스레드는 어르신당 1개(elderly_id 기준)라 어르신 본인 계정이 있으면
              메시지 버튼은 위의 '어르신 본인' 카드로 옮겨 둔다. 본인 계정이 없을 때만
              그 스레드를 읽는 유일한 대상인 보호자에게 메시지 버튼을 노출한다. */}
          {!e.phone && (
          <a href={`/m/chat?elderly_id=${e.id}`} aria-label={`${e.guardian_name || '보호자'}와 메시지`}
             style={{flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'10px 0', background:'var(--surface)', color:'var(--orange-600)', border:'1px solid var(--orange-300)', borderRadius:10, fontSize:13, fontWeight:700, textDecoration:'none'}}>
            <span style={{width:16, height:16, display:'inline-block'}}>{Ic.chat}</span>
            메시지
          </a>
          )}
        </div>
      </div>
      </>)}
    </>
  );
}

// ────────────────────────────────────────────────────
// 6. 방문 기록 작성 (POST)
// Draft auto-save key namespace. Keyed per visit so two field reports
// don't stomp each other when an operator hops between cases.
const REPORT_DRAFT_PREFIX = "care-os.reportDraft.";
const POST_SAVE_REDIRECT_MS = 1500;

function loadDraft(visitId) {
  if (!visitId || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(REPORT_DRAFT_PREFIX + visitId);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function saveDraft(visitId, payload) {
  if (!visitId || typeof window === "undefined") return;
  try { window.localStorage.setItem(REPORT_DRAFT_PREFIX + visitId, JSON.stringify(payload)); }
  catch { /* quota / blocked storage */ }
}
function clearDraft(visitId) {
  if (!visitId || typeof window === "undefined") return;
  try { window.localStorage.removeItem(REPORT_DRAFT_PREFIX + visitId); } catch {}
}

// 활력 측정 입력의 생리학적 허용 범위 — '정상' 범위가 아니라 '입력 가능한(오타가 아닌)'
// 상한/하한이다(정상 여부는 색상 severity가 따로 표시). 이 범위를 벗어나면 저장을 막아
// 365℃·혈압 999 같은 오입력이 기록·보호자 알림으로 흘러가는 걸 차단한다.
const VITAL_RANGES = {
  sys:  { min: 60, max: 260 },  // 수축기 혈압 mmHg
  dia:  { min: 30, max: 160 },  // 이완기 혈압 mmHg
  hr:   { min: 30, max: 220 },  // 맥박 bpm
  temp: { min: 33, max: 43  },  // 체온 °C
};

// 현재 입력(bp "sys/dia" 문자열, hr/temp 문자열)에서 범위 위반 메시지를 필드별로 반환.
// 빈값은 '미측정'이라 검사하지 않는다(null 저장 허용).
function vitalRangeErrors({ bp, hr, temp }) {
  const out = { bp: null, hr: null, temp: null };
  const inR = (v, r) => Number.isFinite(v) && v >= r.min && v <= r.max;
  if (bp != null && String(bp).trim() !== "") {
    const parts = String(bp).split("/");
    const sys = parseInt(parts[0], 10), dia = parseInt(parts[1], 10);
    if (parts.length !== 2 || isNaN(sys) || isNaN(dia)) {
      out.bp = '"수축기/이완기" 형식으로 입력하세요 (예: 132/82)';
    } else if (!inR(sys, VITAL_RANGES.sys) || !inR(dia, VITAL_RANGES.dia)) {
      out.bp = `허용 범위: 수축기 ${VITAL_RANGES.sys.min}~${VITAL_RANGES.sys.max} · 이완기 ${VITAL_RANGES.dia.min}~${VITAL_RANGES.dia.max} mmHg`;
    }
  }
  if (hr != null && String(hr).trim() !== "") {
    if (!inR(parseInt(hr, 10), VITAL_RANGES.hr)) out.hr = `허용 범위: ${VITAL_RANGES.hr.min}~${VITAL_RANGES.hr.max} bpm`;
  }
  if (temp != null && String(temp).trim() !== "") {
    if (!inR(parseFloat(temp), VITAL_RANGES.temp)) out.temp = `허용 범위: ${VITAL_RANGES.temp.min}~${VITAL_RANGES.temp.max} °C`;
  }
  return out;
}

function MgrReport() {
  // ?mode=edit + ?visit_id=N → 기존 기록 prefill + PATCH 모드. /m/reports 의
  // 모달 "수정" 버튼이 이 흐름을 진입점으로 쓴다. 백엔드가 24h 윈도우를
  // 강제하므로 이 화면은 시간 제약을 추가로 두지 않고 410 응답만 사용자에게
  // 표시한다.
  const params = new URLSearchParams(window.location.search);
  const isEditMode = params.get("mode") === "edit" && !!params.get("visit_id");

  const [visit, setVisit] = React.useState(null);
  // 방문 로드 실패(삭제·미존재·권한)면 무한 "불러오는 중" 대신 안내를 띄운다.
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [editingReportId, setEditingReportId] = React.useState(null);
  // 방문에 이미 보고서가 있는지 확인이 끝났는지. draft 복원이 이 확인 뒤에
  // 돌도록 게이트해 서버 값을 stale draft 로 덮지 않게 한다.
  const [reportChecked, setReportChecked] = React.useState(false);
  const [condition, setCondition] = React.useState("normal");
  // 활력 입력은 빈값으로 시작한다 — 더미 기본값(132/82 등)을 두면 매니저가 측정 없이
  // 제출했을 때 실제 잰 적 없는 값이 정상 측정치로 저장돼(요약/이상징후 판정 오염) 오해를
  // 부른다. 미입력은 save에서 null로 보내 "미측정"으로 둔다. 데스크톱 편집기와 동일 동작.
  const [bp, setBp] = React.useState("");
  const [hr, setHr] = React.useState("");
  const [temp, setTemp] = React.useState("");
  const [note, setNote] = React.useState("");
  // 제출 전 선택해 둔 신규 사진(File + 미리보기 objectURL). 보고서는 먼저 저장하고
  // 반환된 id로 업로드하므로, 제출 시점까지 클라이언트에 들고 있는다.
  const [pendingPhotos, setPendingPhotos] = React.useState([]);
  // 수정 모드에서 서버에 이미 저장된 사진 URL(읽기 전용 썸네일).
  const [existingPhotos, setExistingPhotos] = React.useState([]);
  const [savedId, setSavedId] = React.useState(null);
  const [draftSavedAt, setDraftSavedAt] = React.useState(null);
  const [err, setErr] = React.useState(null);

  // objectURL 누수 방지 — 언마운트 시 미리보기 URL 해제.
  React.useEffect(() => () => { pendingPhotos.forEach(p => URL.revokeObjectURL(p.url)); }, []);

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setPendingPhotos(prev => [...prev, ...files.map(f => ({ file: f, url: URL.createObjectURL(f) }))]);
  };
  const removePending = (idx) => {
    setPendingPhotos(prev => {
      const target = prev[idx];
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  // Load the requested visit. Deep-link via ?visit_id=N (set by mgr-visit
  // on completion); otherwise fall back to the in_progress/planned heuristic.
  React.useEffect(() => {
    const explicit = new URLSearchParams(window.location.search).get("visit_id");
    const pickFirst = (j) => j.visits.find(x => x.status === "in_progress")
                          || j.visits.find(x => x.status === "planned")
                          || j.visits[0];
    if (explicit) {
      // 딥링크된 방문은 id 로 직접 로드한다(MgrVisit 과 동일한 mobile/visit/:id).
      // 과거엔 ?week_start=오늘 주간목록에서 find 했는데, on_week 는 [오늘, 오늘+6일]
      // 전방창이라 대상 방문이 그 밖이면(어제·지난주 완료 방문 등) 못 찾아 엉뚱한
      // 방문으로 폴백하거나(잘못된 대상에 보고서) 무한 로딩/404 가 났다. 단일 조회는
      // 날짜와 무관하게 정확히 그 방문을 가져온다. 응답은 elderly 가 visit 의 형제로
      // 오므로 목록 형태(visit.elderly)에 맞춰 합친다.
      fetch(`/api/v1/mobile/visit/${explicit}`, { credentials: "same-origin" })
        .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
        .then(b => setVisit({ ...b.visit, elderly: b.elderly }))
        .catch(() => setLoadFailed(true));
      return;
    }
    fetch("/api/v1/visits", { credentials: "same-origin" })
      .then(r => r.json())
      .then(j => { const v = pickFirst(j); v ? setVisit(v) : setLoadFailed(true); });
  }, []);

  // 방문 1건당 보고서 1건. visit 이 정해지면 명시 edit 진입 여부와 무관하게 항상
  // 기존 보고서를 조회하고, 있으면 새로 만들지 않고 그 보고서를 편집한다(중복 작성
  // 방지). 백엔드도 create 를 409 로 막지만, 진입 단계에서 수정 모드로 전환해
  // 사용자가 같은 방문에 두 번째 보고서를 만드는 경로 자체를 없앤다.
  React.useEffect(() => {
    if (!visit?.id) return;
    let alive = true;
    fetch(`/api/v1/visit_reports?visit_id=${visit.id}`, { credentials: "same-origin" })
      .then(r => r.json())
      .then(arr => {
        if (!alive) return;
        const r = Array.isArray(arr) ? arr[0] : null;
        if (!r) {
          // edit 로 들어왔는데 대상이 없으면 안내(작성 진입이면 정상 — 그대로 작성).
          if (isEditMode) setErr("수정할 방문 기록을 찾을 수 없습니다.");
          setReportChecked(true);
          return;
        }
        setEditingReportId(r.id);
        if (r.condition) setCondition(r.condition);
        if (r.blood_pressure_sys && r.blood_pressure_dia) setBp(`${r.blood_pressure_sys}/${r.blood_pressure_dia}`);
        if (r.heart_rate != null) setHr(r.heart_rate);
        if (r.body_temp != null) setTemp(r.body_temp);
        if (r.note != null) setNote(r.note);
        if (Array.isArray(r.photos)) setExistingPhotos(r.photos);
        setReportChecked(true);
      })
      .catch(e => { if (alive) { setErr(apiErrorMessage(e)); setReportChecked(true); } });
    return () => { alive = false; };
  }, [visit?.id, isEditMode]);

  // 기존 보고서가 없을 때만(작성 모드) draft 복원. 존재 확인이 끝난 뒤 실행해
  // 서버 값을 stale draft 로 덮지 않는다.
  React.useEffect(() => {
    if (!reportChecked || editingReportId || !visit?.id) return;
    const d = loadDraft(visit.id);
    if (!d) return;
    if (d.condition) setCondition(d.condition);
    if (d.bp != null) setBp(d.bp);
    if (d.hr != null) setHr(d.hr);
    if (d.temp != null) setTemp(d.temp);
    if (d.note != null) setNote(d.note);
  }, [reportChecked, editingReportId, visit?.id]);

  // 실효 수정 모드 — URL ?mode=edit 이거나, 작성 진입이라도 기존 보고서가 발견돼
  // 수정으로 전환된 경우. 표시·저장 분기·리다이렉트가 이 값을 따른다.
  const editing = isEditMode || editingReportId != null;

  const persistDraft = React.useCallback(() => {
    if (!visit?.id) return;
    saveDraft(visit.id, { condition, bp, hr, temp, note });
    setDraftSavedAt(new Date());
  }, [visit?.id, condition, bp, hr, temp, note]);

  const save = async () => {
    setErr(null);
    if (!visit) return;
    // 활력 측정 범위 위반은 저장 차단(버튼도 비활성이지만 단축키/엔터 등 대비 이중 가드).
    const re = vitalRangeErrors({ bp, hr, temp });
    if (re.bp || re.hr || re.temp) {
      setErr("활력 측정값을 확인하세요 — " + [re.bp && `혈압(${re.bp})`, re.hr && `맥박(${re.hr})`, re.temp && `체온(${re.temp})`].filter(Boolean).join(" · "));
      return;
    }
    const [sys, dia] = bp.split('/').map(s => parseInt(s, 10));
    const payload = {
      condition,
      blood_pressure_sys: isNaN(sys) ? null : sys,
      blood_pressure_dia: isNaN(dia) ? null : dia,
      // 빈값/비숫자는 null 로 보내 서버가 미측정으로 둔다(맥박은 정수, 체온은 소수).
      heart_rate: hr === "" || isNaN(parseInt(hr, 10)) ? null : parseInt(hr, 10),
      body_temp: temp === "" || isNaN(parseFloat(temp)) ? null : parseFloat(temp),
      note,
    };
    try {
      if (editingReportId) {
        // PATCH 경로 — 410 Gone(윈도우 종료)·403 Forbidden 처리.
        const res = await fetch(`/api/v1/visit_reports/${editingReportId}`, {
          method: "PATCH",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ visit_report: payload }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (res.status === 410) throw new Error(body.message || "작성 후 24시간이 지나 수정할 수 없습니다.");
          if (res.status === 403) throw new Error(body.message || "이 방문 기록을 수정할 권한이 없습니다.");
          const err = new Error(body.message || `오류 (${res.status})`);
          (err as any).code = body?.error;
          throw err;
        }
        if (pendingPhotos.length) await visitReports.addPhotos(editingReportId, pendingPhotos.map(p => p.file));
        setSavedId(body.id);
        // 수정 완료 후엔 모바일 방문 상세로 복귀해 갱신된 기록을 맥락에서 확인한다.
        // (과거엔 /m/reports 로 보냈는데 그 화면은 데스크톱 레이아웃이라 폰 전용
        //  매니저 흐름을 깼다 — 매니저는 모바일에 머물러야 한다. [[manager_mobile_only]])
        window.setTimeout(() => { window.location.href = `/m/visit?id=${visit.id}`; }, POST_SAVE_REDIRECT_MS);
      } else {
        let r;
        try {
          r = await api.create("visit_reports", { visit_id: visit.id, ...payload });
        } catch (e) {
          // 진입~제출 사이에 보고서가 생겼을 때(동시 제출/타기기) 백엔드가 409.
          // 중복 생성 대신 수정 모드로 안내한다.
          if (String(e.message).includes("report_exists") || String(e.message).startsWith("409")) {
            throw new Error("이미 이 방문의 보고서가 작성되었습니다. 새로고침 후 기존 보고서를 수정해 주세요.");
          }
          throw e;
        }
        // 보고서 저장 후 반환된 id로 사진 업로드(멀티파트). 업로드 실패는 사용자에게
        // 노출하되, 본문은 이미 저장됐으므로 보고서 자체를 깨지 않는다.
        if (pendingPhotos.length) await visitReports.addPhotos(r.id, pendingPhotos.map(p => p.file));
        setSavedId(r.id);
        clearDraft(visit.id);
        // 잠시 후 홈으로 돌려보내 다음 방문 흐름을 자연스럽게 이어간다.
        window.setTimeout(() => { window.location.href = "/m/home"; }, POST_SAVE_REDIRECT_MS);
      }
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  // 뒤로가기는 직전 화면으로 복귀(history.back). 과거엔 /m/visit 로 href-push 했는데,
  // /m/visit 의 뒤로가기가 history.back 이라 둘이 서로를 밀어내며 무한 핑퐁이 됐다.
  // 둘 다 history.back 으로 통일하면 일정→방문상세→보고서→뒤로(방문상세)→뒤로(일정)로
  // 선형 복귀한다. 인앱 히스토리가 없는 딥링크 직접 진입만 방문 상세로 폴백한다.
  const goBack = () => {
    if (window.history.length > 1) window.history.back();
    else window.location.href = `/m/visit?id=${visit.id}`;
  };

  if (!visit) return (
    <div className="app" style={{padding:40, textAlign:'center'}}>
      {loadFailed ? (
        <div className="muted" style={{fontSize:13, lineHeight:1.7}}>
          방문을 찾을 수 없습니다.<br/>일정에서 방문을 다시 선택해 주세요.
          <div style={{marginTop:16}}><a href="/m/schedule" className="btn btn-ghost btn-sm">일정으로</a></div>
        </div>
      ) : <span className="muted">방문을 불러오는 중…</span>}
    </div>
  );

  // 입력값 즉시 위험 피드백 — 백엔드 AlertDetector와 동일 임계치인 bp/hr/tempSeverity를
  // 재사용해, 매니저가 위험 수치를 '입력하는 순간' 폼에서 바로 알아채게 한다.
  // (현장에서 직접 측정한 매니저에게 사후 푸시 알림을 보내는 건 중복이라, 알림함이 아닌
  //  맥락적 인라인 경고로 처리한다. 보호자 푸시·케이스 알럿 배지는 별도로 유지.)
  const bpSev = bpSeverity(bp), hrSev = hrSeverity(hr), tempSev = tempSeverity(temp);
  // 범위 위반(오입력) — severity(정상/주의/위험)와 별개로, 생리학적으로 불가능한 값.
  // 위반 시 저장을 막고 해당 카드를 빨갛게 표시한다.
  const rangeErr = vitalRangeErrors({ bp, hr, temp });
  const hasRangeErr = !!(rangeErr.bp || rangeErr.hr || rangeErr.temp);
  const vitalCardStyle = (sev, outOfRange) => ({
    padding: 12,
    border: outOfRange        ? '1.5px solid var(--rose-500)'
          : sev === "critical" ? '1.5px solid var(--rose-500)'
          : sev === "warning"  ? '1.5px solid var(--amber-400)' : undefined,
  });
  const vitalValStyle = (sev) =>
    (sev === "critical" || sev === "warning") ? { color: vitalColorVar(sev) } : {};
  const critVitals = [
    bpSev   === "critical" && "혈압",
    hrSev   === "critical" && "맥박",
    tempSev === "critical" && "체온",
  ].filter(Boolean);

  return (
    <div className="app" style={{background:'var(--bg)', overflow:'hidden'}}>
      <StatusBar/>
      <div style={{padding:'8px 22px 12px', display:'flex', alignItems:'center', gap:14}}>
        <a onClick={goBack} aria-label="방문 상세로"
           style={{width:32, height:32, display:'grid', placeItems:'center', borderRadius:10, background:'var(--surface)', color:'var(--ink-1)', textDecoration:'none', cursor:'pointer'}}>‹</a>
        <div style={{flex:1}}>
          <div style={{fontSize:11, color:'var(--ink-3)'}}>{editing ? "방문 기록 수정" : "방문 기록 작성"}</div>
          <div style={{fontSize:14, fontWeight:700}}>{visit.elderly.name} 어르신 · {new Date(visit.scheduled_at).toLocaleDateString('ko-KR')}</div>
        </div>
      </div>

      {savedId && (
        <div style={{margin:'0 22px 12px', padding:12, background:'var(--green-50)', color:'var(--green-700)', borderRadius:10, fontSize:12.5, fontWeight:600}}>
          ✓ 방문 기록이 {editing ? "수정" : "저장"}되었습니다 (#{savedId}). 잠시 후 {editing ? "방문 상세로" : "홈으로"} 이동합니다.
        </div>
      )}
      {!savedId && draftSavedAt && <div style={{margin:'0 22px 12px', padding:'8px 12px', background:'var(--bg-sub)', color:'var(--ink-3)', borderRadius:10, fontSize:11}}>임시저장됨 · {draftSavedAt.toLocaleTimeString('ko-KR', {hour:'2-digit', minute:'2-digit'})}</div>}
      {err && <div style={{margin:'0 22px 12px', padding:12, background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12}}>{err}</div>}

      <div style={{padding:'4px 22px 0', flex:1, overflow:'auto'}}>
        <div style={{fontSize:14, fontWeight:700, marginBottom:10}}>오늘 어르신 컨디션은 어떠셨나요?</div>
        <div style={{display:'flex', gap:6, marginBottom:18}}>
          {[
            {l:'좋음', v:'good', c:'green'},
            {l:'보통', v:'normal', c:'orange'},
            {l:'나쁨', v:'poor', c:'rose'},
            {l:'위급', v:'critical', c:'rose'},
          ].map((m)=>(
            <div key={m.v} onClick={()=>setCondition(m.v)} style={{flex:1, padding:'12px 0', textAlign:'center', borderRadius:12, border: condition===m.v?`1.5px solid var(--${m.c}-500)`:'1px solid var(--border)', background: condition===m.v?`var(--${m.c}-50)`:'var(--surface)', cursor:'pointer'}}>
              <div style={{fontSize:13, fontWeight:700, color: condition===m.v?`var(--${m.c}-700)`:'var(--ink-2)'}}>{m.l}</div>
            </div>
          ))}
        </div>

        <div style={{fontSize:14, fontWeight:700, marginBottom:10}}>활력 측정</div>
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom: critVitals.length ? 10 : 18}}>
          <div className="card" style={vitalCardStyle(bpSev, !!rangeErr.bp)}>
            <div className="muted" style={{fontSize:10, fontWeight:600}}>혈압</div>
            <input className="input" style={{padding:'2px 0', border:'none', background:'transparent', fontSize:22, fontWeight:800, width:'90%', ...vitalValStyle(bpSev)}}
              inputMode="numeric" maxLength={7} aria-invalid={!!rangeErr.bp}
              placeholder="예: 132/82" value={bp} onChange={e=>setBp(e.target.value)}/>
            <div className="muted" style={{fontSize:10}}>mmHg</div>
            {rangeErr.bp && <div role="alert" style={{fontSize:9.5, color:'var(--rose-600)', fontWeight:700, marginTop:3, lineHeight:1.3}}>{rangeErr.bp}</div>}
          </div>
          <div className="card" style={vitalCardStyle(hrSev, !!rangeErr.hr)}>
            <div className="muted" style={{fontSize:10, fontWeight:600}}>맥박</div>
            <input className="input" style={{padding:'2px 0', border:'none', background:'transparent', fontSize:22, fontWeight:800, width:'90%', ...vitalValStyle(hrSev)}} type="number"
              min={VITAL_RANGES.hr.min} max={VITAL_RANGES.hr.max} inputMode="numeric" aria-invalid={!!rangeErr.hr}
              placeholder="예: 76" value={hr} onChange={e=>setHr(e.target.value)}/>
            <div className="muted" style={{fontSize:10}}>bpm</div>
            {rangeErr.hr && <div role="alert" style={{fontSize:9.5, color:'var(--rose-600)', fontWeight:700, marginTop:3, lineHeight:1.3}}>{rangeErr.hr}</div>}
          </div>
          <div className="card" style={vitalCardStyle(tempSev, !!rangeErr.temp)}>
            <div className="muted" style={{fontSize:10, fontWeight:600}}>체온</div>
            <input className="input" style={{padding:'2px 0', border:'none', background:'transparent', fontSize:22, fontWeight:800, width:'90%', ...vitalValStyle(tempSev)}}
              type="number" step="0.1" inputMode="decimal"
              min={VITAL_RANGES.temp.min} max={VITAL_RANGES.temp.max} aria-invalid={!!rangeErr.temp}
              placeholder="예: 36.5" value={temp} onChange={e=>setTemp(e.target.value)}/>
            <div className="muted" style={{fontSize:10}}>°C</div>
            {rangeErr.temp && <div role="alert" style={{fontSize:9.5, color:'var(--rose-600)', fontWeight:700, marginTop:3, lineHeight:1.3}}>{rangeErr.temp}</div>}
          </div>
        </div>
        {critVitals.length > 0 && (
          <div role="alert" style={{margin:'0 0 18px', padding:'10px 12px', background:'var(--rose-50)', color:'var(--rose-700)', borderRadius:10, fontSize:12, fontWeight:600, display:'flex', alignItems:'flex-start', gap:8}}>
            <span aria-hidden="true">⚠</span>
            <span>{critVitals.join('·')} 수치가 위험 범위입니다. 확인 후 필요한 조치를 취해 주세요.</span>
          </div>
        )}

        <div style={{fontSize:14, fontWeight:700, marginBottom:10}}>특이사항 메모</div>
        <textarea className="input" rows={4} placeholder="가족에게 공유될 메모를 작성하세요"
          value={note} onChange={e=>setNote(e.target.value)}/>

        <div style={{fontSize:14, fontWeight:700, margin:'18px 0 10px'}}>방문 사진</div>
        <div style={{display:'flex', flexWrap:'wrap', gap:8, marginBottom:24}}>
          {existingPhotos.map((url, i) => (
            <div key={`ex-${i}`} style={{width:84, height:84, borderRadius:10, overflow:'hidden', border:'1px solid var(--border)', position:'relative'}}>
              <img src={url} alt={`기존 사진 ${i + 1}`} style={{width:'100%', height:'100%', objectFit:'cover'}}/>
            </div>
          ))}
          {pendingPhotos.map((p, i) => (
            <div key={`new-${i}`} style={{width:84, height:84, borderRadius:10, overflow:'hidden', border:'1px solid var(--border)', position:'relative'}}>
              <img src={p.url} alt={`첨부 사진 ${i + 1}`} style={{width:'100%', height:'100%', objectFit:'cover'}}/>
              <button type="button" aria-label="사진 제거" onClick={()=>removePending(i)} disabled={!!savedId}
                style={{position:'absolute', top:2, right:2, width:20, height:20, borderRadius:'50%', border:'none', background:'rgba(0,0,0,0.6)', color:'#fff', fontSize:12, lineHeight:'20px', cursor:'pointer', padding:0}}>×</button>
            </div>
          ))}
          <label style={{width:84, height:84, borderRadius:10, border:'1.5px dashed var(--border)', display:'grid', placeItems:'center', cursor: savedId ? 'default':'pointer', color:'var(--ink-3)', background:'var(--surface)'}}>
            <div style={{textAlign:'center', fontSize:11, fontWeight:600}}>＋<br/>사진 추가</div>
            <input type="file" accept="image/jpeg,image/png,image/webp" multiple capture="environment"
              disabled={!!savedId} style={{display:'none'}}
              onChange={e => { addFiles(e.target.files); e.target.value = ""; }}/>
          </label>
        </div>
      </div>

      <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', gap:8, background:'var(--surface)'}}>
        <button type="button" className="btn btn-ghost btn-lg" style={{flex:1}}
          onClick={persistDraft} disabled={!!savedId}>
          임시저장
        </button>
        <button type="button" className="btn btn-primary btn-lg" style={{flex:2}}
          onClick={save} disabled={!!savedId || hasRangeErr}
          title={hasRangeErr ? "활력 측정값이 허용 범위를 벗어났습니다" : undefined}>
          {savedId ? '저장됨 ✓' : '제출 →'}
        </button>
      </div>
      <HomeBar/>
    </div>
  );
}

// ────────────────────────────────────────────────────
// 7. 마이 — 내 정보 + 메뉴 + 로그아웃.
// 케어매니저는 현장·모바일 전용이라, 마이페이지는 신원·바로가기·로그아웃만 다룬다.
// 데스크탑 대시보드(/dashboard/manager) 진입점은 제거됨(라우트/화면은 보존).
// 권한·역할 정보는 /api/v1/sessions/me 재사용.
const ROLE_LABEL = {
  manager: "케어매니저", guardian: "보호자", citizen: "어르신",
  agency: "유관기관 담당자",
  region_admin: "권역 관리자",
  system_admin: "시스템 관리자",
};

// 소속 기관 변경 신청 — 매니저 셀프서비스. options로 현재 소속/기관목록/진행중 신청을
// 받아, pending이 있으면 '검토 중' 상태로, 없으면 기관 선택 + 사유 입력 폼을 보인다.
// 제출은 승인 큐(org_transfer)에 적재되고, 운영자/권역관리자 승인 시 실제 이동된다.
function OrgTransferRequest() {
  const [data, setData] = React.useState(null);     // null=loading
  const [open, setOpen] = React.useState(false);
  const [orgId, setOrgId] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [done, setDone] = React.useState(false);

  const load = React.useCallback(() => {
    orgTransfer.options().then(setData).catch(() => setData({ organizations: [] }));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const submit = React.useCallback(async () => {
    if (busy) return;
    setErr(null);
    if (!orgId) { setErr("변경할 기관을 선택해주세요."); return; }
    if (!reason.trim()) { setErr("변경 사유를 입력해주세요."); return; }
    setBusy(true);
    try {
      await orgTransfer.request(Number(orgId), reason.trim());
      setDone(true); setOpen(false); setReason(""); setOrgId("");
      load();   // pending 상태 반영
    } catch (e) {
      // 서버 메시지(이미 검토 중/동일 기관 등)를 최대한 그대로 노출.
      let msg = "신청에 실패했습니다.";
      try { msg = JSON.parse(String(e.message).replace(/^\d+ [^{]*/, "")).message || msg; } catch { /* keep */ }
      setErr(msg);
    } finally { setBusy(false); }
  }, [busy, orgId, reason, load]);

  if (!data) return null;   // 로딩 중엔 자리 차지 안 함

  const pending = data.pending_request;
  // 현재 소속 기관은 선택지에서 제외(동일 기관 신청 방지).
  const orgs = (data.organizations || []).filter(o => o.id !== data.current_organization_id);

  return (
    <>
      <div style={{fontSize:11, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em', marginBottom:6}}>소속 기관</div>
      <div className="card" style={{padding:'14px 16px', marginBottom:14}}>
        <div className="muted" style={{fontSize:11.5, marginBottom:10}}>
          현재 소속: <b style={{color:'var(--ink-1)'}}>{data.current_organization_name || '무소속'}</b>
        </div>

        {pending ? (
          <div style={{display:'flex', alignItems:'center', gap:10, background:'var(--amber-50)', border:'1px solid var(--amber-100)', borderRadius:10, padding:'10px 12px'}}>
            <span style={{fontSize:11, fontWeight:700, color:'var(--amber-700)', whiteSpace:'nowrap'}}>검토 중</span>
            <div style={{flex:1, minWidth:0, fontSize:11.5}}>
              <b>{pending.to_organization_name}</b>(으)로 변경 신청됨
              <div className="muted" style={{fontSize:10.5, marginTop:1}}>사유: {pending.reason}</div>
            </div>
          </div>
        ) : !open ? (
          <>
            {done && <div style={{fontSize:11.5, color:'var(--green-600)', marginBottom:8}}>변경 신청이 접수되었습니다. 승인 후 반영됩니다.</div>}
            <button type="button" onClick={() => { setOpen(true); setDone(false); }}
                    style={{width:'100%', padding:'10px 0', background:'var(--surface)', color:'var(--orange-600)', border:'1px solid var(--orange-100)', borderRadius:10, fontSize:12.5, fontWeight:700, cursor:'pointer'}}>
              소속 기관 변경 신청
            </button>
          </>
        ) : (
          <div style={{display:'flex', flexDirection:'column', gap:8}}>
            {/* care 기관이 전국 수천 건이라 native <select>는 무겁다 → 회원가입·관리화면과
                동일한 검색형 OrgCombobox로 좁혀 고른다(이름 입력 → 상위 N개). */}
            <OrgCombobox
              orgs={orgs}
              value={orgId ? Number(orgId) : null}
              onChange={id => setOrgId(id != null ? String(id) : "")}
              placeholder="변경할 기관 이름 검색"
              width="100%"
            />
            <textarea value={reason} onChange={e => setReason(e.target.value)}
                      placeholder="변경 사유 (필수)" rows={3}
                      style={{padding:'9px 10px', border:'1px solid var(--border)', borderRadius:8, fontSize:12.5, resize:'vertical', fontFamily:'inherit'}}/>
            {err && <div style={{fontSize:11.5, color:'var(--rose-500)'}}>{err}</div>}
            <div style={{display:'flex', gap:8}}>
              <button type="button" onClick={() => { setOpen(false); setErr(null); }} disabled={busy}
                      style={{flex:1, padding:'9px 0', background:'var(--surface)', color:'var(--ink-2)', border:'1px solid var(--border)', borderRadius:8, fontSize:12.5, fontWeight:700, cursor:'pointer'}}>취소</button>
              <button type="button" onClick={submit} disabled={busy}
                      style={{flex:1, padding:'9px 0', background:'var(--orange-600)', color:'#fff', border:'none', borderRadius:8, fontSize:12.5, fontWeight:700, cursor: busy ? 'default' : 'pointer'}}>{busy ? '신청 중…' : '신청'}</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// 케어매니저 근무 상태(가용/바쁨/과부하/이동중/오프라인) 라벨·색.
const MGR_STATUS_META = {
  available:  { label: "근무 가능", color: "green" },
  busy:       { label: "바쁨",      color: "amber" },
  overloaded: { label: "과부하",    color: "rose"  },
  moving:     { label: "이동 중",   color: "sky"   },
  offline:    { label: "오프라인",  color: "gray"  },
};

// 매니저 본인이 근무 상태(오프라인 ↔ 근무 가능)를 직접 토글. 오프라인이면 AI 자동매칭
// 후보에서 빠진다. 현재 상태는 manager_home에서 읽고, PATCH 후 응답으로 갱신한다.
function ManagerAvailabilityToggle() {
  const [status, setStatus] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const load = React.useCallback(() => {
    mobileFetch("/api/v1/mobile/manager/home")
      .then(b => setStatus(b?.manager?.status || null))
      .catch(() => {});
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const isOffline = status === "offline";
  const meta = MGR_STATUS_META[status] || { label: status || "—", color: "gray" };

  const toggle = React.useCallback(async () => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await mobileFetch("/api/v1/mobile/manager/status", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ status: isOffline ? "available" : "offline" }),
      });
      setStatus(r?.status || null);
    } catch (e) {
      setErr(humanMobileError(e));
    } finally {
      setBusy(false);
    }
  }, [busy, isOffline]);

  return (
    <div className="card" style={{padding:'16px 18px', marginBottom:14}}>
      <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:10}}>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:13.5, fontWeight:700}}>근무 상태</div>
          <div className="muted" style={{fontSize:11, marginTop:2, lineHeight:1.5}}>
            오프라인이면 AI 자동매칭 후보에서 제외됩니다. 근무 가능으로 돌아오면 담당 부하에 따라 자동 표시됩니다.
          </div>
        </div>
        <span className={"badge badge-" + meta.color} style={{flex:'0 0 auto'}}>
          {status == null ? "…" : meta.label}
        </span>
      </div>
      {err && <div style={{margin:'0 0 10px', padding:'8px 10px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:8, fontSize:11.5}}>{err}</div>}
      <button type="button" onClick={toggle} disabled={busy || status == null}
        className={"btn btn-md " + (isOffline ? "btn-primary" : "btn-ghost")}
        style={{width:'100%'}}>
        {busy ? "변경 중…" : isOffline ? "근무 시작 (근무 가능으로 전환)" : "오프라인으로 전환 (근무 종료)"}
      </button>
    </div>
  );
}

function MgrMe() {
  // useCurrentUser로 마이그레이션 — ProfileEditor가 save 후 발사하는 session:refresh
  // 이벤트로 자동 재페치되므로 별도 fetch+setMe를 둘 필요가 없다. role_label도
  // sessions#me가 직접 제공해 ROLE_LABEL 매핑 의존을 줄였다.
  const { user, loaded } = useCurrentUser();
  const [busy, setBusy] = React.useState(false);

  const logout = React.useCallback(async () => {
    if (busy) return;
    if (!window.confirm("로그아웃하시겠습니까?")) return;
    setBusy(true);
    try {
      await fetch("/api/v1/sessions", { method: "DELETE", credentials: "same-origin" });
    } catch { /* 네트워크 실패해도 클라이언트 측 이탈은 진행 */ }
    finally {
      // 서버 응답과 관계없이 로그인 화면으로 이동. 세션 쿠키는 destroy에서 비웠음.
      // 로그인 화면 라우트는 /login(=screen "auth"). /auth 는 라우트가 없어 RoutingError.
      window.location.href = "/login";
    }
  }, [busy]);

  return (
    <div className="app" style={{background:'var(--bg)', overflow:'hidden'}}>
      <StatusBar/>
      <div style={{padding:'10px 22px 8px'}}>
        <div style={{fontSize:18, fontWeight:800}}>내 정보</div>
      </div>

      {!loaded && <div className="muted" style={{padding:'30px 22px', textAlign:'center', fontSize:12}}>불러오는 중…</div>}
      {loaded && !user && (
        <div style={{padding:'30px 22px', textAlign:'center'}}>
          <div className="muted" style={{fontSize:13, marginBottom:14}}>로그인이 필요합니다.</div>
          <a href="/login" className="btn btn-primary" style={{padding:'10px 22px', borderRadius:10, textDecoration:'none'}}>로그인</a>
        </div>
      )}

      {user && (
        <div style={{padding:'4px 22px 0', flex:1, overflow:'auto'}}>
          {/* 신원 카드 — 이름·역할·소속(권역/조직) */}
          <div className="card" style={{padding:'16px 18px', marginBottom:14, display:'flex', alignItems:'center', gap:14}}>
            <div className="avatar lg" style={{width:54, height:54, fontSize:20, overflow:'hidden', background:'var(--orange-50)', color:'var(--orange-700)'}}>
              {user.avatar_url
                ? <img src={user.avatar_url} alt="프로필 사진" style={{width:'100%', height:'100%', objectFit:'cover'}}/>
                : (user.name || '?')[0]}
            </div>
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontSize:16, fontWeight:800}}>{user.name}</div>
              <div className="muted" style={{fontSize:11.5, marginTop:2}}>
                {user.role_label || ROLE_LABEL[user.role] || user.role}
                {user.organization_name ? ` · ${user.organization_name}` : ''}
                {user.region_name       ? ` · ${user.region_name}`       : ''}
              </div>
              {user.email && (
                <div className="muted" style={{fontSize:10.5, marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{user.email}</div>
              )}
            </div>
          </div>

          <AvatarEditor user={user}/>
          <ProfileEditor user={user}/>
          <PasswordChanger/>
          <NotificationPreferences/>

          {/* 근무 상태 토글 — 매니저 본인이 오프라인↔근무 가능 직접 전환 */}
          {user.role === "manager" && user.care_manager_id && <ManagerAvailabilityToggle/>}

          {/* 소속 기관 변경 신청 — 매니저 본인만(care_manager 연결 계정). 운영자 승인 후 반영 */}
          {user.role === "manager" && user.care_manager_id && <OrgTransferRequest/>}

          {/* 바로가기 메뉴 — 자주 가는 화면을 모아둠 */}
          <div style={{fontSize:11, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em', marginBottom:6}}>바로가기</div>
          <div className="card" style={{padding:0, marginBottom:14, overflow:'hidden'}}>
            {[
              { l: "알림함",         href: "/m/notifications"   },
              { l: "내 일정",        href: "/m/schedule"        },
              { l: "내 방문 기록",   href: "/m/reports"         },
              { l: "내 케이스",      href: "/m/cases"           },
              { l: "지도·내비",      href: "/m/map"             },
            ].map((it, i, arr) => (
              <a key={i} href={it.href}
                 style={{display:'flex', alignItems:'center', padding:'12px 14px', textDecoration:'none', color:'inherit', borderTop: i === 0 ? 'none' : '1px solid var(--border-soft)'}}>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:13, fontWeight:600}}>{it.l}</div>
                  {it.desc && <div className="muted" style={{fontSize:10.5, marginTop:1}}>{it.desc}</div>}
                </div>
                <span style={{color:'var(--ink-3)', fontSize:14}}>›</span>
              </a>
            ))}
          </div>

          {/* 정책 링크 — 모바일 푸터의 약관/개인정보처리방침 자리 */}
          <div style={{fontSize:11, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.06em', marginBottom:6}}>정책</div>
          <div className="card" style={{padding:0, marginBottom:18, overflow:'hidden'}}>
            {[
              { l: "이용약관",         href: "/m/terms"   },
              { l: "개인정보처리방침", href: "/m/privacy" },
            ].map((it, i) => (
              <a key={i} href={it.href}
                 style={{display:'flex', alignItems:'center', padding:'12px 14px', textDecoration:'none', color:'inherit', borderTop: i === 0 ? 'none' : '1px solid var(--border-soft)'}}>
                <div style={{flex:1, fontSize:13, fontWeight:600}}>{it.l}</div>
                <span style={{color:'var(--ink-3)', fontSize:14}}>›</span>
              </a>
            ))}
          </div>

          <button type="button" onClick={logout} disabled={busy}
                  style={{width:'100%', padding:'12px 0', background:'var(--surface)', color:'var(--rose-500)', border:'1px solid var(--rose-100)', borderRadius:12, fontSize:13.5, fontWeight:700, cursor: busy ? 'default' : 'pointer', marginBottom:14}}>
            {busy ? '로그아웃 중…' : '로그아웃'}
          </button>
        </div>
      )}

      <TabBar active="me"/>
      <HomeBar/>
    </div>
  );
}

// 매니저 알림함. 보호자 NotifyCenter와 동일 피드(공용 NotifyFeed)를 매니저 chrome
// 으로 감싼다. 알림 API는 current_user 신원으로 스코프하므로 매니저 알림만 내려온다.
// TabBar에 '알림' 탭이 없어 active 없음 — 복귀는 피드 헤더의 ‹(history.back) 또는
// 매니저 탭바(홈/일정/지도/케이스/마이)로 한다.
function MgrNotify() {
  return (
    <div className="app" style={{background:'var(--bg)', overflow:'hidden'}}>
      <StatusBar/>
      <NotifyFeed/>
      <TabBar active={null}/>
      <HomeBar/>
    </div>
  );
}

// 정책 화면(이용약관/개인정보처리방침)의 모바일 셸. 폰 전용 역할(매니저)과 모바일
// PWA를 쓰는 보호자·어르신이 각자 마이페이지 → 정책에서 공유 진입한다(셸 chrome은
// 역할 색이 없는 범용 상태바·뒤로가기·홈바라 그대로 재사용). 본문(PrivacyBody/
// TermsBody)은 데스크톱 legal.tsx와 한 곳을 공유하고, 여기서는 폰 chrome(상태바·
// 뒤로가기 헤더·스크롤 본문)만 감싼다. 데스크톱 2단 그리드/sticky 사이드바 대신
// 본문 위에 칩 형태 TOC(앵커 점프)를 둔다.
function PolicyMobileShell({ title, badge, toc, children }) {
  return (
    <div className="app" style={{background:'var(--bg)', overflow:'hidden'}}>
      <StatusBar/>
      <div style={{padding:'8px 22px 10px', display:'flex', alignItems:'center', gap:12, borderBottom:'1px solid var(--border-soft)', flex:'0 0 auto'}}>
        <a onClick={() => history.back()} aria-label="뒤로"
           style={{width:32, height:32, display:'grid', placeItems:'center', borderRadius:10, background:'var(--surface)', color:'var(--ink-1)', cursor:'pointer', textDecoration:'none'}}>‹</a>
        <div style={{fontSize:15, fontWeight:800, flex:1}}>{title}</div>
      </div>

      <div style={{flex:1, overflow:'auto', padding:'16px 22px 28px'}}>
        <div style={{fontSize:10.5, fontWeight:700, color:'var(--orange-700)', letterSpacing:'0.08em', marginBottom:6}}>{badge}</div>
        <h1 style={{fontSize:22, fontWeight:800, letterSpacing:'-0.02em', margin:0}}>{title}</h1>
        <div className="muted" style={{fontSize:11, marginTop:6}}>최종 개정 2026-04-01 · 시행 2026-04-15</div>

        {/* 목차 — 데스크톱 사이드바 TOC를 칩으로 대체. 탭하면 해당 조항으로 앵커 점프 */}
        <div style={{display:'flex', flexWrap:'wrap', gap:6, margin:'14px 0 6px'}}>
          {toc.map(t => (
            <a key={t.id} href={`#${t.id}`}
               style={{fontSize:11, color:'var(--ink-2)', textDecoration:'none', padding:'5px 10px', borderRadius:999, background:'var(--bg-sub)', border:'1px solid var(--border-soft)', whiteSpace:'nowrap'}}>{t.label}</a>
          ))}
        </div>

        <div style={{marginTop:18, fontSize:13.5, lineHeight:1.8, color:'var(--ink-2)'}}>
          {children}
        </div>

        <div style={{marginTop:24, padding:14, background:'var(--bg-sub)', borderRadius:12, fontSize:11.5, color:'var(--ink-3)', lineHeight:1.6}}>
          <div style={{fontWeight:700, color:'var(--ink-2)', marginBottom:4}}>문의</div>
          개인정보 보호책임자<br/>
          support@care.local<br/>
          1588-0000 (평일 09:00–18:00)
        </div>
      </div>

      <HomeBar/>
    </div>
  );
}

function MgrPrivacy() {
  return (
    <PolicyMobileShell title="개인정보처리방침" badge="PRIVACY POLICY" toc={PRIVACY_TOC}>
      <PrivacyBody/>
    </PolicyMobileShell>
  );
}

function MgrTerms() {
  return (
    <PolicyMobileShell title="이용약관" badge="TERMS OF SERVICE" toc={TERMS_TOC}>
      <TermsBody/>
    </PolicyMobileShell>
  );
}

// ─────────────────────────────────────────────────────────────
// 8. 내 방문 기록(모바일) — 데스크톱 mgr-reports(/m/reports, Sidebar 레이아웃)의
//    폰 버전. 매니저는 폰 전용이라 데스크톱 셸로 보내면 흐름이 깨졌다([[manager_mobile_only]]).
//    같은 API(GET /api/v1/visit_reports, 매니저 자동 스코프)를 카드 목록으로 보여주고,
//    탭하면 바텀시트 상세 + 24h 윈도우 내면 기존 모바일 수정 폼(/m/report?...&mode=edit)으로.
// ─────────────────────────────────────────────────────────────
const RPT_CONDITION = {
  good:     { l: "좋음", c: "green" },
  normal:   { l: "보통", c: "green" },
  poor:     { l: "나쁨", c: "amber" },
  critical: { l: "위급", c: "rose" },
};

function useDebouncedVal(value, ms) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const id = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return v;
}

// 날짜 표기는 로컬(KST) 컴포넌트로 — toISOString().slice 는 UTC off-by-one 함정.
function rptDate(iso) {
  const d = new Date(iso);
  const dow = ["일","월","화","수","목","금","토"][d.getDay()];
  return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,"0")}.${String(d.getDate()).padStart(2,"0")} (${dow})`;
}

function MgrReportsMobile() {
  const [q,        setQ]        = React.useState("");
  const [abnormal, setAbnormal] = React.useState(false);
  const [page,     setPage]     = React.useState(1);
  const [data,     setData]     = React.useState(null);
  const [loading,  setLoading]  = React.useState(true);
  const [err,      setErr]      = React.useState(null);
  const [detail,   setDetail]   = React.useState(null); // 열린 보고서 row | null
  const dq = useDebouncedVal(q, 250);
  const PER = 15;

  // 필터(검색·비정상)가 바뀌면 1페이지로.
  React.useEffect(() => { setPage(1); }, [dq, abnormal]);

  React.useEffect(() => {
    const p = new URLSearchParams();
    if (dq)       p.set("q", dq);
    if (abnormal) p.set("abnormal", "true");
    p.set("page", String(page));
    p.set("per",  String(PER));
    let alive = true;
    setLoading(true); setErr(null);
    fetch(`/api/v1/visit_reports?${p.toString()}`, { credentials: "same-origin" })
      .then(async r => {
        const b = await r.json().catch(() => null);
        if (!r.ok) throw new Error(apiErrorMessage(b, r.status));
        return b;
      })
      .then(b => { if (alive) setData(b); })
      .catch(e => { if (alive) setErr(apiErrorMessage(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [dq, abnormal, page]);

  const rows       = data?.rows || [];
  const total      = data?.total ?? 0;
  const totalPages = data?.total_pages ?? 1;
  const summary    = data?.summary || {};
  const pristine   = !dq && !abnormal;

  return (
    <div className="app" style={{background:'var(--bg)', overflow:'hidden'}}>
      <StatusBar/>
      <div style={{padding:'8px 22px 6px', display:'flex', alignItems:'center', gap:12}}>
        <a href="/m/home" aria-label="홈으로"
           style={{width:32, height:32, display:'grid', placeItems:'center', borderRadius:10, background:'var(--surface)', color:'var(--ink-1)', textDecoration:'none', flex:'0 0 auto'}}>‹</a>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:18, fontWeight:800}}>내 방문 기록</div>
          <div className="muted" style={{fontSize:11}}>총 {total}건 · 이번 달 {summary.this_month_count ?? 0}건</div>
        </div>
      </div>

      {/* 검색 + 비정상 토글 */}
      <div style={{padding:'4px 22px 8px', display:'flex', gap:8, alignItems:'center', flex:'0 0 auto'}}>
        <div style={{flex:1, position:'relative'}}>
          <span style={{position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:'var(--ink-3)', display:'inline-flex', width:15, height:15}}>{Ic.search}</span>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="어르신·메모 검색"
                 style={{width:'100%', padding:'10px 12px 10px 34px', borderRadius:10, border:'1px solid var(--border)', background:'var(--surface)', fontSize:13, boxSizing:'border-box'}}/>
        </div>
        <button type="button" onClick={()=>setAbnormal(a=>!a)}
                style={{padding:'9px 12px', borderRadius:10, fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap',
                        border: abnormal?'1.5px solid var(--rose-500)':'1px solid var(--border)',
                        background: abnormal?'var(--rose-50)':'var(--surface)', color: abnormal?'var(--rose-600)':'var(--ink-2)'}}>
          비정상{summary.abnormal_count!=null?` ${summary.abnormal_count}`:''}
        </button>
      </div>

      <div style={{padding:'2px 22px 0', flex:1, overflow:'auto'}}>
        {err && <div style={{padding:12, background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>불러오기 실패: {err}</div>}
        {!err && loading && !data && <div className="muted" style={{padding:40, textAlign:'center', fontSize:12.5}}>불러오는 중…</div>}
        {!err && data && rows.length===0 && (
          <div style={{padding:'48px 16px', textAlign:'center'}}>
            <div style={{fontSize:34, marginBottom:10}}>📋</div>
            <div style={{fontSize:14, fontWeight:700, marginBottom:6}}>{pristine ? "아직 작성한 방문 기록이 없습니다" : "조건에 맞는 기록이 없습니다"}</div>
            <div className="muted" style={{fontSize:12, lineHeight:1.5}}>{pristine ? "방문을 종료하면 기록을 작성할 수 있어요." : "검색어나 필터를 조정해 보세요."}</div>
          </div>
        )}
        {!err && rows.map(r => <ReportCard key={r.id} r={r} onOpen={()=>setDetail(r)}/>)}

        {!err && totalPages>1 && (
          <div style={{display:'flex', justifyContent:'center', alignItems:'center', gap:16, padding:'12px 0 18px'}}>
            <button type="button" className="btn btn-ghost btn-sm" disabled={page<=1} onClick={()=>setPage(p=>Math.max(1,p-1))}>이전</button>
            <span className="muted" style={{fontSize:12}}>{page} / {totalPages}</span>
            <button type="button" className="btn btn-ghost btn-sm" disabled={page>=totalPages} onClick={()=>setPage(p=>Math.min(totalPages,p+1))}>다음</button>
          </div>
        )}
        <div style={{height:8}}/>
      </div>

      <TabBar active={null}/>
      <HomeBar/>
      {detail && <ReportSheet r={detail} onClose={()=>setDetail(null)}/>}
    </div>
  );
}

function ReportCard({ r, onOpen }) {
  const e = r.elderly || {};
  const sev = r.severity || {};
  const cond = RPT_CONDITION[r.condition || "normal"] || { l: r.condition || "-", c: "gray" };
  const bpText = r.blood_pressure_sys && r.blood_pressure_dia ? `${r.blood_pressure_sys}/${r.blood_pressure_dia}` : "–";
  return (
    <button type="button" onClick={onOpen}
            style={{width:'100%', textAlign:'left', display:'block', background:'var(--surface)', border:'1px solid var(--border-soft)', borderRadius:14, padding:'13px 15px', marginBottom:10, cursor:'pointer'}}>
      <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:8}}>
        <div className="avatar sm" style={{background:'var(--orange-50)', color:'var(--orange-700)'}}>{(e.name||'·')[0]}</div>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:13.5, fontWeight:700, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{e.name}{e.age!=null?` · ${e.age}세`:''}</div>
          <div className="muted" style={{fontSize:10.5}}>{rptDate(r.visit_scheduled_at)}</div>
        </div>
        <span className={"badge badge-"+cond.c} style={{fontSize:10, flex:'0 0 auto'}}>{cond.l}</span>
      </div>
      <div style={{display:'flex', gap:6}}>
        {[["혈압",bpText,sev.bp],["맥박",r.heart_rate??'–',sev.hr],["체온",r.body_temp??'–',sev.temp]].map(([lab,val,s],i)=>(
          <div key={i} style={{flex:1, padding:'6px 4px', borderRadius:8, background:'var(--bg-sub)', textAlign:'center'}}>
            <div className="muted" style={{fontSize:9, fontWeight:700}}>{lab}</div>
            <div className="mono" style={{fontSize:12.5, fontWeight:800, color: vitalColorVar(s), marginTop:1}}>{val}</div>
          </div>
        ))}
      </div>
      {r.note && <div className="muted" style={{fontSize:11.5, marginTop:8, lineHeight:1.4, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden'}}>{r.note}</div>}
      {r.photos_count>0 && <div className="muted" style={{fontSize:10.5, marginTop:6}}>📷 사진 {r.photos_count}장</div>}
    </button>
  );
}

function ReportSheet({ r, onClose }) {
  const e = r.elderly || {};
  const sev = r.severity || {};
  const cond = RPT_CONDITION[r.condition || "normal"] || { l: r.condition || "-", c: "gray" };
  const bpText = r.blood_pressure_sys && r.blood_pressure_dia ? `${r.blood_pressure_sys}/${r.blood_pressure_dia}` : "–";
  const canEdit = !!r.editable_now;
  return (
    <div onClick={onClose} style={{position:'absolute', inset:0, background:'rgba(20,16,12,0.42)', display:'flex', alignItems:'flex-end', zIndex:80}}>
      <div onClick={ev=>ev.stopPropagation()}
           style={{width:'100%', background:'var(--surface)', borderRadius:'18px 18px 0 0', maxHeight:'86%', overflow:'auto', paddingBottom:18}}>
        <div style={{width:40, height:4, background:'var(--border)', borderRadius:99, margin:'8px auto 12px'}}/>
        <div style={{padding:'0 22px'}}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4}}>
            <div style={{fontSize:16, fontWeight:800}}>방문 기록 #{r.id}</div>
            <span className={"badge badge-"+cond.c} style={{fontSize:11}}>{cond.l}</span>
          </div>
          <div className="muted" style={{fontSize:11.5, marginBottom:14}}>{rptDate(r.visit_scheduled_at)} · {r.visit_duration_mins||60}분 · 방문 #{r.visit_id}</div>

          <div style={{display:'flex', alignItems:'center', gap:10, padding:'10px 12px', background:'var(--bg-sub)', borderRadius:12, marginBottom:14}}>
            <div className="avatar sm" style={{background:'var(--orange-50)', color:'var(--orange-700)'}}>{(e.name||'·')[0]}</div>
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontSize:13.5, fontWeight:700}}>{e.name}{e.age!=null?` · ${e.age}세`:''}
                {e.risk_level && <span className={"badge badge-"+(RISK_COLOR[e.risk_level]||"gray")} style={{fontSize:9, marginLeft:6}}>{RISK_LABEL[e.risk_level]||e.risk_level}</span>}
              </div>
              {e.address && <div className="muted" style={{fontSize:10.5, marginTop:1}}>{e.address}</div>}
            </div>
          </div>

          <div style={{display:'flex', gap:8, marginBottom:14}}>
            {[["혈압",bpText,sev.bp],["맥박",r.heart_rate??'–',sev.hr],["체온",r.body_temp??'–',sev.temp],["체중",r.weight_kg??'–',null]].map(([lab,val,s],i)=>(
              <div key={i} style={{flex:1, padding:'10px 4px', borderRadius:10, background:'var(--bg-sub)', textAlign:'center'}}>
                <div className="muted" style={{fontSize:9.5, fontWeight:700}}>{lab}</div>
                <div className="mono" style={{fontSize:14, fontWeight:800, color: vitalColorVar(s), marginTop:2}}>{val}</div>
              </div>
            ))}
          </div>

          {r.medications_taken && (
            <div style={{marginBottom:12}}>
              <div className="muted" style={{fontSize:11, fontWeight:700, marginBottom:4}}>복약</div>
              <div style={{fontSize:12.5, whiteSpace:'pre-wrap'}}>{r.medications_taken}</div>
            </div>
          )}
          {r.note && (
            <div style={{marginBottom:12}}>
              <div className="muted" style={{fontSize:11, fontWeight:700, marginBottom:4}}>메모</div>
              <div style={{fontSize:12.5, whiteSpace:'pre-wrap', padding:'10px 12px', background:'var(--bg-sub)', borderRadius:10, lineHeight:1.5}}>{r.note}</div>
            </div>
          )}
          {r.photos?.length>0 && (
            <div style={{marginBottom:12}}>
              <div className="muted" style={{fontSize:11, fontWeight:700, marginBottom:6}}>사진 {r.photos.length}장</div>
              <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                {r.photos.map((u,i)=>(<img key={i} src={u} alt={`방문 사진 ${i+1}`} loading="lazy"
                  style={{width:72, height:72, objectFit:'cover', borderRadius:8, border:'1px solid var(--border)'}}/>))}
              </div>
            </div>
          )}

          <div className="muted" style={{fontSize:10.5, marginBottom:14}}>작성 {new Date(r.created_at).toLocaleDateString('ko-KR')} · {canEdit ? '작성 후 24시간 내 수정 가능' : '수정 가능 시간 종료'}</div>

          <div style={{display:'flex', gap:8}}>
            <a href={`/m/visit?id=${r.visit_id}`} className="btn btn-ghost btn-lg" style={{flex:1, textDecoration:'none', textAlign:'center'}}>방문 상세</a>
            {canEdit
              ? <a href={`/m/report?visit_id=${r.visit_id}&mode=edit`} className="btn btn-primary btn-lg" style={{flex:1, textDecoration:'none', textAlign:'center'}}>기록 수정</a>
              : <button type="button" onClick={onClose} className="btn btn-ghost btn-lg" style={{flex:1}}>닫기</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

export {
  // 모바일 셸 공통 — 사용자 매뉴얼(manual-manager)이 동일한 상태바·하단 탭바를
  // 그대로 재사용하도록 노출(탭바 정의를 한 곳에 유지 → 드리프트 방지).
  StatusBar, TabBar,
  MgrHome, MgrVisit, MgrMatch, MgrMap, MgrCase, MgrCaseList, MgrReport, MgrReportsMobile, MgrMe, MgrNotify,
  MgrPrivacy, MgrTerms,
  // 데스크톱 케이스 상세(mgr-case-desktop.tsx) 가 패널을 그대로 재사용하도록
  // 노출. 패널 내부의 Sparkline/VitalCell/VisitRow 등은 모듈 내부에서만 쓰이므로
  // 별도 export 불필요 — 패널이 같은 모듈에서 해결한다.
  CaseSummaryPane, CaseMedsPane, CaseVisitsPane, CaseReportsPane, CaseFamilyPane,
};
